import { getAccessToken, refreshAccessToken, isTokenExpired, restoreSession } from './auth';
import { isAIFeatureEnabled } from '../utils/featureFlags';

// Defense in depth: short-circuit AI API calls when the matching
// `features.ai.<key>` flag is OFF. Returns the same `{ ok, error, data }`
// shape every other apiFetch returns, so callers never have to
// special-case "feature disabled" — they just check `res.ok`. UI
// surfaces are also expected to hide their own button when the flag is
// OFF; this is the second layer that ensures we never hit the model
// even if a code path slips through.
const aiBlockedResponse = (flagKey, fnName) => {
  // eslint-disable-next-line no-console
  console.warn(`[api:ai] ${fnName} blocked by features.ai.${flagKey} flag`);
  return {
    ok: false,
    error: `This AI feature is disabled by an administrator. Toggle features.ai.${flagKey} on the /flags admin page to re-enable.`,
    data: null,
  };
};

// Empty string = relative URLs (production via nginx proxy).
// Only fall back to direct backend URL if env var is truly undefined.
//
// G309 — exported so SSE / WebSocket consumers (e.g. LiveExecutionTab)
// can construct cross-origin URLs that mirror the apiFetch wrapper's
// base. Keep the env-precedence semantics identical: REACT_APP_API_URL
// wins when set, otherwise the local-dev fallback.
export const API_URL = process.env.REACT_APP_API_URL != null ? process.env.REACT_APP_API_URL : 'https://localhost:8443';
const REQUEST_TIMEOUT = 30000;

// UUID v4 regex — only append params that look like real UUIDs to prevent 400s
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const isUUID = (v) => typeof v === 'string' && UUID_RE.test(v);

let _authToken = null;
let _tenantId = null;

export const setAuthToken = (token) => {
  _authToken = token;
};

export const setTenantId = (id) => {
  _tenantId = id;
};

/* G309 — exported so SSE / WebSocket consumers can attach the same
 * Authorization + tenant headers that apiFetch sends without
 * duplicating the JWT-refresh logic. */
export const getHeaders = async (method = 'GET') => {
  let token = _authToken || getAccessToken();

  /* D108 — refresh when there is NO token, not only when one has expired.
   *
   * The access token is held in memory only (`_persistTokens` deliberately
   * removes it from sessionStorage), so after any full page load it is gone
   * and `_tokenExpiresAt` is null. `isTokenExpired()` returns FALSE for null
   * (auth.js:903-906), so this asked "has it expired?", heard "no", and sent
   * no Authorization header at all. Every call then answered 403 while the
   * shell rendered signed in from sessionStorage — and the one recovery that
   * exists (the 401 retry below) never fires, because the server says 403.
   *
   * The renewal was always there: the refresh token also rides in an HttpOnly
   * cookie that survives the reload, so that "the SPA renews silently after
   * reload+expiry instead of hard-logging-out" (auth.js:866-871). Nothing on
   * the boot path asked for it.
   *
   * Guarded on `restoreSession()` — the shell's own record that a session
   * exists, and the very thing that makes the app look signed in while the
   * token is missing. Without that guard every call made before anyone signs
   * in would attempt a refresh, and a refused refresh calls `_onSessionExpired`
   * and clears tokens, which on the login screen is a redirect loop. */
  if (!token && !isTokenExpired() && restoreSession()) {
    token = await refreshAccessToken();
  } else if (isTokenExpired()) {
    token = await refreshAccessToken();
  }

  const headers = {
    'Content-Type': 'application/json',
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  if (_tenantId) {
    headers['X-Tenant-Id'] = _tenantId;
  }

  // G409c — Double-submit CSRF defense. For mutating requests, echo the
  // csrf_token cookie value in the X-CSRF-Token header. The server's
  // CsrfHeaderFilter rejects with 403 if the two don't match. A cross-
  // origin attacker can't read the cookie (different origin / SameSite=
  // Strict) so they can't forge the header.
  const isMutating = method && ['POST', 'PUT', 'PATCH', 'DELETE'].includes(method.toUpperCase());
  if (isMutating) {
    const csrf = readCsrfCookie();
    if (csrf) headers['X-CSRF-Token'] = csrf;
  }

  return headers;
};

/**
 * G409c — Read the non-HttpOnly csrf_token cookie that AuthCookies sets
 * at login. Returns null if absent (caller can still proceed; the server
 * will 403 if a CSRF header was required).
 */
function readCsrfCookie() {
  if (typeof document === 'undefined' || !document.cookie) return null;
  const parts = document.cookie.split(';');
  for (const p of parts) {
    const [name, ...rest] = p.trim().split('=');
    if (name === 'csrf_token') return decodeURIComponent(rest.join('='));
  }
  return null;
}

const createAbortController = (timeout = REQUEST_TIMEOUT) => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);
  return { controller, timeoutId };
};

/* DEF-PERMS-CACHE-SYNC-001 — server-driven perm-cache invalidation.
 *
 * Backend PermsRevFilter stamps every authenticated response with
 * `X-Perms-Rev: <hash>`. We track the last seen value here at module
 * scope (singleton, intentionally outside React's render cycle so it
 * survives unmounts). On every API response we compare the header
 * against our cache; if they differ AND a listener has been registered
 * (by AuthContext via setPermsRevListener), we fire the listener after
 * a 1s debounce. AuthContext's listener calls refreshPermissions(),
 * which re-fetches /api/v1/auth/permissions and updates session.permissions.
 *
 * Result: zero perceived UX lag on deploys that change ROLE_PERMS or
 * ROLE_LEVELS — the next ambient API call after the deploy triggers the
 * refresh transparently. Steady state (matching hashes): zero overhead. */
let _lastSeenPermsRev = null;
let _permsRevListener = null;
let _permsRevDebounceTimer = null;

/** Registered by AuthContext on mount. Callback receives the new rev
 *  string. Pass `null` to unregister (AuthContext unmount path). */
export function setPermsRevListener(cb) {
  _permsRevListener = cb;
}

function _maybeFirePermsRev(headerVal) {
  if (!headerVal) return;
  // First-load priming: just record, don't fire.
  if (_lastSeenPermsRev === null) {
    _lastSeenPermsRev = headerVal;
    return;
  }
  if (headerVal === _lastSeenPermsRev) return;
  // Mismatch detected — perm map changed server-side. Debounce so a
  // burst of in-flight responses (e.g. page that fires N parallel
  // calls right after a deploy) yields exactly one refresh.
  _lastSeenPermsRev = headerVal;
  if (_permsRevDebounceTimer) clearTimeout(_permsRevDebounceTimer);
  _permsRevDebounceTimer = setTimeout(() => {
    _permsRevDebounceTimer = null;
    try {
      if (_permsRevListener) _permsRevListener(headerVal);
    } catch (e) {
      // Listener crashing must never break in-flight requests.
      console.warn('perms-rev listener threw:', e);
    }
  }, 1000);
}

export const apiFetch = async (path, method = 'GET', body = null, timeout, externalSignal, extraHeaders = null) => {
  const { controller, timeoutId } = createAbortController(timeout);
  const url = `${API_URL}${path}`;
  // G409c — pass method so the CSRF header is attached on mutating requests.
  const headers = await getHeaders(method);

  // Phase 2A — caller-supplied extra headers (e.g. If-Match for ETag
  // concurrency on the Intent Profiles Catalog). Merged AFTER the base
  // headers so callers can override, but we intentionally do NOT permit
  // overriding Authorization or Content-Type from this slot to avoid
  // surprising behaviour. Anyone needing to override those should use a
  // raw fetch.
  if (extraHeaders && typeof extraHeaders === 'object') {
    for (const [k, v] of Object.entries(extraHeaders)) {
      if (v == null) continue;
      if (k.toLowerCase() === 'authorization' || k.toLowerCase() === 'content-type') continue;
      headers[k] = String(v);
    }
  }

  // If an external AbortSignal is provided, abort our internal controller when it fires
  if (externalSignal) {
    if (externalSignal.aborted) { clearTimeout(timeoutId); controller.abort(); }
    else { externalSignal.addEventListener('abort', () => controller.abort(), { once: true }); }
  }

  const options = {
    method,
    headers,
    // G409 — Send the HttpOnly agentlets_session cookie on every request so
    // the JwtAuthFilter cookie-fallback path works. Same-origin so this is
    // safe; SameSite=Strict on the cookie blocks cross-origin abuse.
    credentials: 'include',
    signal: controller.signal,
  };

  if (body) {
    options.body = JSON.stringify(body);
  }

  try {
    const response = await fetch(url, options);
    clearTimeout(timeoutId);

    // DEF-PERMS-CACHE-SYNC-001 — opportunistic perm-rev check on every
    // response, regardless of status. (Even error responses carry the
    // header.) Cheap header lookup; deeper logic in _maybeFirePermsRev.
    _maybeFirePermsRev(response.headers.get('X-Perms-Rev'));

    if (!response.ok) {
      if (response.status === 401) {
        // Check if this is a token-invalidation response (role change).
        try {
          const errBody = await response.clone().json();
          /* RC-90b — Prefer the structured `code` discriminator the BE
           * stamps in JwtAuthFilter. Pre-RC-90b this matched
           * `errBody.error.includes('role change')` — that worked but
           * was brittle: any future 401 whose human-readable error
           * happened to contain "role change" (eg. a downstream
           * service's "permission denied — see role change audit
           * trail") would falsely trigger a hard logout + token wipe.
           * The substring fallback is kept so deployments with a
           * pre-RC-90b BE on the other end still trigger the right
           * logout path — both sides upgrade independently. */
          /* The backend sets ONE watermark for four unrelated events —
           * sign-out, role change, scope switch and customer lifecycle — and
           * until V389 it reported all of them as TOKEN_INVALIDATED_ROLE_CHANGE.
           * Signing out of one tab therefore told every other tab the user's
           * role had changed. Each event now carries its own code, so the
           * person is told what actually happened.
           *
           * All four still force a full re-login: which sessions get killed is
           * a deliberate backend decision (G408 kills copies in other tabs),
           * and this change deliberately does not alter it — only the
           * explanation. The bare TOKEN_INVALIDATED code covers a watermark
           * row written before V389, where the reason was never recorded. */
          const INVALIDATION_REASONS = {
            TOKEN_INVALIDATED_ROLE_CHANGE: 'role_changed',
            TOKEN_INVALIDATED_LOGOUT: 'signed_out',
            TOKEN_INVALIDATED_SCOPE_SWITCH: 'scope_changed',
            TOKEN_INVALIDATED_ACCOUNT_CHANGE: 'account_changed',
            TOKEN_INVALIDATED: 'session_ended',
          };
          let invalidationReason = INVALIDATION_REASONS[errBody.code];
          /* Substring fallback for a backend older than V389, which emits only
           * the role-change code. Kept narrow: it matches the legacy message
           * only, so a future 401 that merely mentions a role change in prose
           * cannot trigger a hard logout. */
          if (!invalidationReason
              && typeof errBody.error === 'string'
              && errBody.error.includes('invalidated due to role change')) {
            invalidationReason = 'role_changed';
          }
          if (invalidationReason) {
            // Force full re-authentication — token refresh won't help.
            console.warn(`Token invalidated (${invalidationReason}) — forcing re-login`);
            localStorage.removeItem('accessToken');
            localStorage.removeItem('refreshToken');
            window.location.href = `/login?reason=${invalidationReason}`;
            throw new Error(`Token invalidated: ${invalidationReason}`);
          }
        } catch (parseErr) { /* ignore parse errors, continue with refresh */ }

        const newToken = await refreshAccessToken();
        if (newToken) {
          // Preserve extraHeaders (e.g. If-Match) on the retry; otherwise an
          // ETag-gated PATCH would silently drop the precondition and could
          // pass when it shouldn't. timeout/signal are deliberately omitted —
          // matches prior behaviour for the 401-refresh retry path.
          return apiFetch(path, method, body, undefined, undefined, extraHeaders);
        }
        throw new Error('Unauthorized');
      }
      // 429 Too Many Requests — rate limited
      if (response.status === 429) {
        const retryAfter = response.headers.get('Retry-After') || '60';
        throw new Error(`Rate limited. Please try again in ${retryAfter} seconds.`);
      }
      // 409 Conflict — return the structured body so a caller can handle a
      // real duplicate.
      //
      // `duplicate` is the SERVER'S claim, not a synonym for 409. This stamped
      // `duplicate: true` on every 409 the platform can emit, and only one of
      // them means a name clash: WorkflowController returns
      // `{error, duplicate: true}` for DuplicateWorkflowException, while
      // GlobalExceptionHandler maps EVERY DataIntegrityViolationException —
      // any not-null, check or foreign-key violation — to a 409 carrying
      // `{status, error, message, errorId}` and no duplicate flag.
      //
      // Both arrived at the canvas as "a workflow named X already exists —
      // choose a different name", so a not-null violation on insert sent
      // operators to rename a workflow, repeatedly, with no name that could
      // ever work. Spreading the body AFTER the defaults lets a server that
      // does claim `duplicate` still win; one that says nothing no longer has
      // the claim invented for it.
      if (response.status === 409) {
        try {
          const conflictBody = await response.json();
          // `error` is what most callers show in a toast, but a structured 409 from
          // GlobalExceptionHandler puts the HTTP phrase ("Conflict") there and the
          // operator-facing sentence in `message`. Prefer the sentence so the toast
          // explains the conflict; fall back to the phrase when there is no message.
          // Servers that DO author `error` (e.g. workflow duplicate) have no `message`,
          // so they are unaffected.
          const humanError = conflictBody.message || conflictBody.error;
          return { ok: false, status: 409, duplicate: false, ...conflictBody, error: humanError };
        } catch (_) { /* fall through to generic error */ }
      }
      // G268 — 423 Locked is used by the canvas phase-allowlist for FULL_LOCK
      // (Act-phase rejection). Preserve the structured body so the pre-save
      // diff modal can render attemptedAdds/Removes/Mutates + regressTarget.
      // Mirrors the 409 path; same shape on both error codes means one modal,
      // one branch.
      if (response.status === 423) {
        try {
          const lockedBody = await response.json();
          return { ok: false, status: 423, locked: true, ...lockedBody };
        } catch (_) { /* fall through to generic error */ }
      }
      // Phase 2A — 412 Precondition Failed (If-Match etag mismatch on the
      // Intent Profiles Catalog). Distinct from 409 so the FE can render a
      // "reload and retry" banner (concurrent edit) vs a "fix the input"
      // banner (semantic conflict like "version not in DRAFT"). Body may be
      // empty; both shapes are tolerated.
      if (response.status === 412) {
        let body = {};
        try { body = await response.json(); } catch (_) { /* empty body is fine */ }
        return { ok: false, status: 412, etagMismatch: true, ...body };
      }
      // Try to parse error body for details (including field-level validation info)
      let errorMsg = `HTTP ${response.status}: ${response.statusText}`;
      let errorField = null;
      try {
        const errBody = await response.json();
        if (errBody?.message) errorMsg = errBody.message;
        else if (errBody?.error) errorMsg = errBody.error;
        if (errBody?.field) errorField = errBody.field;
      } catch (_) { /* no JSON body */ }
      const err = new Error(errorMsg);
      if (errorField) err.field = errorField;
      // Expose the HTTP status on the thrown error so callers can branch on it
      // (e.g. 404 → "not found" UX) WITHOUT brittle message string-matching.
      // Additive: existing callers that only read err.message are unaffected.
      err.status = response.status;
      throw err;
    }

    // Phase 2A — surface ETag header on every success response. Additive: any
    // existing caller that destructures `{ ok, data }` continues to work
    // unchanged. ETag-aware callers (Intent Profiles Catalog FE) read the
    // new `etag` field to thread If-Match on subsequent mutations. Header is
    // null when the endpoint doesn't emit one (most lists, write-only POSTs).
    const etag = response.headers.get('ETag') || null;

    // Handle 204 No Content and other empty responses
    const contentLength = response.headers.get('content-length');
    if (response.status === 204 || contentLength === '0') {
      return { ok: true, data: null, etag };
    }

    // Try to parse JSON; fall back to null for empty bodies
    const text = await response.text();
    if (!text || !text.trim()) {
      return { ok: true, data: null, etag };
    }

    try {
      const data = JSON.parse(text);
      return { ok: true, data, etag };
    } catch (_) {
      return { ok: true, data: null, etag };
    }
  } catch (error) {
    clearTimeout(timeoutId);
    /* Fix G4/G10: don't spam DevTools with 403/404s — those are legitimate
     * "no access / not found" outcomes that callers handle. The previous
     * blanket console.error fired on every page navigation when an
     * inbox-count probe came back 403, polluting the console for engineers
     * trying to spot real bugs. Server errors (5xx), aborts, and network
     * failures are still logged because those ARE actionable. */
    const msg = error && error.message ? String(error.message) : '';
    const is4xx = /HTTP 4\d\d/.test(msg);
    if (!is4xx) {
      console.error(`[api] ${method} ${path}:`, error);
    }
    /* `status` is additive and matches the shape the 409 / 412 / 423 branches above already
       return, so callers can branch on the HTTP status instead of matching on message text.
       That matters for the difference between "this failed" and "you are not allowed to see
       this" — one deserves a red alarm, the other a quiet note. RC-90b's own comment records
       what substring-matching an error message costs when the wording later changes. */
    return { ok: false, error: error.message, status: error.status };
  }
};

// ---------------------------------------------------------------------------
// Dashboard Metrics  (G-O04 / G-O05 — replaces hardcoded values)
// ---------------------------------------------------------------------------
export const getDashboardMetrics = (orgId, tenantId) => {
  const params = new URLSearchParams();
  if (isUUID(orgId)) params.append('orgId', orgId);
  if (isUUID(tenantId)) params.append('tenantId', tenantId);
  const qs = params.toString();
  return apiFetch(`/api/v1/dashboard/metrics${qs ? `?${qs}` : ''}`);
};

// ---------------------------------------------------------------------------
// Agents  (G-O01 — /api/ → /api/v1/   |   G-O02 — pass orgId / tenantId)
// ---------------------------------------------------------------------------
export const getAgents = ({ orgId, tenantId } = {}) => {
  const params = new URLSearchParams();
  if (isUUID(orgId)) params.append('orgId', orgId);
  if (isUUID(tenantId)) params.append('tenantId', tenantId);
  const qs = params.toString();
  return apiFetch(`/api/v1/agents${qs ? `?${qs}` : ''}`);
};

// The Crossing (Slice D) — Live Trust Action.
export const getLiveTrustSnapshot = (agentId) =>
  apiFetch(`/api/v1/trust/profiles/${agentId}/live`);

// Trust Score — a starting score for a subject with no runtime evidence yet. Owner-scoped:
// every call below is authenticated and resolves the caller's tenant server-side.
export const getTrustScores = () =>
  apiFetch('/api/v1/trust/scores');

export const getTrustScore = (scoreId) =>
  apiFetch(`/api/v1/trust/scores/${scoreId}`);

export const recomputeDiscoveredAgentTrustScore = (discoveredAgentId) =>
  apiFetch(`/api/v1/trust/scores/discovered-agents/${discoveredAgentId}/recompute`, 'POST');

// The run-time half of the same score, for a governed agent. Same scale and bands as the reads
// above; what differs is the evidence, which the response states as its own basis.
export const getAgentRunTimeScore = (agentId) =>
  apiFetch(`/api/v1/trust/scores/agents/${agentId}`);

// What this person has brought under governance through the Smart Agent Wizard — their own
// agents only, scoped to the caller server-side. One call carrying the tile each came from, its
// phase, its seven proves and its stored score, because assembling that in the browser needs
// registration and connector reads a Viewer does not hold. An empty array is the normal answer
// for somebody who has not made anything yet.
export const getWizardEstate = () => apiFetch('/api/v1/wizard/estate');

// The design-time half, for a governed agent: what is structurally true before it runs — the
// identity it holds, what it can reach, what is bound to it, whether it can be halted. Distinct
// from getAgentRunTimeScore above, which answers what its runs have earned it. 404 means it has
// never been scored, which is the honest answer rather than a zero.
export const getAgentDesignTimeScore = (agentId) =>
  apiFetch(`/api/v1/trust/scores/agents/${agentId}/design-time`);

// Score a governed agent now, or bring its passport up to date. Gated on agent.edit server-side,
// so a Developer can score the agent they just built.
export const recomputeAgentDesignTimeScore = (agentId) =>
  apiFetch(`/api/v1/trust/scores/agents/${agentId}/recompute`, 'POST');

// A score at the address on a badge. Authenticated like every other read here — following a badge
// sends a visitor to sign in first, and what comes back depends on whether the score is theirs.
export const getTrustScoreBySlug = (slug) =>
  apiFetch(`/api/v1/trust/scores/by-slug/${encodeURIComponent(slug)}`);

export const getMostActiveAgents = (limit = 10, windowDays = 30) =>
  apiFetch(`/api/v1/trust/agents/most-active?limit=${limit}&windowDays=${windowDays}`);

// G-4 Replay — distinct governed runs (correlationIds) for an agent, newest first,
// last 30d, paginated. { since?, limit?, offset? }
export const getTrustGraphRuns = (agentId, { since, limit = 50, offset = 0 } = {}) => {
  const p = new URLSearchParams();
  if (since) p.set('since', since);
  p.set('limit', String(limit));
  p.set('offset', String(offset));
  return apiFetch(`/api/v1/trust-graph/subjects/${agentId}/runs?${p.toString()}`);
};

/* Phase A · Agent Discovery — Unified inventory.
 * Returns a list of InventoryRowDto items with kind="governed" or
 * kind="shadow", carrying a uniform discoverySource so the FE can render
 * the Source facet + SHADOW badge without composing two endpoints client
 * side. The original getAgents() above stays unchanged for callers that
 * don't need shadow rows. */
export const getAgentInventory = ({ orgId, tenantId, includeShadow = true } = {}) => {
  const params = new URLSearchParams();
  if (isUUID(orgId)) params.append('orgId', orgId);
  if (isUUID(tenantId)) params.append('tenantId', tenantId);
  params.append('includeShadow', String(includeShadow));
  return apiFetch(`/api/v1/agents/inventory?${params.toString()}`);
};
export const getAgent = (agentId) => apiFetch(`/api/v1/agents/${agentId}`);
export const createAgent = (payload) => apiFetch('/api/v1/agents', 'POST', payload);
export const updateAgent = (agentId, payload) => apiFetch(`/api/v1/agents/${agentId}`, 'PUT', payload);
export const deleteAgent = (agentId) => apiFetch(`/api/v1/agents/${agentId}`, 'DELETE');

// ── Agentic App Packs ──────────────────────────────────────────────────────
export const getAgenticAppPacks = () => apiFetch('/api/v1/agentic-app-packs');
export const getAgenticAppPack = (id) => apiFetch(`/api/v1/agentic-app-packs/${id}`);
export const getAgenticAppPackInstallations = () => apiFetch('/api/v1/agentic-app-packs/installations');
export const provisionAgenticAppPack = (id, mode, targetOrgId, targetTenantId) =>
  apiFetch(`/api/v1/agentic-app-packs/${id}/provision`, 'POST', { mode, targetOrgId, targetTenantId });

// Complete a quick-start installation's publish (gated Build→Act; ACTIVE only when all agents reach Act).
export const publishPackInstallation = (installationId) =>
  apiFetch(`/api/v1/agentic-app-packs/installations/${installationId}/publish`, 'POST');

// Pack assignments — grant/list/revoke a provisioned pack's instance to tenant users/groups.
// The grant fans out across the installation's agents + workflow server-side (PackAssignmentService).
export const getPackAssignments = (installationId) =>
  apiFetch(`/api/v1/agentic-app-packs/installations/${installationId}/assignments`);
export const assignPackAccess = (installationId, { userId, groupId, accessLevel }) =>
  apiFetch(`/api/v1/agentic-app-packs/installations/${installationId}/assignments`, 'POST', { userId, groupId, accessLevel });
export const removePackAssignment = (installationId, { userId, groupId }) => {
  const q = userId ? `userId=${encodeURIComponent(userId)}` : `groupId=${encodeURIComponent(groupId)}`;
  return apiFetch(`/api/v1/agentic-app-packs/installations/${installationId}/assignments?${q}`, 'DELETE');
};

// List all skills for an agent. Used by LinkedWorkflowsSection to derive
// the phase (build/decide/act) of each linked workflow's workflow_skill
// row, which gates whether the × unlink button is enabled — see the
// unlink-cascade guard in WorkflowService.updateWorkflow (Rule A blocks
// decide/act unlinks; Rule B cascades for build).
export const getAgentSkills = (agentId) => apiFetch(`/api/v1/agents/${agentId}/skills`);
export const addSkill = (agentId, payload) => apiFetch(`/api/v1/agents/${agentId}/skills`, 'POST', payload);
// Model B — execution backend selector (demo | connector) persisted on the agent's aiSystemMetadata.
export const getExecutionBackend = (agentId) => apiFetch(`/api/v1/agents/${agentId}/execution-backend`);
export const setExecutionBackend = (agentId, backend) => apiFetch(`/api/v1/agents/${agentId}/execution-backend`, 'PUT', { backend });
export const editSkill = (agentId, skillId, payload) => apiFetch(`/api/v1/agents/${agentId}/skills/${skillId}`, 'PUT', payload);
// W1 (D-1) — save a Governance Policy Skill's config (policy_skill only; skill.edit).
export const attachSkillPolicy = (agentId, skillId, policyPayload) =>
    apiFetch(`/api/v1/agents/${agentId}/skills/${skillId}/policy`, 'PUT', policyPayload);
export const deleteSkill = (agentId, skillId) => apiFetch(`/api/v1/agents/${agentId}/skills/${skillId}`, 'DELETE');
// Explicit unpublish action (2026-05-04 lockdown). Clears wasPublished/pushed
// on a Build-phase skill AND triggers an agentlet re-sync to drop the skill
// from the gateway's in-memory bundle. After this, deleteSkill proceeds.
// Returns the updated skill row.
export const unpublishSkill = (agentId, skillId) => apiFetch(`/api/v1/agents/${agentId}/skills/${skillId}/unpublish`, 'POST');
export const pushSkill = (agentId, skillId) => apiFetch(`/api/v1/agents/${agentId}/skills/${skillId}/push`, 'POST');
export const pushAllSkills = (agentId) => apiFetch(`/api/v1/agents/${agentId}/skills/push-all`, 'POST');
export const advanceSkill = (agentId, skillId) => apiFetch(`/api/v1/agents/${agentId}/skills/${skillId}/advance`, 'POST');
export const regressSkill = (agentId, skillId) => apiFetch(`/api/v1/agents/${agentId}/skills/${skillId}/regress`, 'POST');
export const syncToAgentlet = (agentId) => apiFetch(`/api/v1/agents/${agentId}/sync-to-agentlet`, 'POST');

/**
 * Forward Agent → Canvas materialisation. Powers the
 * [Open in Canvas to Test & Verify] CTA on the ACT-phase agent panel.
 *
 * Returns { canvasDefinition, agentLink, validation, usedFallback }.
 * v1 only supports mode='preview' — the response carries the materialised
 * Canvas Definition inline. The frontend then creates a draft workflow
 * with that definition and navigates to /canvas?workflowId=…&agentId=…
 * (mode='persist' lands in 1.x).
 *
 * Reference: AGENT_DEFINITION_JSON_PREVIEW_SPEC.md §4.
 */
export const materializeAgentCanvas = (agentId, { mode = 'preview' } = {}) =>
  apiFetch(`/api/v1/agents/${agentId}/canvas`, 'POST', { mode });

/**
 * Auto Create from Runbook / Workflow / Doc Library — POST stub.
 *
 * v1 always returns 501 with a structured "coming in 1.1" body so the
 * UI can surface the message in-place rather than a generic error. The
 * UX shape is locked here so 1.1 is a backend wiring change.
 *
 * @param body { sourceKind: 'runbook' | 'workflow' | 'doc_library',
 *              sourceRef: string,
 *              hints?: object }
 */
export const autoCreateAgent = (body) =>
  apiFetch('/api/v1/auto-create', 'POST', body);

/**
 * Stage a runbook upload for Auto Create.
 *
 * v1: validates MIME + size and returns a synthetic staging id of the
 * form `upload://staging/<uuid>`. The bytes are NOT persisted in v1 —
 * the 1.1 pipeline wires real storage to the tenant's secure file-staging
 * bucket, at which point this endpoint's contract is unchanged. Calling
 * this in v1 is therefore safe but the file effectively evaporates after
 * the response — the staging id is only meaningful within the same
 * session's Auto Create flow.
 *
 * Returns { stagingId, filename, size, contentType, persisted, eta, note }
 * on success, throws on validation failure (400 / 413).
 */
/**
 * Snapshot of an in-flight Auto Create run.
 * Returns { runId, status, currentPhase, ...all per-phase outputs }.
 */
export const getAutoCreateRun = (runId) =>
  apiFetch(`/api/v1/auto-create/runs/${encodeURIComponent(runId)}`);

/**
 * Submit gap resolutions for a paused-on-gap run; the pipeline resumes
 * immediately. Body: { resolutions: [{ stepId, registryId?, action,
 * displayName?, kind?, source? }] }.
 *
 * Per-step `action` values: 'use_match' | 'use_alternative' | 'install_connector' |
 * 'browse_registry' | 'skip_step'.
 */
export const submitAutoCreateGapResolution = (runId, resolutions) =>
  apiFetch(`/api/v1/auto-create/runs/${encodeURIComponent(runId)}/gap-resolution`,
    'POST', { resolutions });

/**
 * Final review submission. accepted=true → backend creates the Agent
 * record + persists the AgentDefinition. accepted=false → run marked
 * rejected; no agent created.
 */
export const submitAutoCreateReview = (runId, accepted, editedDefinition) =>
  apiFetch(`/api/v1/auto-create/runs/${encodeURIComponent(runId)}/review`,
    'POST', { accepted, editedDefinition });

/**
 * Where one agent stands in each of the seven proves, in journey order.
 *
 * Always returns seven entries — a prove nothing backs yet comes back PENDING
 * rather than being omitted, so the caller never has to tell "not yet" apart
 * from "not asked for".
 *
 * Returns the standard envelope:
 *   { ok: true, data: { agentId, proves: [{ domain, label, state, detail,
 *                                           sealedAt, predicate }] } }
 *
 * `state` is 'SEALED' (an assertion exists in the trust graph), 'READY' (the
 * condition is met in real state, nothing has sealed it) or 'PENDING'. These
 * are three different claims and the UI must not collapse them.
 *
 * Throws with `err.status === 404` for an agent that does not exist or that the
 * caller may not see — the wizard polls this while a run is still creating the
 * agent, so callers should treat 404 as "not yet", not as a failure.
 */
export const getAgentProves = (agentId) =>
  apiFetch(`/api/v1/agents/${encodeURIComponent(agentId)}/proves`);

/** Cancel a running Auto Create. The pipeline polls a flag at phase boundaries. */
export const cancelAutoCreateRun = (runId) =>
  apiFetch(`/api/v1/auto-create/runs/${encodeURIComponent(runId)}/cancel`, 'POST', {});

/**
 * Mint a single-use stream token for an Auto Create SSE handshake.
 * Header-authenticated (normal Bearer JWT), so the JWT never lands in
 * a URL/log. Returned token is opaque, valid for ~60s, and consumed
 * atomically on first SSE connect. Reconnects MUST mint a fresh token.
 *
 * Returns the standard apiFetch envelope:
 *   { ok: true, data: { token, expiresAt, runId } }
 *   { ok: false, error: '...' }
 */
export const mintAutoCreateStreamToken = (runId) =>
  apiFetch(`/api/v1/auto-create/runs/${encodeURIComponent(runId)}/stream-token`,
           'POST', {});

/**
 * Build the SSE URL for a run's event stream. Pass the last-seen
 * sequence as `after` for cursor-based replay on reconnect, and an
 * opaque `streamToken` (from mintAutoCreateStreamToken) as the auth
 * credential. The caller is responsible for instantiating EventSource
 * and listening to the documented event types (phase_started,
 * phase_progress, phase_completed, gap_detected, run_requires_review,
 * run_completed, run_failed, run_cancelled, run_rejected).
 *
 * Auth (2026-05-04 hardened): EventSource can't send Authorization
 * headers, so we pass an opaque single-use stream token as
 * ?stream_token=<short>. The bearer JWT is NEVER placed in this URL —
 * earlier iterations did, which leaked the JWT into nginx access logs
 * and referer headers. The token here is short-lived (~60s) and
 * single-use; useAutoCreateRun mints a fresh one before each connect.
 */
export const autoCreateEventStreamUrl = (runId, { afterSequence = 0, streamToken } = {}) => {
  const base = `${API_URL}/api/v1/auto-create/runs/${encodeURIComponent(runId)}/events`;
  const params = new URLSearchParams();
  if (afterSequence > 0) params.append('after', String(afterSequence));
  if (streamToken) params.append('stream_token', streamToken);
  const qs = params.toString();
  return qs ? `${base}?${qs}` : base;
};

/* RC-121b — ApprovalCenter file attachment upload.
 *
 * Multipart POST to /api/v1/approvals/{requestId}/attachments. Returns
 * {id, storageUrl, sha256, sizeBytes, filename, contentType, fieldKey}.
 * The FE writes storageUrl into form_data as the value of the matching
 * type='file' field; downstream workflow nodes consume the URL via the
 * companion GET endpoint.
 *
 * Mirrors stageAutoCreateRunbook's CSRF wiring — raw multipart fetch
 * bypasses apiFetch's CSRF + cookie machinery, so we re-add both
 * explicitly to avoid 403s on cookie-only sessions (post SEC-25). */
export const uploadApprovalAttachment = async (requestId, file, fieldKey) => {
  const token = _authToken || getAccessToken();
  const formData = new FormData();
  formData.append('file', file);
  if (fieldKey) formData.append('fieldKey', fieldKey);
  const headers = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;
  if (_tenantId) headers['X-Tenant-Id'] = _tenantId;
  const csrf = readCsrfCookie();
  if (csrf) headers['X-CSRF-Token'] = csrf;
  const res = await fetch(`${API_URL}/api/v1/approvals/${requestId}/attachments`, {
    method: 'POST', headers, body: formData, credentials: 'include',
  });
  // Surface the structured error envelope (body.reason from UploadGuard)
  // so the FE banner can render the specific blocker rather than a
  // generic "upload failed (400)" message.
  let body = null;
  try { body = await res.json(); } catch { /* non-JSON body — leave null */ }
  if (!res.ok) {
    const err = new Error((body && body.error) || `Upload failed (${res.status})`);
    err.status = res.status;
    err.reason = body && body.reason;
    throw err;
  }
  return body;
};

export const listApprovalAttachments = (requestId) =>
  apiFetch(`/api/v1/approvals/${requestId}/attachments`);

/** Build a download URL for an approval attachment. Token-auth and tenant
 *  headers are picked up by the fetch automatically when this URL is
 *  loaded via <a download> or apiFetch — the FE uses this for the
 *  "[filename]" link rendered on the file field after upload. */
export const buildApprovalAttachmentDownloadUrl = (requestId, attachmentId) =>
  `${API_URL}/api/v1/approvals/${requestId}/attachments/${attachmentId}`;

export const stageAutoCreateRunbook = async (file, purpose) => {
  const token = _authToken || getAccessToken();
  const formData = new FormData();
  formData.append('file', file);
  // purpose='process_files' widens the upload allowlist to spreadsheets; the
  // default (runbook) is unchanged.
  if (purpose) formData.append('purpose', purpose);
  const headers = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;
  if (_tenantId) headers['X-Tenant-Id'] = _tenantId;
  /* SEC/CSRF (RC-91 family) — raw multipart fetch bypasses apiFetch's CSRF +
   * cookie wiring. Cookie-authenticated uploads (no in-memory Bearer token,
   * e.g. after a page reload under SEC-25 cookie auth) are CSRF-enforced, so
   * echo the csrf_token cookie + send credentials or the upload 403s
   * intermittently with "CSRF token missing or invalid". */
  const csrf = readCsrfCookie();
  if (csrf) headers['X-CSRF-Token'] = csrf;
  const res = await fetch(`${API_URL}/api/v1/auto-create/upload`, {
    method: 'POST', headers, body: formData, credentials: 'include',
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Upload failed (${res.status})`);
  }
  return res.json();
};
export const promoteSkill = (agentId, skillId, payload) => apiFetch(`/api/v1/agents/${agentId}/skills/${skillId}/promote`, 'POST', payload);
export const revokeSkill = (agentId, skillId) => apiFetch(`/api/v1/agents/${agentId}/skills/${skillId}/revoke`, 'POST');

export const syncGuardrailConfig = (agentId, payload) => apiFetch(`/api/v1/agents/${agentId}/guardrails/sync`, 'POST', payload);

export const generateEval = (agentId, payload) => apiFetch(`/api/v1/agents/${agentId}/evals/generate`, 'POST', payload);
export const runEval = (agentId, evalId) => apiFetch(`/api/v1/agents/${agentId}/evals/${evalId}/run`, 'POST');
export const getEvals = (agentId) => apiFetch(`/api/v1/agents/${agentId}/evals`);
export const getEvalSuite = (agentId, evalId) => apiFetch(`/api/v1/agents/${agentId}/evals/${evalId}`);
export const getEvalTestCases = (agentId, evalId) => apiFetch(`/api/v1/agents/${agentId}/evals/${evalId}/test-cases`);
export const getEvalRuns = (agentId, evalId) => apiFetch(`/api/v1/agents/${agentId}/evals/${evalId}/runs`);
export const getAllEvalRuns = (agentId) => apiFetch(`/api/v1/agents/${agentId}/evals/runs/all`);
export const updateEvalSuite = (agentId, evalId, payload) => apiFetch(`/api/v1/agents/${agentId}/evals/${evalId}`, 'PUT', payload);
export const updateTestCase = (agentId, evalId, testCaseId, payload) => apiFetch(`/api/v1/agents/${agentId}/evals/${evalId}/test-cases/${testCaseId}`, 'PUT', payload);
export const getLlmProviders = (agentId) => apiFetch(`/api/v1/agents/${agentId}/evals/llm-providers`);
// DELETE with POST fallback for environments where DELETE method is blocked (403)
export const deleteEvalSuite = async (agentId, evalId) => {
  try {
    return await apiFetch(`/api/v1/agents/${agentId}/evals/${evalId}`, 'DELETE');
  } catch (e) {
    // Fallback to POST-based delete if DELETE method is blocked
    if (e.message && e.message.includes('403')) {
      return await apiFetch(`/api/v1/agents/${agentId}/evals/${evalId}/delete`, 'POST');
    }
    throw e;
  }
};

export const approveExecution = (executionId, payload) => apiFetch(`/api/v1/executions/${executionId}/approve`, 'POST', payload);
export const rejectExecution = (executionId, payload) => apiFetch(`/api/v1/executions/${executionId}/reject`, 'POST', payload);
export const getExecutions = (agentId) => apiFetch(`/api/v1/agents/${agentId}/executions`);

// ---------------------------------------------------------------------------
// Phase Gate Lifecycle
// ---------------------------------------------------------------------------
export const advancePhase = (agentId, userId) => apiFetch(`/api/v1/agents/${agentId}/advance-phase`, 'POST', { userId });
export const getPhaseGates = (agentId) => apiFetch(`/api/v1/agents/${agentId}/phase-gates`);

/* G274 — THE reader for the advance-phase response.
 *
 * AgentController deliberately returns HTTP 200 whether the gate PASSED or FAILED
 * ("a failed gate is a normal business outcome, not an HTTP error" — returning 422 hid the
 * gate details from apiFetch). The two outcomes are distinguished by the BODY, not the
 * status:
 *
 *     passed →  { agent, gate }          gate.status = 'passed'
 *     failed →  { gate }                 gate.status = 'failed'   ← NO `agent` key
 *
 * And apiFetch wraps every 2xx as { ok: true, data }. So `res.ok` says NOTHING about
 * whether the agent actually moved. Both callers branched on `res.ok` alone and therefore
 * reported SUCCESS on a refused gate — the canvas toasted "Agent advanced to decide" while
 * the agent was still sitting in Build with a 1-of-2 gate. Displayed ≠ actual, on the one
 * control that is supposed to be the hard stop between an agent that can act and one that
 * cannot.
 *
 * This is the single place that contract is interpreted. Both surfaces call it.
 *
 * @returns {{advanced: boolean, agent: object|null, gate: object|null,
 *            toPhase: string|null, failedChecks: object[], error: string|null}}
 */
export function readAdvanceResult(res) {
  // Tolerate both the apiFetch envelope ({ok, data}) and a raw body.
  const body = (res && typeof res === 'object' && 'ok' in res) ? (res.data || {}) : (res || {});
  const error = res?.error || body?.error || null;
  const gate = body?.gate || null;
  const agent = body?.agent || null;
  const failedChecks = parseGateChecks(gate).filter((c) => c && c.passed === false);
  /* Advanced ONLY when the server handed back the updated agent AND the gate didn't fail.
   * Requiring `agent` is what makes this fail-closed: any future response shape that omits
   * it reads as "did not advance" rather than silently reporting success. */
  const advanced = !error && !!agent && (!gate || gate.status !== 'failed');
  return { advanced, agent, gate, toPhase: gate?.toPhase || null, failedChecks, error };
}

/* AgentPhaseGate.checks is a JSON string column; some serialisers hand it back already
 * parsed. Accept both rather than assuming. */
function parseGateChecks(gate) {
  const raw = gate?.checks;
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (_) {
    return [];
  }
}

/* G268 / LET_CANVAS_GOLDEN_RULES §5.4 — Agent-level phase regress.
 * Symmetric to advancePhase. Used by the canvas pre-save diff modal's
 * "Regress to Build" / "Regress to Decide" actions when a save is
 * rejected on structural-lock or full-lock.
 *   targetPhase: 'build' | 'decide'
 * Returns { ok, agent, phase } on 200 or { error, code: 'INVALID_REGRESS' } on 400. */
export const regressPhase = (agentId, targetPhase, userId) =>
  apiFetch(`/api/v1/agents/${agentId}/regress-phase`, 'POST', { targetPhase, userId });

/* G263 — One-shot canvas materialisation aggregator. Returns
 *   { agent, skills, inheritedBindings, executionStats, statsWindowDays }
 * replacing the previous 4 parallel client fetches in fetchAgentCanvasBindings. */
export const getCanvasMaterialisation = (agentId, statsWindowDays = 30) =>
  apiFetch(`/api/v1/agents/${agentId}/canvas-materialisation?statsWindowDays=${statsWindowDays}`);

/* G263 — Server-side per-skill execution stats (last `windowDays`).
 * Returns { agentId, windowDays, stats: { skillId: {count,p50,p95,...} }, skillCount }. */
export const getAgentExecutionStats = (agentId, windowDays = 30) =>
  apiFetch(`/api/v1/agents/${agentId}/execution-stats?windowDays=${windowDays}`);

/* G261 — Canvas → Agent reverse-sync. POSTs the canvas definition;
 * backend diffs against agent.skills, applies phase-allowlist enforcement,
 * returns { ok, phase, added:[], removed:[], mutated:[], summary }.
 * 409 STRUCTURAL_LOCK / 423 FULL_LOCK surface as { ok:false, code, error, phase }. */
/* G385 — workflowId is now required so the backend can verify
 * workflow.kind === 'let_canvas' before touching agent.skills. Calls without
 * a workflowId will be refused with 422 NOT_AN_AGENT_CANVAS. This is the
 * structural fix for G383 where a [Native] workflow's empty skill diff
 * would silently delete all of the linked agent's skills. */
export const reconcileCanvas = (agentId, canvasDefinition, workflowId) =>
  apiFetch(`/api/v1/agents/${agentId}/canvas-reconcile`, 'POST', {
    definition: canvasDefinition,
    workflowId,
  });

// ---------------------------------------------------------------------------
// Skill Reviews (Decide Phase)
// ---------------------------------------------------------------------------
export const reviewSkill = (agentId, skillId, payload) => apiFetch(`/api/v1/agents/${agentId}/skills/${skillId}/review`, 'POST', payload);
export const getSkillReviews = (agentId) => apiFetch(`/api/v1/agents/${agentId}/reviews`);

// ---------------------------------------------------------------------------
// User/Group Assignments (Act Phase)
// ---------------------------------------------------------------------------
/* G373 — Path-UUID functions: refuse to fire when agentId is missing or not
 * a UUID. Without the guard the template literal stringifies undefined into
 * the URL → backend returns 400 "Path or query parameter 'agentId' could
 * not be parsed as UUID", spamming the console on every Team page mount
 * (V2's AssignmentsInventory calls loadAssignments() with no arg). Returning
 * a benign empty success keeps callers' downstream array-handling intact. */
export const getAssignments = (agentId) => {
  if (!isUUID(agentId)) return Promise.resolve({ ok: true, data: [] });
  return apiFetch(`/api/v1/agents/${agentId}/assignments`);
};
export const createAssignment = (agentId, payload) => apiFetch(`/api/v1/agents/${agentId}/assignments`, 'POST', payload);
export const removeAssignment = (agentId, assignmentId) => apiFetch(`/api/v1/agents/${agentId}/assignments/${assignmentId}`, 'DELETE');

// ---------------------------------------------------------------------------
// Publish (Act Phase)
// ---------------------------------------------------------------------------
export const publishAgent = (agentId, userId) => apiFetch(`/api/v1/agents/${agentId}/publish`, 'POST', { userId });
export const getPublications = (agentId, page, size = 10) =>
  page !== undefined
    ? apiFetch(`/api/v1/agents/${agentId}/publications?page=${page}&size=${size}`)
    : apiFetch(`/api/v1/agents/${agentId}/publications`);
export const rollbackPublication = (agentId, pubId, userId) => apiFetch(`/api/v1/agents/${agentId}/publish/${pubId}/rollback`, 'POST', { userId });

// ---------------------------------------------------------------------------
// Agentlets (Act Phase — auto-discovered containers)
// ---------------------------------------------------------------------------
export const getAgentlets = ({ tenantId, tenantOrGlobal } = {}) => {
  const params = new URLSearchParams();
  if (isUUID(tenantId)) params.append('tenantId', tenantId);
  // tenantOrGlobal=true returns ONLY tenant-scoped + global containers (no
  // "show all" dev fallback) — the exact set the publish gate counts, so the
  // Agentlets Live panel and the Act→Published gate stay in agreement.
  if (tenantOrGlobal && isUUID(tenantId)) params.append('tenantOrGlobal', 'true');
  const qs = params.toString();
  return apiFetch(`/api/v1/agentlets${qs ? `?${qs}` : ''}`);
};
export const pingAgentlet = (agentletId) => apiFetch(`/api/v1/agentlets/${agentletId}/ping`);
export const deleteAgentlet = (agentletId) => apiFetch(`/api/v1/agentlets/${agentletId}`, 'DELETE');
export const updateAgentlet = (agentletId, payload) => apiFetch(`/api/v1/agentlets/${agentletId}`, 'PATCH', payload);

// ---------------------------------------------------------------------------
// Publish Validation & Selective Deploy (Act Phase)
// ---------------------------------------------------------------------------
export const validatePublish = (agentId) => apiFetch(`/api/v1/agents/${agentId}/publish/validate`, 'POST');
export const publishSelective = (agentId, agentletIds, userId) => apiFetch(`/api/v1/agents/${agentId}/publish/selective`, 'POST', { agentletIds, userId });
/* G178/G179 — Read-only preview of the current phase gate (build→decide,
 * decide→act). Lets V2's BuildTab/DecideTab render the authoritative backend
 * checks instead of hand-coded criteria that drift from platform truth. */
export const previewPhaseGate = (agentId, fromPhase) => apiFetch(
  `/api/v1/agents/${agentId}/phase-gate/preview${fromPhase ? `?fromPhase=${encodeURIComponent(fromPhase)}` : ''}`);

/* Agentlet-creation drafts (server-side, V331). Owner-scoped on the backend — a user only
 * ever sees their own drafts. Used by the Create Agentlet flow to Save/Resume/Discard an
 * in-progress form across sessions and devices. */
export const listAgentDrafts = () => apiFetch('/api/v1/agent-drafts');
export const getAgentDraft = (id) => apiFetch(`/api/v1/agent-drafts/${id}`);
export const saveAgentDraft = (payload) => apiFetch('/api/v1/agent-drafts', 'POST', payload);
export const deleteAgentDraft = (id) => apiFetch(`/api/v1/agent-drafts/${id}`, 'DELETE');

// Test Run — pre-publish skill execution with gate checks & RBAC simulation
export const testRunAgent = (agentId, skillName, input = {}, simulateRole = null) =>
  apiFetch(`/api/v1/agents/${agentId}/test-run`, 'POST', {
    skillName,
    input,
    ...(simulateRole ? { simulateRole } : {}),
  });

/* ───────────────────────────────────────────────────────────────────────────────────────
 * G273d — Cognitive run budgets are DERIVED, not hardcoded.
 *
 * The 30s REQUEST_TIMEOUT was aborting live cognitive runs: the AbortController fired while
 * the agent was still reasoning, and the UI showed the bare DOM string "signal is aborted
 * without reason" — as if the AGENT had failed. It hadn't; the client gave up.
 *
 * The tempting fix is a bigger constant. That is what happened last time (AI_ANALYST_TIMEOUT_MS
 * above), and it is why we were about to add a THIRD magic number. But a global constant is
 * wrong in principle: the wall clock of a cognitive run is a property of the AGENT, not of the
 * app. An agent whose brief allows 25 iterations legitimately needs far longer than one allowed
 * 3, and any single number is simultaneously too short for the first and too long for the second.
 *
 * A hardcoded client timeout was never a config problem — it was a MISSING SEAM. The client had
 * no idea what the server's budget was. So the server now tells it
 * (GET /api/v1/platform/execution-limits, read straight off the fields the execution loop obeys),
 * and the client sizes its patience from the same arithmetic the runtime does:
 *
 *     budget = rounds × llmTimeoutSeconds + overhead
 *
 * where `rounds` is the agent's own cognitiveBrief.maxIterations (clamped to the server's hard
 * ceiling) and `llmTimeoutSeconds` is the platform's LLM_TIMEOUT_SECONDS. Raise the env var or
 * the brief and the client follows — no redeploy, no guess, and the number the client waits for
 * is provably the number the server enforces.
 *
 * NOTE (deliberate): we do NOT clamp the derived budget to some "sane UI maximum". That would
 * re-introduce the exact bug — a client that gives up on a run the server is still legitimately
 * executing. The derived value IS the server's own worst case; if that worst case is an
 * uncomfortable wait, the answer is to lower maxIterations or LLM_TIMEOUT_SECONDS, or to move
 * test-run chat onto the async/SSE path (deferred) — not to lie to the user about it.
 * ─────────────────────────────────────────────────────────────────────────────────────── */

/** Non-LLM slack: policy gates (entry/per-tool/exit), tool execution, network, JSON. */
const COGNITIVE_OVERHEAD_MS = 30000;

/** Used only if the limits endpoint is unreachable. Mirrors the server defaults; deliberately
 *  generous, because guessing LOW here re-creates the very abort we're fixing. */
const EXECUTION_LIMITS_FALLBACK = {
  llmTimeoutSeconds: 180,
  defaultMaxToolRounds: 5,
  hardMaxToolRounds: 25,
};

let _executionLimitsCache = null;

/** Runtime ceilings the server actually enforces. Cached — they change only on redeploy. */
export const getPlatformExecutionLimits = async () => {
  if (_executionLimitsCache) return _executionLimitsCache;
  try {
    const res = await apiFetch('/api/v1/platform/execution-limits');
    const body = res && res.ok ? res.data : res;
    if (body && typeof body.llmTimeoutSeconds === 'number') {
      _executionLimitsCache = { ...EXECUTION_LIMITS_FALLBACK, ...body };
      return _executionLimitsCache;
    }
  } catch (_) {
    /* Older backend or transient failure — fall through. Do NOT cache the fallback, so a
     * later call can still pick up the real limits. */
  }
  return EXECUTION_LIMITS_FALLBACK;
};

/**
 * Client wait budget for one cognitive turn, in ms.
 * @param {number|null} maxIterations agent.cognitiveBrief.maxIterations (null → server default)
 */
export const cognitiveRunBudgetMs = async (maxIterations) => {
  const limits = await getPlatformExecutionLimits();
  const requested = Number(maxIterations);
  const rounds = Number.isFinite(requested) && requested >= 1
    ? Math.min(requested, limits.hardMaxToolRounds)   // same clamp the server applies
    : limits.defaultMaxToolRounds;
  return rounds * limits.llmTimeoutSeconds * 1000 + COGNITIVE_OVERHEAD_MS;
};

// Test Run Chat — proxy messages to agentlet gateway's /chat endpoint.
// Gated by `features.ai.testRunChat` (default ON).
// `opts.maxIterations` — pass agent.cognitiveBrief.maxIterations so the wait is sized to THIS
// agent. Omitted → the server's default round count is used.
export const testRunChat = async (agentId, message, history = [], opts = {}) => {
  if (!isAIFeatureEnabled('testRunChat')) return aiBlockedResponse('testRunChat', 'testRunChat');
  const timeout = await cognitiveRunBudgetMs(opts.maxIterations);
  return apiFetch(`/api/v1/agents/${agentId}/test-run/chat`, 'POST', { message, history }, timeout);
};

// ---------------------------------------------------------------------------
// Workflows & Canvas
// ---------------------------------------------------------------------------
// V132 — `archived` controls the server-side filter:
//   undefined / 'false' → non-archived only (default, matches backend default)
//   'all'               → include archived AND non-archived
//   'true'              → only archived rows (Archived view)
//
// RC-19f (task #210) — `standaloneOnly` (default false) restricts the
// result set to workflows with is_standalone=true. Used by the AutoLet
// "From an Existing Workflow" picker so AutoLet CEGs (already bound to
// existing agents, persisted with isStandalone=false) never appear as
// eligible source workflows. Other callers omit the flag and get the
// pre-RC-19f behavior unchanged.
export const getWorkflows = ({ orgId, tenantId, instanceId, agentId, archived, standaloneOnly } = {}) => {
  const params = new URLSearchParams();
  if (isUUID(orgId)) params.append('orgId', orgId);
  if (isUUID(tenantId)) params.append('tenantId', tenantId);
  // RC-Instance-Scope-Enforcement (#210, 210d) — instanceId from
  // useActiveScope() narrows /api/v1/workflows the same way orgId /
  // tenantId already do. Callers that don't pass instanceId continue
  // to get the back-compat path (no instance narrowing, server-side
  // JWT backfill applies for non-SA). Existing call sites that omit
  // the new field break NOTHING — JS object destructuring ignores
  // unknown keys and `instanceId` defaults to undefined.
  if (isUUID(instanceId)) params.append('instanceId', instanceId);
  if (agentId) params.append('agentId', agentId);
  if (archived) params.append('archived', String(archived));
  if (standaloneOnly) params.append('standaloneOnly', 'true');
  return apiFetch(`/api/v1/workflows?${params}`);
};
/**
 * Fetch a workflow's detail payload.
 *
 * @param {string} workflowId — UUID
 * @param {string[]} [expand] — optional enrichment keys. Supported:
 *   'source'  → conversion provenance + reportId
 *   'binding' → workerBindingStatus
 *   'runs'    → latestRun + recentRuns (last 5)
 *   Unknown keys are ignored server-side.
 *
 * Default (no expand) is the lean shape the canvas editor needs:
 * id/name/description/definition/status/triggerType/version/agentId/
 * orgId/tenantId/timestamps/createdBy. Pass expand=['source','binding','runs']
 * for the WorkflowDetailPage's full enrichment view.
 */
export const getWorkflow = (workflowId, expand) => {
    const qs = Array.isArray(expand) && expand.length
        ? `?expand=${expand.map((s) => encodeURIComponent(s)).join(',')}`
        : '';
    return apiFetch(`/api/v1/workflows/${workflowId}${qs}`);
};

/**
 * Resolve which navigation items (Recent trail / deep links) are accessible in
 * the caller's CURRENT scope + still surfaceable (not archived/deleted).
 * `items` = [{ type, id }]. Returns { accessible: [{ type, id }] }. Authority is
 * the backend (keys off the JWT scope) — the FE just renders the survivors.
 */
export const validateRecentAccess = (items) =>
    apiFetch('/api/v1/navigation/recent-access', 'POST', { items: items || [] });

export const createWorkflow = (payload) => apiFetch('/api/v1/workflows', 'POST', payload);
export const updateWorkflow = (workflowId, payload) => apiFetch(`/api/v1/workflows/${workflowId}`, 'PUT', payload);
export const deleteWorkflow = (workflowId) => apiFetch(`/api/v1/workflows/${workflowId}`, 'DELETE');

/*
 * RC-117 Phase 1 — Workflow version history.
 *
 * `listWorkflowVersions` returns a lightweight rowset (no full
 * definitions) — the Versions slideout scans it on open.
 * `getWorkflowVersion` returns ONE version's full definition for the
 * diff / rollback surfaces Phase 2 builds on top.
 *
 * Both gated by the same `workflow.read` permission as the parent
 * workflow GET — if you can see the workflow, you can see its history.
 */
export const listWorkflowVersions = (workflowId) =>
  apiFetch(`/api/v1/workflows/${workflowId}/versions`);

export const getWorkflowVersion = (workflowId, version) =>
  apiFetch(`/api/v1/workflows/${workflowId}/versions/${version}`);

/*
 * RC-117b — Roll back a workflow to a prior version.
 *
 * Creates a NEW version equal to the target (no destructive rewind —
 * the in-between versions stay in history). BE runs the target def
 * through the same validator gate an interactive save uses; a
 * validator rejection comes back as a 422 with structured issues the
 * Canvas Issues panel can render. 404 when the target version doesn't
 * exist on this workflow.
 */
export const rollbackWorkflowVersion = (workflowId, version) =>
  apiFetch(`/api/v1/workflows/${workflowId}/versions/${version}/rollback`, 'POST', {});
export const getWorkflowLinkedSkills = (workflowId) => apiFetch(`/api/v1/workflows/${workflowId}/linked-skills`);

/* PEP-5 — Effective-config projection for a single canvas node.
 *
 * Returns the resolved view of tenant rules + per-workflow override +
 * per-node override + the effective rule set the runtime would apply
 * at this node. Powers the Inspector's "Effective config" panel.
 *
 * Returns null when the workflow/node isn't found (404), so callers
 * can render an empty state without throwing. Other errors bubble.
 */
export const getEffectiveConfig = async (workflowId, nodeId) => {
  if (!workflowId || !nodeId) return null;
  try {
    return await apiFetch(
      `/api/v1/governance/effective-config?workflowId=${encodeURIComponent(workflowId)}&nodeId=${encodeURIComponent(nodeId)}`
    );
  } catch (err) {
    // 404 → null (Inspector will render an empty/diagnostic state)
    if (err && (err.status === 404 || /404|not.?found/i.test(String(err.message || '')))) {
      return null;
    }
    throw err;
  }
};
/* RC-117c + RC-134 — Save the canvas with optional change-narrative + optimistic-lock guard.
 *
 *   • changeKind  — narrative hint for the WorkflowVersion snapshot
 *                   (manual_save | ai_build | ai_fix | import). Sent as
 *                   query param. Unknown values silently fall back to
 *                   manual_save in WorkflowService.parseChangeKind.
 *
 *   • ifMatchVersion — RC-134 optimistic-lock guard. Sent as the RFC 7232
 *                   `If-Match` header. The BE compares to the workflow's
 *                   current version; mismatch returns 412 (status:412,
 *                   etagMismatch:true, code:"workflow_version_conflict",
 *                   currentVersion, attemptedVersion). The FE conflict
 *                   banner reads those fields. Pass the literal string
 *                   "*" (= "ignore") to disable the check — the
 *                   Save-anyway flow does this after the operator
 *                   acknowledges the conflict. Omit (null/undefined) to
 *                   keep legacy last-write-wins behavior — additive,
 *                   pre-RC-134 callers continue to work unchanged.
 */
export const saveCanvas = (workflowId, canvasData, { changeKind, ifMatchVersion } = {}) => {
  const qs = changeKind ? `?changeKind=${encodeURIComponent(changeKind)}` : '';
  const headers = {};
  if (ifMatchVersion != null && ifMatchVersion !== '') {
    // Send as a plain value — the BE normalisation strips W/ and " "
    // quoting so we don't need to wrap. Special-case "*" stays "*".
    headers['If-Match'] = String(ifMatchVersion);
  }
  return apiFetch(
    `/api/v1/workflows/${workflowId}/canvas${qs}`,
    'PUT',
    canvasData,
    undefined,   // timeout
    undefined,   // externalSignal
    Object.keys(headers).length > 0 ? headers : null,
  );
};
export const executeWorkflow = (workflowId, payload) => apiFetch(`/api/v1/workflows/${workflowId}/execute`, 'POST', payload);

// Resolves the inputs the workflow requires at invocation. Merges native
// trigger-node parameters with external workflow input schemas (for GEL
// Protected wrappers) — Execute prompt uses this to drive the modal.
export const getWorkflowRequiredInputs = (workflowId) =>
  apiFetch(`/api/v1/workflows/${workflowId}/required-inputs`);

// Canvas AI Builder  (optional signal for user-initiated abort).
// Gated by `features.ai.canvasBuilder` — also fronts the V3
// recommendation-chip apply path (each chip click hits this same endpoint).
// RC-97f — Optional 5th arg `linkedAgentSkills`: array of
// {id, name, description?} when the canvas is bound to an agent.
// Defaults to undefined; absent in the wire body when not provided,
// so existing callers that pass only 4 args get IDENTICAL request
// bytes to pre-RC-97f. The BE accepts the field as optional and
// gracefully no-ops when missing.
export const aiCanvasBuild = (prompt, currentCanvas, installedConnectors, signal, linkedAgentSkills) => {
  if (!isAIFeatureEnabled('canvasBuilder')) return Promise.resolve(aiBlockedResponse('canvasBuilder', 'aiCanvasBuild'));
  const body = { prompt, currentCanvas, installedConnectors };
  if (Array.isArray(linkedAgentSkills) && linkedAgentSkills.length > 0) {
    body.linkedAgentSkills = linkedAgentSkills;
  }
  return apiFetch('/api/v1/canvas/ai/build', 'POST', body, 420000, signal);
};

/**
 * Draft a NEW canvas workflow from selected Process Library documents.
 * Reuses the AI Workflow Builder server-side; returns the same
 * {nodes, connections, summary, warnings} shape as aiCanvasBuild for the
 * canvas to render as an unsaved draft. Gated by workflow.create on the BE.
 */
export const aiCanvasBuildFromProcessFiles = (stagingRefs, installedConnectors, signal) => {
  if (!isAIFeatureEnabled('canvasBuilder')) return Promise.resolve(aiBlockedResponse('canvasBuilder', 'aiCanvasBuildFromProcessFiles'));
  return apiFetch('/api/v1/canvas/ai/from-process-files', 'POST', { stagingRefs, installedConnectors }, 420000, signal);
};

// Gated by `features.ai.canvasDebugger` — also fronts the AI Explainer
// panel (Tier-S #1) which uses this endpoint with a focused subgraph.
//
// RC-101 — `targetNodeId` (optional, 6th arg) tells the BE that the FE
// wants Claude to focus on EXACTLY ONE node, not the whole subgraph.
// When present, BE rewrites the Claude prompt to "explain only this
// node, return a SINGLE-entry nodeAnalysis" — so the FE's RC-97i2
// matcher trivially succeeds and operators stop seeing "Couldn't
// generate explanation" on perfectly-good Claude responses.
//
// Back-compat: when targetNodeId is omitted, behavior is unchanged
// (whole-subgraph analysis as before). Existing callers in
// CanvasPageV3.jsx don't pass it and aren't affected.
/*
 * RC-109c — optional `mode` argument (default = legacy "debug").
 *
 * The AI Explainer panel (per-node {business, technical, security}
 * tabs) passes mode='explain' to switch the BE to a system prompt
 * tuned for audience-tagged output. Pre-RC-109c the BE always used
 * the debug system prompt, which mandated the {summary, nodeAnalysis}
 * shape — the FE then dumped the synthesized markdown into the
 * Business tab only, leaving Technical and Security empty and the
 * render-fallback duplicated the Business content on all three tabs.
 *
 * mode is the LAST arg so existing positional callers (workflow-level
 * debugger panel) don't need to change.
 */
export const aiCanvasDebug = (prompt, currentCanvas, executionResults, installedConnectors, signal, targetNodeId, mode) => {
  if (!isAIFeatureEnabled('canvasDebugger')) return Promise.resolve(aiBlockedResponse('canvasDebugger', 'aiCanvasDebug'));
  const body = { prompt, currentCanvas, executionResults, installedConnectors };
  if (targetNodeId && typeof targetNodeId === 'string' && targetNodeId.trim()) {
    body.targetNodeId = targetNodeId.trim();
  }
  if (mode && typeof mode === 'string' && mode.trim()) {
    body.mode = mode.trim();
  }
  return apiFetch('/api/v1/canvas/ai/debug', 'POST', body, 420000, signal);
};

/* Bug #161 — Deterministic in-place node-type swap.
 *
 * Posts the current canvas + node id + new type to the backend swap
 * service, which preserves node identity (id, position, label, edges)
 * and cascades reference rewrites across the canvas. Returns:
 *   {canvas, referencesRewritten, impacts: [{referencingNodeId,
 *    configPath, expression, reason}]}
 *
 * Backed by CanvasSwapController.swapNode. Closed-ontology guard —
 * newType must be one of the values returned by canvasSwapAllowedTypes()
 * (any other value 400s with "newType not in CanonicalNodeType...").
 */
export const canvasSwapNode = (payload, signal) =>
  apiFetch('/api/v1/canvas/swap/node', 'POST', payload, 30000, signal);

/** Returns the closed set of canvas types the swap endpoint accepts. */
export const canvasSwapAllowedTypes = () =>
  apiFetch('/api/v1/canvas/swap/allowed-types');

// Email (SMTP) Connector
export const testEmailConnection = (smtpConfig) =>
  apiFetch('/api/v1/connectors/email/test', 'POST', smtpConfig, 30000);
export const sendTestEmail = (payload) =>
  apiFetch('/api/v1/connectors/email/send', 'POST', payload, 30000);

// Workflow Runs
export const getWorkflowRuns = (workflowId) => apiFetch(`/api/v1/workflows/${workflowId}/runs`);
export const getWorkflowRun = (runId) => apiFetch(`/api/v1/workflows/runs/${runId}`);
export const cancelWorkflowRun = (runId) => apiFetch(`/api/v1/workflows/runs/${runId}/cancel`, 'POST');
export const getNodeExecutions = (runId) => apiFetch(`/api/v1/workflows/runs/${runId}/nodes`);
export const getRunDagSnapshot = (runId) => apiFetch(`/api/v1/workflows/runs/${runId}/dag-snapshot`);

// Schedule Status & Control
export const getScheduleStatus = (workflowId) => apiFetch(`/api/v1/workflows/${workflowId}/schedule-status`);
export const archiveWorkflow = (workflowId) => apiFetch(`/api/v1/workflows/${workflowId}/archive`, 'POST');
export const unarchiveWorkflow = (workflowId) => apiFetch(`/api/v1/workflows/${workflowId}/unarchive`, 'POST');

export const disableSchedule = (workflowId) => apiFetch(`/api/v1/workflows/${workflowId}/schedule/disable`, 'POST');
export const enableSchedule = (workflowId) => apiFetch(`/api/v1/workflows/${workflowId}/schedule/enable`, 'POST');

// Execution Controls
export const retryFromFailedStep = (runId, body = {}) =>
  apiFetch(`/api/v1/workflows/runs/${runId}/retry`, 'POST', body);

export const resumeWorkflowRun = (runId, body = {}) =>
  apiFetch(`/api/v1/workflows/runs/${runId}/resume`, 'POST', body);

/* RC-120b — Replay a prior run with its STORED inputs as a fresh
 * execution. Distinct from retryFromFailedStep:
 *   • retry  → resumes from the failed step (preserves completed-node outputs)
 *   • replay → brand-new run from the START with the same input envelope
 * Used by the failed-node deep-dive "Replay with same inputs" affordance. */
export const replayWorkflowRun = (runId, body = {}) =>
  apiFetch(`/api/v1/workflows/runs/${runId}/replay`, 'POST', body);

export const terminateWorkflowRun = (runId) =>
  apiFetch(`/api/v1/workflows/runs/${runId}/terminate`, 'POST');

// Workflow RBAC Assignments
export const getWorkflowAssignments = (workflowId) =>
  apiFetch(`/api/v1/workflows/${workflowId}/assignments`);

export const createWorkflowAssignment = (workflowId, body) =>
  apiFetch(`/api/v1/workflows/${workflowId}/assignments`, 'POST', body);

export const removeWorkflowAssignment = (assignmentId) =>
  apiFetch(`/api/v1/workflows/assignments/${assignmentId}`, 'DELETE');

// Control Chat access grants (per-user/group/role → tool/skill/label/agent, ALLOW/DENY).
// Filter by subject: pass a userId for a person's grants, or a groupId for a group's grants.
export const getChatAccessGrants = (userId, groupId) => {
  const qs = userId ? `?userId=${userId}` : groupId ? `?groupId=${groupId}` : '';
  return apiFetch(`/api/v1/chat-access/grants${qs}`);
};
export const createChatAccessGrant = (body) =>
  apiFetch(`/api/v1/chat-access/grants`, 'POST', body);
// Update a grant in place (object/effect/dataScope/expiry/agent scope). The subject is immutable —
// to re-target a subject, remove and recreate. Same body shape as createChatAccessGrant.
export const updateChatAccessGrant = (grantId, body) =>
  apiFetch(`/api/v1/chat-access/grants/${grantId}`, 'PUT', body);
export const removeChatAccessGrant = (grantId) =>
  apiFetch(`/api/v1/chat-access/grants/${grantId}`, 'DELETE');
export const getMyChatAccess = (agentId) =>
  apiFetch(`/api/v1/chat-access/me${agentId ? `?agentId=${agentId}` : ''}`);
// Agent-scoped when agentId is supplied: returns THIS chat agent's live-verified
// tools (same source as GET /me), so grants intersect what the agent actually
// exposes. Omit agentId ("All chats") for the union catalog.
export const getChatAccessCatalog = (agentId) =>
  apiFetch(`/api/v1/chat-access/catalog${agentId ? `?agentId=${agentId}` : ''}`);

// Workflow Audit Log
export const getWorkflowAuditLog = (workflowId) =>
  apiFetch(`/api/v1/workflows/${workflowId}/audit`);

export const getRunAuditLog = (runId) =>
  apiFetch(`/api/v1/workflows/runs/${runId}/audit`);

// Orchestration Dashboard
export const getOrchestrationStats = (orgId, tenantId) => {
  const params = new URLSearchParams({ orgId, tenantId });
  return apiFetch(`/api/v1/workflows/orchestration/stats?${params}`);
};

export const activateKillSwitch = (agentId, payload) => apiFetch(`/api/v1/agents/${agentId}/inference/kill-switch/activate`, 'POST', payload);
export const deactivateKillSwitch = (agentId, payload) => apiFetch(`/api/v1/agents/${agentId}/inference/kill-switch/deactivate`, 'POST', payload || {});
export const setInferenceMode = (agentId, mode) => apiFetch(`/api/v1/agents/${agentId}/inference/mode`, 'PUT', { mode });
export const setSkillInferenceMode = (agentId, skillId, mode) => apiFetch(`/api/v1/agents/${agentId}/skills/${skillId}/inference/mode`, 'PUT', { mode });
export const getOverrides = (agentId) => apiFetch(`/api/v1/agents/${agentId}/inference/overrides`);
export const getInferenceStats = (agentId) => apiFetch(`/api/v1/agents/${agentId}/inference/stats`);
export const getInferenceAuditLog = (agentId) => apiFetch(`/api/v1/agents/${agentId}/inference/audit-log`);

// ---------------------------------------------------------------------------
// Agentlet Operations
// ---------------------------------------------------------------------------
export const purgeAgentletAgents = (agentletId) => apiFetch(`/api/v1/agentlets/${agentletId}/purge-agents`, 'POST');
export const purgeAllAgentletAgents = () => apiFetch(`/api/v1/agentlets/purge-all`, 'POST');

// ---------------------------------------------------------------------------
// Platform Promotion (Export / Import)
// ---------------------------------------------------------------------------
export const platformExportPreview = (includeSystem = true, includeUser = true) =>
  apiFetch(`/api/v1/platform/export/preview?includeSystem=${includeSystem}&includeUser=${includeUser}`);
export const platformExport = ({ includeSystem = true, includeUser = true, agentIds, workflowIds, connectorIds, userIds, userGroupIds } = {}) => {
  const params = new URLSearchParams();
  params.append('includeSystem', includeSystem);
  params.append('includeUser', includeUser);
  if (agentIds) params.append('agentIds', agentIds);
  if (workflowIds) params.append('workflowIds', workflowIds);
  if (connectorIds) params.append('connectorIds', connectorIds);
  if (userIds) params.append('userIds', userIds);
  if (userGroupIds) params.append('userGroupIds', userGroupIds);
  return apiFetch(`/api/v1/platform/export?${params.toString()}`);
};
export const platformImport = (manifest, strategy = 'SKIP') =>
  apiFetch(`/api/v1/platform/import?strategy=${strategy}`, 'POST', manifest);

// ---------------------------------------------------------------------------
// Connectors
// ---------------------------------------------------------------------------
/* Both connector endpoints — /api/v1/connectors and /api/v1/gel/connectors — return
 * each connector wrapped with its sharing metadata, as
 * { connector, isOwner, sharedWithTenantIds, sharedWithGrants }, rather than as a bare
 * connector. Unwrapping happens HERE, once, so that no screen has to know the wire
 * shape and the two endpoints cannot drift apart.
 *
 * This is deliberately the only decoder. When each caller decoded for itself, the
 * ones that forgot still "worked": a status filter of `!c.status || c.status ===
 * 'ACTIVE'` passes when `c.status` is undefined, so the connector was kept and every
 * field read off it afterwards was undefined. The screen rendered an empty row rather
 * than an error, which is indistinguishable from having no connectors at all.
 *
 * Tolerant of an already-unwrapped row so an older server response still works. */
const flattenConnectorEnvelope = (row) => {
  if (row && typeof row === 'object' && 'connector' in row) {
    return {
      ...row.connector,
      __isOwner: row.isOwner !== false,
      __sharedWithTenantIds: Array.isArray(row.sharedWithTenantIds) ? row.sharedWithTenantIds : [],
      /* Grant shape: [{ tenantId, instanceIds[] }]. Empty instanceIds means cascade. */
      __sharedWithGrants: Array.isArray(row.sharedWithGrants) ? row.sharedWithGrants : [],
    };
  }
  if (!row || typeof row !== 'object') return row;
  return {
    ...row,
    __isOwner: row.__isOwner !== undefined ? row.__isOwner : true,
    __sharedWithTenantIds: Array.isArray(row.__sharedWithTenantIds) ? row.__sharedWithTenantIds : [],
    __sharedWithGrants: Array.isArray(row.__sharedWithGrants) ? row.__sharedWithGrants : [],
  };
};

/* Collection endpoints have been served as a bare array, a paged {content}, and a
 * {items} envelope at different times. Normalise once so callers read `res.data`
 * and nothing else. */
const asConnectorArray = (d) =>
  Array.isArray(d) ? d
    : Array.isArray(d?.items) ? d.items
      : Array.isArray(d?.content) ? d.content
        : Array.isArray(d?.data) ? d.data
          : [];

export const getConnectors = async ({ tenantId, orgId, type, status } = {}) => {
  const params = new URLSearchParams();
  if (isUUID(tenantId)) params.append('tenantId', tenantId);
  if (isUUID(orgId)) params.append('orgId', orgId);
  if (type) params.append('type', type);
  if (status) params.append('status', status);
  const qs = params.toString();
  const res = await apiFetch(`/api/v1/connectors${qs ? `?${qs}` : ''}`);
  if (!res?.ok) return res;
  return { ...res, data: asConnectorArray(res.data).map(flattenConnectorEnvelope) };
};
export const getConnector = async (connectorId) => {
  const res = await apiFetch(`/api/v1/connectors/${connectorId}`);
  if (!res?.ok || !res.data) return res;
  return { ...res, data: flattenConnectorEnvelope(res.data) };
};
export const createConnector = (payload) => apiFetch('/api/v1/connectors', 'POST', payload);
export const updateConnector = (connectorId, payload) => apiFetch(`/api/v1/connectors/${connectorId}`, 'PUT', payload);
export const deleteConnector = (connectorId) => apiFetch(`/api/v1/connectors/${connectorId}`, 'DELETE');
export const testConnector = (connectorId) => apiFetch(`/api/v1/connectors/${connectorId}/test`, 'POST');
export const getConnectorConfig = (connectorId) => apiFetch(`/api/v1/connectors/${connectorId}/config`);
export const saveConnectorConfig = (connectorId, configFields) => apiFetch(`/api/v1/connectors/${connectorId}/config`, 'PUT', configFields);
export const testConnectorUrl = (url) => apiFetch('/api/v1/connectors/test-url', 'POST', { url });

/* V349 — Directory scope: WHICH people a SCIM connector pulls for the caller's organization.
 *
 * There is no orgId parameter on purpose. The backend resolves the org from the caller's token,
 * exactly as the sync itself does, so the scope that gets saved is necessarily the scope the sync
 * will apply. Letting the client name an org here is how you end up with a filter that is stored,
 * displayed as active, and silently never used.
 *
 * getConnectorDirectoryScope resolves with a 204 and no body when nothing is configured — that is
 * the "pulls everything" state, not an error.
 */
export const getConnectorDirectoryScope = (connectorId) => apiFetch(`/api/v1/connectors/${connectorId}/directory-scope`);
export const saveConnectorDirectoryScope = (connectorId, scope) => apiFetch(`/api/v1/connectors/${connectorId}/directory-scope`, 'PUT', scope);
export const deleteConnectorDirectoryScope = (connectorId) => apiFetch(`/api/v1/connectors/${connectorId}/directory-scope`, 'DELETE');

/* V226 / CONN-SCOPE — cross-tenant share grants for generic connectors.
 *   assignConnectorTenantScopes — append new active grants.
 *   revokeConnectorTenantScope  — soft-revoke a single tenant grant.
 *
 * BE permission gate: caller must be Org Admin / Super Admin / Platform Admin.
 * Tenant Admin call → 403. The FE conditionally renders these affordances
 * based on session role, so the 403 is a backstop rather than an expected path. */
export const assignConnectorTenantScopes = (connectorId, tenantIds) =>
  apiFetch(`/api/v1/connectors/${connectorId}/scope-tenants`, 'POST', { tenantIds: Array.isArray(tenantIds) ? tenantIds : [] });
export const revokeConnectorTenantScope = (connectorId, tenantId) =>
  apiFetch(`/api/v1/connectors/${connectorId}/scope-tenants/${encodeURIComponent(tenantId)}`, 'DELETE');

/* V227 / CONN-SCOPE — per-instance grant variants.
 *
 *   assignConnectorTenantGrants — rich shape. `grants` is an array of
 *     {tenantId, instanceIds[]}. Empty instanceIds → cascade (all
 *     instances). Non-empty → per-instance grants. BE enforces mutual
 *     exclusion: switching a (connector, tenant) from cascade ↔ specific
 *     soft-revokes the opposite-type rows.
 *
 *   revokeConnectorTenantInstanceScope — soft-revoke a single per-instance
 *     grant. Idempotent (no-op if no active grant). Distinct from
 *     revokeConnectorTenantScope which revokes the tenant entirely. */
export const assignConnectorTenantGrants = (connectorId, grants) =>
  apiFetch(`/api/v1/connectors/${connectorId}/scope-tenants`, 'POST', { grants: Array.isArray(grants) ? grants : [] });
export const revokeConnectorTenantInstanceScope = (connectorId, tenantId, instanceId) =>
  apiFetch(`/api/v1/connectors/${connectorId}/scope-tenants/${encodeURIComponent(tenantId)}/instances/${encodeURIComponent(instanceId)}`, 'DELETE');

/* ─── Secrets Manager (Phase 1) ───────────────────────────────────────────
 * Backend surface: /api/v1/secrets (SecretController). Listing is opt-in per
 * backend — Phase 1 implements list() only on LocalEncryptedSecretBackend;
 * remote backends report empty until Phase 3. Plaintext never leaves the
 * vault — these endpoints only handle metadata + references. */
export const listSecretBackends = () => apiFetch('/api/v1/secrets/backends');
export const listSecrets = (backendId) => apiFetch(
  `/api/v1/secrets${backendId ? `?backend=${encodeURIComponent(backendId)}` : ''}`);
export const storeSecret = (payload) => apiFetch('/api/v1/secrets', 'POST', payload);
export const rotateSecret = (ref, newPlaintext) =>
  apiFetch('/api/v1/secrets/rotate', 'POST', { ref, newPlaintext });
export const deleteSecret = (ref) =>
  apiFetch(`/api/v1/secrets?ref=${encodeURIComponent(ref)}`, 'DELETE');
export const checkSecretExists = (ref) =>
  apiFetch(`/api/v1/secrets/exists?ref=${encodeURIComponent(ref)}`);

/* Phase 2 — per-org backend configuration. Sensitive fields come back masked
 * with a •••••••• sentinel; sending the sentinel back on PUT preserves the
 * stored value (round-trip safe). Server-side gates these on secrets.admin
 * (Org Admin only). */
export const getSecretBackendConfig = (backendId) =>
  apiFetch(`/api/v1/secrets/backends/${encodeURIComponent(backendId)}/config`);
export const saveSecretBackendConfig = (backendId, configFields) =>
  apiFetch(`/api/v1/secrets/backends/${encodeURIComponent(backendId)}/config`, 'PUT', configFields);
export const deleteSecretBackendConfig = (backendId) =>
  apiFetch(`/api/v1/secrets/backends/${encodeURIComponent(backendId)}/config`, 'DELETE');
// OAuth user-account connection
export const authorizeConnector = (connectorId) => apiFetch(`/api/v1/connectors/${connectorId}/authorize`, 'POST');
export const exchangeConnectorOAuthCode = (connectorId, code) => apiFetch(`/api/v1/connectors/${connectorId}/authorize/callback`, 'POST', { code });
export const getMyConnections = () => apiFetch('/api/v1/connectors/my-connections');
export const getConnectorSkills = (connectorId) => apiFetch(`/api/v1/connectors/${connectorId}/skills`);
export const linkConnectorSkill = (connectorId, skillId, payload) => apiFetch(`/api/v1/connectors/${connectorId}/skills/${skillId}`, 'POST', payload);
export const unlinkConnectorSkill = (connectorId, skillId) => apiFetch(`/api/v1/connectors/${connectorId}/skills/${skillId}`, 'DELETE');
export const getConnectorStats = (orgId, tenantId) => {
  const params = new URLSearchParams();
  if (isUUID(orgId)) params.append('orgId', orgId);
  if (isUUID(tenantId)) params.append('tenantId', tenantId);
  const qs = params.toString();
  return apiFetch(`/api/v1/connectors/stats${qs ? `?${qs}` : ''}`);
};

// ─── Connector Registry (read-only manifest catalogue) ────────────────────
// Served by ConnectorRegistryController. These endpoints expose the static
// manifests loaded from classpath:connectors/registry at startup — used by
// the marketplace UI and the Visual Mapper to list available integrations.
export const listConnectorManifests = (filters = {}) => {
  const params = new URLSearchParams();
  if (filters.category) params.append('category', filters.category);
  if (filters.canonicalType) params.append('canonicalType', filters.canonicalType);
  const qs = params.toString();
  return apiFetch(`/api/v1/connector-registry/manifests${qs ? `?${qs}` : ''}`);
};
export const getConnectorManifest = (id) => apiFetch(`/api/v1/connector-registry/manifests/${id}`);
export const listConnectorCategories = () => apiFetch('/api/v1/connector-registry/categories');
export const listConnectorPlatforms = () => apiFetch('/api/v1/connector-registry/platforms');
export const listConnectorSkills = (filters = {}) => {
  const params = new URLSearchParams();
  if (filters.category) params.append('category', filters.category);
  if (filters.connectorId) params.append('connectorId', filters.connectorId);
  const qs = params.toString();
  return apiFetch(`/api/v1/connector-registry/skills${qs ? `?${qs}` : ''}`);
};
export const listConnectorGuardrails = (filters = {}) => {
  const params = new URLSearchParams();
  if (filters.severity) params.append('severity', filters.severity);
  if (filters.connectorId) params.append('connectorId', filters.connectorId);
  const qs = params.toString();
  return apiFetch(`/api/v1/connector-registry/guardrails${qs ? `?${qs}` : ''}`);
};
export const listConnectorEvals = (filters = {}) => {
  const params = new URLSearchParams();
  if (filters.metric) params.append('metric', filters.metric);
  if (filters.connectorId) params.append('connectorId', filters.connectorId);
  const qs = params.toString();
  return apiFetch(`/api/v1/connector-registry/evals${qs ? `?${qs}` : ''}`);
};
export const getConnectorRegistrySummary = () => apiFetch('/api/v1/connector-registry/summary');

// Skill-side connector endpoints
export const getSkillConnectors = (agentId, skillId) => apiFetch(`/api/v1/agents/${agentId}/skills/${skillId}/connectors`);
export const linkSkillConnector = (agentId, skillId, payload) => apiFetch(`/api/v1/agents/${agentId}/skills/${skillId}/connectors`, 'POST', payload);
export const unlinkSkillConnector = (agentId, skillId, connectorId) => apiFetch(`/api/v1/agents/${agentId}/skills/${skillId}/connectors/${connectorId}`, 'DELETE');

// Connector Groups
export const getConnectorGroups = (tenantId) => {
  const qs = tenantId ? `?tenantId=${tenantId}` : '';
  return apiFetch(`/api/v1/connector-groups${qs}`);
};
export const createConnectorGroup = (payload) => apiFetch('/api/v1/connector-groups', 'POST', payload);
export const updateConnectorGroup = (groupId, payload) => apiFetch(`/api/v1/connector-groups/${groupId}`, 'PUT', payload);
export const deleteConnectorGroup = (groupId) => apiFetch(`/api/v1/connector-groups/${groupId}`, 'DELETE');
export const addConnectorGroupMember = (groupId, connectorId) => apiFetch(`/api/v1/connector-groups/${groupId}/connectors/${connectorId}`, 'POST');
export const removeConnectorGroupMember = (groupId, connectorId) => apiFetch(`/api/v1/connector-groups/${groupId}/connectors/${connectorId}`, 'DELETE');

// Agent-Connector Assignments
/* G373 — Same defensive guards as getAssignments. See note above. */
export const getAgentConnectorAssignments = (agentId) => {
  if (!isUUID(agentId)) return Promise.resolve({ ok: true, data: [] });
  return apiFetch(`/api/v1/agents/${agentId}/connector-assignments`);
};
export const createAgentConnectorAssignment = (agentId, payload) => apiFetch(`/api/v1/agents/${agentId}/connector-assignments`, 'POST', payload);
export const removeAgentConnectorAssignment = (agentId, assignmentId) => apiFetch(`/api/v1/agents/${agentId}/connector-assignments/${assignmentId}`, 'DELETE');
export const getAssignedConnectorIds = (agentId) => {
  if (!isUUID(agentId)) return Promise.resolve({ ok: true, data: [] });
  return apiFetch(`/api/v1/agents/${agentId}/assigned-connector-ids`);
};

// ---------------------------------------------------------------------------
// Deployments
// ---------------------------------------------------------------------------
export const getDeployments = ({ orgId, tenantId, agentId, environment, status } = {}) => {
  const params = new URLSearchParams();
  if (isUUID(orgId)) params.append('orgId', orgId);
  if (isUUID(tenantId)) params.append('tenantId', tenantId);
  if (agentId) params.append('agentId', agentId);
  if (environment) params.append('environment', environment);
  if (status) params.append('status', status);
  const qs = params.toString();
  return apiFetch(`/api/v1/deployments${qs ? `?${qs}` : ''}`);
};
export const getDeployment = (deploymentId) => apiFetch(`/api/v1/deployments/${deploymentId}`);
export const createDeployment = (payload) => apiFetch('/api/v1/deployments', 'POST', payload);
export const updateDeployment = (deploymentId, payload) => apiFetch(`/api/v1/deployments/${deploymentId}`, 'PUT', payload);
export const updateDeploymentStatus = (deploymentId, status) => apiFetch(`/api/v1/deployments/${deploymentId}/status`, 'PUT', { status });
export const deleteDeployment = (deploymentId) => apiFetch(`/api/v1/deployments/${deploymentId}`, 'DELETE');
export const getDeploymentHierarchy = (orgId) => {
  const params = new URLSearchParams();
  if (isUUID(orgId)) params.append('orgId', orgId);
  const qs = params.toString();
  return apiFetch(`/api/v1/deployments/hierarchy${qs ? `?${qs}` : ''}`);
};
export const getDeploymentHealth = (deploymentId) => apiFetch(`/api/v1/deployments/${deploymentId}/health`);
export const deployFromAgent = (agentId, config) => apiFetch(`/api/v1/deployments/from-agent/${agentId}`, 'POST', config);
export const getDeploymentStats = (orgId, tenantId) => {
  const params = new URLSearchParams();
  if (isUUID(orgId)) params.append('orgId', orgId);
  if (isUUID(tenantId)) params.append('tenantId', tenantId);
  const qs = params.toString();
  return apiFetch(`/api/v1/deployments/stats${qs ? `?${qs}` : ''}`);
};

// ---------------------------------------------------------------------------
// Organizations
// ---------------------------------------------------------------------------
export const getOrganizations = () => apiFetch('/api/v1/organizations');
export const getOrganization = (orgId) => apiFetch(`/api/v1/organizations/${orgId}`);
export const createOrganization = (payload) => apiFetch('/api/v1/organizations', 'POST', payload);
export const updateOrganization = (orgId, payload) => apiFetch(`/api/v1/organizations/${orgId}`, 'PUT', payload);
export const deleteOrganization = (orgId) => apiFetch(`/api/v1/organizations/${orgId}`, 'DELETE');

// ---------------------------------------------------------------------------
// Tenants
// ---------------------------------------------------------------------------
// ---------------------------------------------------------------------------
// Instances  (Org → Tenant → Instance)
// ---------------------------------------------------------------------------
/**
 * GET /api/v1/instances — backend filters by caller's most-specific-wins
 * effective role. Optional orgId is a platform-admin-only override that
 * widens the scope to a different org (back-end rejects non-admin probes
 * with 403, so this is safe to always send).
 *
 * G526 — accepts the orgId override the Phase-5 Multi-Tenancy page passes
 * when the URL deep-links to a cross-org scope.
 */
export const getInstances = (orgId) => {
  const params = new URLSearchParams();
  if (isUUID(orgId)) params.append('orgId', orgId);
  const qs = params.toString();
  return apiFetch(`/api/v1/instances${qs ? `?${qs}` : ''}`);
};
export const getInstance = (instanceId) => apiFetch(`/api/v1/instances/${instanceId}`);
export const createInstance = (payload) => apiFetch('/api/v1/instances', 'POST', payload);
export const updateInstance = (instanceId, payload) => apiFetch(`/api/v1/instances/${instanceId}`, 'PUT', payload);
export const deleteInstance = (instanceId) => apiFetch(`/api/v1/instances/${instanceId}`, 'DELETE');

// Cloud Operations deployment targets — used to link an Instance to a target (mode derives from it).
// Returns { items: [{ id, name, mode, ... }] }. PA + SA readable.
export const listDeploymentTargets = (page = 0, size = 100) =>
  apiFetch(`/api/v1/cloud-operations/targets?page=${page}&size=${size}`);

export const getTenants = () => apiFetch('/api/v1/tenants');
export const getTenantsByOrg = (orgId) => apiFetch(`/api/v1/organizations/${orgId}/tenants`);
export const createTenant = (payload) => apiFetch('/api/v1/tenants', 'POST', payload);
export const updateTenant = (tenantId, payload) => apiFetch(`/api/v1/tenants/${tenantId}`, 'PUT', payload);
export const deleteTenant = (tenantId) => apiFetch(`/api/v1/tenants/${tenantId}`, 'DELETE');

// ── Escalation approver directory (V347) ─────────────────────────────────────
//
// Backs the approver picker that replaced the free-text box. That box wrote whatever was typed as
// the tier's targetValue, and EscalationService requires a user id — so every approver ever typed
// resolved to the org-admin fallback instead of the person named.
//
// Returns a slim projection (never the user entity) scoped to the caller's reachable tenants.
export const searchApprovers = ({ q, department, title, userType, page = 0, size = 20 } = {}) => {
  const params = new URLSearchParams();
  if (q) params.append('q', q);
  if (department) params.append('department', department);
  if (title) params.append('title', title);
  if (userType) params.append('userType', userType);
  params.append('page', page);
  params.append('size', size);
  return apiFetch(`/api/v1/team/approvers?${params.toString()}`);
};

// Names for the ids stored on a saved policy's tiers. POST because the id list belongs in a body
// rather than a query string that proxies and access logs record.
export const resolveApprovers = (ids) =>
  apiFetch('/api/v1/team/approvers/resolve', 'POST', { ids: Array.isArray(ids) ? ids : [] });

export const getUsers = ({ tenantId, role } = {}) => {
  const params = new URLSearchParams();
  if (isUUID(tenantId)) params.append('tenantId', tenantId);
  // PROVE7-054 — optional RBAC role filter: return only users who HOLD this role, so the intent
  // "specific approver" picker can never offer a non-holder (which the workflow would reject).
  if (role) params.append('role', role);
  const qs = params.toString();
  return apiFetch(`/api/v1/team/users${qs ? `?${qs}` : ''}`);
};
export const createUser = (payload) => apiFetch('/api/v1/team/users', 'POST', payload);
export const updateUser = (userId, payload) => apiFetch(`/api/v1/team/users/${userId}`, 'PUT', payload);
// DEF-RBAC-USER-LIFECYCLE-001 — DELETE now SOFT-deactivates by default
// (recoverable, keeps memberships, blocks login, stays visible). `deleteUser`
// is retained for back-compat and maps to the safe soft path.
export const deleteUser = (userId) => apiFetch(`/api/v1/team/users/${userId}`, 'DELETE');
export const deactivateUser = (userId) => apiFetch(`/api/v1/team/users/${userId}`, 'DELETE');
export const reactivateUser = (userId) => apiFetch(`/api/v1/team/users/${userId}/reactivate`, 'PUT');
// Irreversible hard delete (offboard/compliance) — gate behind type-to-confirm in the UI.
export const purgeUser = (userId) => apiFetch(`/api/v1/team/users/${userId}?purge=true`, 'DELETE');
// DEF-RBAC-CUSTOMER-MOVE-001 — atomically move a grant (no leftover duplicate).
export const moveUserMembership = (userId, membershipId, payload) =>
  apiFetch(`/api/v1/team/users/${userId}/memberships/${membershipId}/move`, 'PUT', payload);

export const getGroups = ({ tenantId } = {}) => {
  const params = new URLSearchParams();
  if (isUUID(tenantId)) params.append('tenantId', tenantId);
  const qs = params.toString();
  return apiFetch(`/api/v1/team/groups${qs ? `?${qs}` : ''}`);
};
export const createGroup = (payload) => apiFetch('/api/v1/team/groups', 'POST', payload);
export const updateGroup = (groupId, payload) => apiFetch(`/api/v1/team/groups/${groupId}`, 'PUT', payload);
export const deleteGroup = (groupId) => apiFetch(`/api/v1/team/groups/${groupId}`, 'DELETE');

// Group membership
export const addGroupMember = (groupId, userId) => apiFetch(`/api/v1/team/groups/${groupId}/members/${userId}`, 'POST');
export const removeGroupMember = (groupId, userId) => apiFetch(`/api/v1/team/groups/${groupId}/members/${userId}`, 'DELETE');

// User Org/Tenant Memberships
export const getUserMemberships = (userId) => apiFetch(`/api/v1/team/users/${userId}/memberships`);
export const addUserMembership = (userId, payload) => apiFetch(`/api/v1/team/users/${userId}/memberships`, 'POST', payload);
export const removeUserMembership = (userId, membershipId) => apiFetch(`/api/v1/team/users/${userId}/memberships/${membershipId}`, 'DELETE');
export const setDefaultMembership = (userId, membershipId) => apiFetch(`/api/v1/team/users/${userId}/memberships/${membershipId}/set-default`, 'PUT');

// User Invitations
export const sendInvitation = (payload) => apiFetch('/api/v1/team/invitations', 'POST', payload);
export const getInvitations = () => apiFetch('/api/v1/team/invitations');
export const revokeInvitation = (invitationId) => apiFetch(`/api/v1/team/invitations/${invitationId}`, 'DELETE');
export const validateInviteToken = (token) => apiFetch(`/api/v1/auth/invite/validate?token=${encodeURIComponent(token)}`);
export const registerWithInvite = (token, payload) => apiFetch('/api/v1/auth/invite/register', 'POST', { token, ...payload });
// Send a chosen email template to a user via the platform Email (SMTP) connector.
export const sendUserEmail = (userId, templateId) => apiFetch(`/api/v1/team/users/${userId}/send-email`, 'POST', { templateId });

// ════════════════════════════════════════════════════════════════════════
// RC-161 — Forms · To Do (Shape B form_task fill surface)
// ════════════════════════════════════════════════════════════════════════
// Wraps the FormTaskController endpoints (RC-156). List + fill + submit.
// SSE subscription is set up in the page component via EventSource.

/** List form_task assignments the current user can fill. */
export const listMyFormTasks = (statuses) => {
  const params = new URLSearchParams();
  if (Array.isArray(statuses)) statuses.forEach((s) => params.append('status', s));
  const qs = params.toString();
  return apiFetch(`/api/v1/form-tasks/me${qs ? `?${qs}` : ''}`);
};

/**
 * RC-226 — Pending-count for the Forms left-nav badge. Returns
 * {count: N} (server uses COUNT(*), no row materialization). Status
 * filter is hardcoded server-side to CREATED + ASSIGNED — the badge
 * means "tasks currently waiting for me", not "everything I've ever
 * been involved with".
 */
export const countMyPendingFormTasks = () =>
  apiFetch('/api/v1/form-tasks/me/pending-count');

// ════════════════════════════════════════════════════════════════════════
// RC-227 — Unified My Tasks inbox (Forms + Attestations)
// ════════════════════════════════════════════════════════════════════════

/**
 * Unified My Tasks row list — aggregates Forms (form_task_assignments) +
 * Attestations (attestation_tasks) for the current user. Server returns
 * pre-sorted normalized rows of shape:
 *   { type: 'form'|'attestation', id, title, status, dueAt, createdAt,
 *     openUrl, ...sourceSpecificFields }
 * Sort order: dueAt ASC with NULL-due last (date-bound tasks rise).
 */
export const listMyTasks = () =>
  apiFetch('/api/v1/my-tasks');

/**
 * 2026-07-04 — history variant. Same envelope shape (list of rows in
 * `.data`), same row schema (type/id/title/status/dueAt/openUrl/…).
 * Sort order: most-recent-completion first (submittedAt DESC, falling
 * back to createdAt for rows that terminal-ed without a submission).
 * FE consumer: MyTasksPage "History" tab.
 */
export const listMyTasksHistory = () =>
  apiFetch('/api/v1/my-tasks/history');

/**
 * Pending-count for the My Tasks left-nav badge. Sums form-task pending
 * count + attestation pending count (cheap COUNT(*) per source, no row
 * materialization). Single round-trip from the FE.
 */
export const countMyPendingTasks = () =>
  apiFetch('/api/v1/my-tasks/pending-count');

/** Fetch a single form_task assignment for the fill page. */
export const getFormTaskForFill = (assignmentId) =>
  apiFetch(`/api/v1/form-tasks/${encodeURIComponent(assignmentId)}`);

/**
 * 2026-07-04 — upstream-submissions for the FormFillPage "Prior submissions"
 * card. Returns a chronological list of every completed assignee-side row
 * in the same workflow run (SUBMITTED form_task_assignments + SUBMITTED
 * external_step_pauses), excluding the current assignment. Each row:
 *   { source, workflowNodeId, stepId?, title, submittedAt, submittedValues }
 * Same eligibility gate as getFormTaskForFill (403 if the caller is not
 * in the resolved assignee set).
 */
export const getFormTaskUpstreamSubmissions = (assignmentId) =>
  apiFetch(`/api/v1/form-tasks/${encodeURIComponent(assignmentId)}/upstream-submissions`);

/** Submit form values for a form_task assignment. */
export const submitFormTask = (assignmentId, formValues) =>
  apiFetch(`/api/v1/form-tasks/${encodeURIComponent(assignmentId)}/submit`, 'POST', formValues);

/** Cancel a form_task assignment (admin / system). */
export const cancelFormTask = (assignmentId, reason) =>
  apiFetch(`/api/v1/form-tasks/${encodeURIComponent(assignmentId)}/cancel`, 'POST', { reason });

/**
 * V328 (2026-07-07) — Workflow Protect · Reveal PII.
 *
 * POSTs to /api/v1/form-tasks/{id}/reveal-pii with an operator-supplied
 * justification (bounded to 250 chars server-side via @Size). Returns the
 * decrypted submitted_values plus reveal metadata:
 *
 *   {
 *     formTaskId, workflowRunId, submittedAt, submitterUserId,
 *     fieldsRevealed: [...],
 *     submittedValues: { ...plaintext }
 *   }
 *
 * Only callers with the `pii.reveal` permission (Compliance role and
 * above) succeed. 403 for insufficient role, 404 for missing / cross-
 * tenant assignment (existence-leak protection), 409 for not-yet-submitted
 * assignments. Every successful call writes a PII_REVEAL PolicyAuditEvent
 * row — reveal is permanent in the audit chain.
 *
 * See docs/design/WORKFLOW_PROTECT_ENCRYPTION_AND_MASKING.md §5.4.
 */
export const revealFormTaskPii = (assignmentId, justification) =>
  apiFetch(
    `/api/v1/form-tasks/${encodeURIComponent(assignmentId)}/reveal-pii`,
    'POST',
    { justification },
  );

// ════════════════════════════════════════════════════════════════════════
// RC-163a/b — Forms Catalogue (cross-workflow form_task projection)
// ════════════════════════════════════════════════════════════════════════
// Wraps FormsCatalogueController. Distinct from /form-tasks above:
//   - /api/v1/form-tasks   = per-user assignee inbox (Shape B runtime)
//   - /api/v1/forms        = author-side catalogue of form_task node
//                            definitions across all visible workflows
// FormsLibraryPage (RC-162d) + FormsDesignerIndexPage (RC-162c) consume
// listForms; FormDetailPage / FormDesignerPage load consume getFormDetail.

/**
 * List every form_task / human_task node across visible workflows,
 * narrowed by the active-scope chip (orgId + tenantId + instanceId from
 * useActiveScope()). Mirrors /api/v1/workflows scope semantics exactly
 * per RC-163a-scope-fix + RC-Instance-Scope-Enforcement (#210):
 *   - Non-SA callers: backfill from JWT.
 *   - SA/OA: narrow within accessible envelope; chip is UX-narrowing only.
 *   - Instance Admin / Developer / Operator: JWT-pinned instance is the
 *     authoritative envelope; chip MUST match the JWT pin or the
 *     request returns empty (no leak).
 *   - PA: no envelope; chip narrows freely.
 * Bubble-up: workflows with instance_id IS NULL (tenant-level) remain
 * visible from every instance under that tenant.
 *
 * @param {{orgId?: string, tenantId?: string, instanceId?: string}} [opts]
 *   Any combination may be omitted. Calling with no args returns
 *   whatever the caller's envelope shows without chip narrowing.
 * Returns FormCatalogueEntry[] — see BE FormCatalogueEntry.java for shape.
 */
export const listForms = (opts = {}) => {
  const params = new URLSearchParams();
  if (opts.orgId)      params.append('orgId',      opts.orgId);
  if (opts.tenantId)   params.append('tenantId',   opts.tenantId);
  if (opts.instanceId) params.append('instanceId', opts.instanceId);
  const qs = params.toString();
  return apiFetch(`/api/v1/forms${qs ? `?${qs}` : ''}`);
};

/**
 * Get full detail for a single form_task. Returns FormDetailResponse:
 *   { entry, recentAssignments[], assignmentStatusBreakdown }
 * 404 when out-of-scope or unknown node — matches /api/v1/workflows/{id}.
 */
export const getFormDetail = (workflowId, nodeId) =>
  apiFetch(`/api/v1/forms/${encodeURIComponent(workflowId)}/${encodeURIComponent(nodeId)}`);

// G539 — Runtime refresh of the caller's effective permission set.
// Frontend calls this after a role/scope change that the standard auth
// response paths can't reach (e.g. an admin in another tab just promoted
// the user; the user can manually refresh perms without re-login).
// Returns { role, permissions: string[] }.
export const getPermissions = () => apiFetch('/api/v1/auth/permissions');

// ── SEC-25 Phase 2 (2026-06-11) — REMOVED dead-code connectWebSocket ──
// The pre-SEC-25 implementation here connected to `/ws/agents/${agentId}?token=${jwt}`
// with the JWT in the URL query string. Two problems:
//   1. The URL query is the audit-flagged token leak (nginx access logs,
//      browser history, Referer headers).
//   2. The backend endpoint `/ws/agents/...` does not exist — no
//      `@ServerEndpoint`, no STOMP endpoint registration. Dead code.
// Zero callers found in the FE either (`api.connectWebSocket` is exported
// but referenced nowhere). Keeping a function that documents an insecure
// URL-token pattern is a future-footgun, so it's been deleted outright.
// The live WebSocket path is in services/websocket.js, post-SEC-25-Phase-2
// converted to cookie + STOMP-CONNECT-header auth.

// ─── Approval System ──────────────────────────────────────

// Inbox & queries
export const getApprovalInbox = () => apiFetch('/api/v1/approvals/inbox');
export const getApprovalInboxCount = () => apiFetch('/api/v1/approvals/inbox/count');
export const getApprovalSubmitted = () => apiFetch('/api/v1/approvals/submitted');
// PROVE7-040 — role-holder's oversight of their role's queue (all statuses, tenant-scoped).
export const getMyRoleApprovals = () => apiFetch('/api/v1/approvals/my-role');
export const getApprovals = ({ orgId, tenantId, status } = {}) => {
  const params = new URLSearchParams();
  if (isUUID(orgId)) params.append('orgId', orgId);
  if (isUUID(tenantId)) params.append('tenantId', tenantId);
  if (status) params.append('status', status);
  const qs = params.toString();
  return apiFetch(`/api/v1/approvals${qs ? '?' + qs : ''}`);
};
export const getApprovalRequest = (requestId) => apiFetch(`/api/v1/approvals/${requestId}`);
export const getEnrichedApprovalRequest = (requestId) => apiFetch(`/api/v1/approvals/${requestId}/enriched`);
export const getApprovalAuditLog = (requestId) => apiFetch(`/api/v1/approvals/${requestId}/audit-log`);
export const getApprovalsBySource = (sourceType, sourceId) => apiFetch(`/api/v1/approvals/source/${sourceType}/${sourceId}`);
export const getApprovalStats = ({ orgId, tenantId } = {}) => {
  const params = new URLSearchParams();
  if (isUUID(orgId)) params.append('orgId', orgId);
  if (isUUID(tenantId)) params.append('tenantId', tenantId);
  const qs = params.toString();
  return apiFetch(`/api/v1/approvals/stats${qs ? '?' + qs : ''}`);
};

// Decision actions — formData is the operator-supplied form payload
// (only meaningful for HUMAN-task bridged approvals; safely ignored
// otherwise). Sent as the orchestrator task's outputData on Approve.
export const approveApprovalRequest = (requestId, comment, formData) =>
    apiFetch(`/api/v1/approvals/${requestId}/approve`, 'POST',
        formData ? { comment, formData } : { comment });
export const rejectApprovalRequest = (requestId, comment) => apiFetch(`/api/v1/approvals/${requestId}/reject`, 'POST', { comment });
export const cancelApprovalRequest = (requestId) => apiFetch(`/api/v1/approvals/${requestId}/cancel`, 'POST');

// Policies
export const getApprovalPolicies = ({ orgId, tenantId } = {}) => {
  const params = new URLSearchParams();
  if (isUUID(orgId)) params.append('orgId', orgId);
  if (isUUID(tenantId)) params.append('tenantId', tenantId);
  const qs = params.toString();
  return apiFetch(`/api/v1/approvals/policies${qs ? '?' + qs : ''}`);
};
export const getApprovalPoliciesForTarget = (targetType, targetId) => apiFetch(`/api/v1/approvals/policies/target/${targetType}/${targetId}`);
export const createApprovalPolicy = (policy) => apiFetch('/api/v1/approvals/policies', 'POST', policy);
export const updateApprovalPolicy = (policyId, updates) => apiFetch(`/api/v1/approvals/policies/${policyId}`, 'PUT', updates);
export const deleteApprovalPolicy = (policyId) => apiFetch(`/api/v1/approvals/policies/${policyId}`, 'DELETE');

// ─── Policy Documents (RAG) ──────────────────────────────────────
export const uploadPolicyDocument = async (skillId, file, enforcement = 'advisory', priority = 0) => {
  const token = _authToken || getAccessToken();
  const formData = new FormData();
  formData.append('file', file);
  formData.append('enforcement', enforcement);
  formData.append('priority', String(priority));
  const headers = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;
  if (_tenantId) headers['X-Tenant-Id'] = _tenantId;
  /* SEC/CSRF (RC-91 family) — multipart uploads use a raw fetch (FormData must
   * set its own boundary), so they bypass apiFetch's CSRF + cookie wiring.
   * Mirror it: echo the csrf_token cookie in X-CSRF-Token (CsrfHeaderFilter
   * requires it on every mutating request) and send credentials so the auth +
   * csrf cookies ride along. Without this, the strict CSRF hardening 403s the
   * upload with "CSRF token missing or invalid". */
  const csrf = readCsrfCookie();
  if (csrf) headers['X-CSRF-Token'] = csrf;
  const res = await fetch(`${API_URL}/api/v1/skills/${skillId}/policy-documents`, {
    method: 'POST', headers, body: formData, credentials: 'include',
  });
  if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.error || `Upload failed (${res.status})`); }
  return res.json();
};
export const listPolicyDocuments = (skillId) => apiFetch(`/api/v1/skills/${skillId}/policy-documents`);
/** Tenant-wide process-document library (across all policy skills) for the Auto-Create file picker. */
export const listTenantProcessDocuments = () => apiFetch('/api/v1/policy-documents');
export const deletePolicyDocument = (skillId, docId) => apiFetch(`/api/v1/skills/${skillId}/policy-documents/${docId}`, 'DELETE');
export const searchPolicyDocuments = (skillId, query, topK = 5) => apiFetch(`/api/v1/skills/${skillId}/policy-documents/search`, 'POST', { query, topK });
export const getPolicyDocumentStats = (skillId) => apiFetch(`/api/v1/skills/${skillId}/policy-documents/stats`);
export const reprocessPolicyDocument = (skillId, docId) => apiFetch(`/api/v1/skills/${skillId}/policy-documents/${docId}/reprocess`, 'POST');
export const getPolicyDocumentPreview = (skillId, docId) => apiFetch(`/api/v1/skills/${skillId}/policy-documents/${docId}/preview`);

// ── Per-skill policy bind / unbind (POLICY_BINDING_RUNTIME_DESIGN Phase 2) ──
// listSkillPolicyBindings → bound runtime controls for a policy skill.
// bindSkillPolicyControl → 409 {duplicate:true,...} when (rule,skill,PEP) already bound.
// unbindSkillPolicyControl → delete the binding row (rule untouched); 403 for framework bindings.
export const listSkillPolicyBindings = (skillId) => apiFetch(`/api/v1/skills/${skillId}/policy-bindings`);
export const bindSkillPolicyControl = (skillId, policyRuleId, enforcementPoint) =>
  apiFetch(`/api/v1/skills/${skillId}/policy-bindings`, 'POST', { policyRuleId, enforcementPoint });
export const unbindSkillPolicyControl = (bindingId) =>
  apiFetch(`/api/v1/policy-bindings/${bindingId}`, 'DELETE');

// ── Unified global search (#14 Phase B) — scoped + permissioned backend ──
// Returns ranked SearchHit[] {type,id,label,sublabel,path,score} across
// agents/skills/policies/connectors/users/orgs/tenants the caller may read.
export const searchGlobal = (q, { types, limit, orgId, tenantId } = {}) => {
  const params = new URLSearchParams();
  params.set('q', q);
  if (types) params.set('types', Array.isArray(types) ? types.join(',') : types);
  if (limit) params.set('limit', String(limit));
  // Active UI scope (validated server-side) so search matches the tenant/org the
  // user is viewing rather than the JWT home tenant.
  if (orgId) params.set('orgId', orgId);
  if (tenantId) params.set('tenantId', tenantId);
  return apiFetch(`/api/v1/search?${params.toString()}`);
};

// ─── Intelligence Center ──────────────────────────────────────────
// Gated by `features.ai.intelligenceChat`.
export const intelligenceChat = (sessionId, message) => {
  if (!isAIFeatureEnabled('intelligenceChat')) return Promise.resolve(aiBlockedResponse('intelligenceChat', 'intelligenceChat'));
  return apiFetch('/api/intelligence/chat', 'POST', { sessionId, message });
};
export const intelligenceChatHistory = (sessionId) => apiFetch(`/api/intelligence/chat/${sessionId}`);
export const intelligenceDrillDown = (targetType, targetId) => apiFetch(`/api/intelligence/drill-down/${targetType}/${targetId}`);
export const intelligenceActions = (hours = 24) => apiFetch(`/api/intelligence/actions?hours=${hours}`);
export const intelligencePendingActions = () => apiFetch('/api/intelligence/actions/pending');
export const intelligenceRollbackableActions = () => apiFetch('/api/intelligence/actions/rollbackable');
export const intelligenceApproveAction = (id) => apiFetch(`/api/intelligence/actions/${id}/approve`, 'POST');
export const intelligenceDismissAction = (id) => apiFetch(`/api/intelligence/actions/${id}/dismiss`, 'POST');
export const intelligenceRollbackAction = (id) => apiFetch(`/api/intelligence/actions/${id}/rollback`, 'POST');
export const intelligenceBulkApprove = (ids) => apiFetch('/api/intelligence/actions/bulk/approve', 'POST', { ids });
export const intelligenceBulkDismiss = (ids) => apiFetch('/api/intelligence/actions/bulk/dismiss', 'POST', { ids });
export const intelligenceBulkRollback = (ids) => apiFetch('/api/intelligence/actions/bulk/rollback', 'POST', { ids });
export const intelligenceInsights = (hours = 24) => apiFetch(`/api/intelligence/insights?hours=${hours}`);
export const intelligenceActionableInsights = () => apiFetch('/api/intelligence/insights/actionable');
export const intelligenceAcknowledgeInsight = (id) => apiFetch(`/api/intelligence/insights/${id}/acknowledge`, 'POST');
export const intelligenceBulkAcknowledge = (ids) => apiFetch('/api/intelligence/insights/bulk/acknowledge', 'POST', { ids });
export const intelligenceConfig = () => apiFetch('/api/intelligence/config');
export const intelligenceSetAutonomyMode = (mode) => apiFetch('/api/intelligence/config/autonomy-mode', 'PUT', { mode });
export const intelligenceSystemWorkflows = () => apiFetch('/api/intelligence/workflows');

// ─── Knowledge Graph (Phase 1) ──────────────────────────────────
export const intelligenceGraphStats = () => apiFetch('/api/intelligence/graph/stats');
export const intelligenceGraphSnapshot = () => apiFetch('/api/intelligence/graph/snapshot');
export const intelligenceGraphNodes = (type, minHealth) => {
  const params = new URLSearchParams();
  if (type) params.append('type', type);
  if (minHealth != null) params.append('minHealth', minHealth);
  const qs = params.toString();
  return apiFetch(`/api/intelligence/graph/nodes${qs ? '?' + qs : ''}`);
};
export const intelligenceGraphNode = (id) => apiFetch(`/api/intelligence/graph/nodes/${id}`);
export const intelligenceGraphBlastRadius = (nodeId) => apiFetch(`/api/intelligence/graph/blast-radius/${nodeId}`);
export const intelligenceGraphDependencies = (nodeId) => apiFetch(`/api/intelligence/graph/dependencies/${nodeId}`);
export const intelligenceGraphDependencyPath = (from, to) => apiFetch(`/api/intelligence/graph/dependency-path/${from}/${to}`);
export const intelligenceGraphClusters = () => apiFetch('/api/intelligence/graph/clusters');
export const intelligenceGraphHealthTree = () => apiFetch('/api/intelligence/graph/health-tree');
export const intelligenceGraphSearch = (query) => apiFetch(`/api/intelligence/graph/search?q=${encodeURIComponent(query)}`);
export const intelligenceGraphRebuild = () => apiFetch('/api/intelligence/graph/rebuild', 'POST');

// ─── Outcome Tracking (Phase 1) ─────────────────────────────────
export const intelligenceOutcomeStats = () => apiFetch('/api/intelligence/outcomes/stats');
export const intelligenceOutcomes = (hours = 24) => apiFetch(`/api/intelligence/outcomes?hours=${hours}`);
export const intelligenceOutcomesForAction = (actionId) => apiFetch(`/api/intelligence/outcomes/action/${actionId}`);

// ─── Pattern Memory (Phase 1 + Phase 3) ─────────────────────────
export const intelligencePatternStats = () => apiFetch('/api/intelligence/patterns/stats');
export const intelligencePatterns = (type) => {
  const params = type ? `?type=${type}` : '';
  return apiFetch(`/api/intelligence/patterns${params}`);
};
export const intelligenceHighConfidencePatterns = () => apiFetch('/api/intelligence/patterns/high-confidence');

// Phase 3: Temporal OSS predictions
export const intelligenceUpcomingPatterns = (hours = 24) => apiFetch(`/api/intelligence/patterns/upcoming?hours=${hours}`);
export const intelligenceHighRecurrencePatterns = () => apiFetch('/api/intelligence/patterns/high-recurrence');

// Phase 3: Causal correlations
export const intelligenceCorrelations = () => apiFetch('/api/intelligence/correlations');
export const intelligenceCorrelationStats = () => apiFetch('/api/intelligence/correlations/stats');
export const intelligenceHighConfidenceCorrelations = () => apiFetch('/api/intelligence/correlations/high-confidence');
export const intelligenceCausalGraph = () => apiFetch('/api/intelligence/correlations/causal-graph');
export const intelligenceRootCause = (eventType) => apiFetch(`/api/intelligence/correlations/root-cause/${eventType}`);

// Phase 3: Resolution memory
export const intelligenceBestResolution = (targetType) => apiFetch(`/api/intelligence/resolutions/best/${targetType}`);
export const intelligenceSimilarResolutions = (targetType, severity) =>
  apiFetch(`/api/intelligence/resolutions/similar?targetType=${targetType}&severity=${severity || 'error'}`);
export const intelligenceHighSuccessResolutions = () => apiFetch('/api/intelligence/resolutions/high-success');

export const intelligenceRegisterSystemWorkflow = (sourceWorkflowId, name) =>
  apiFetch('/api/intelligence/workflows/register', 'POST', { sourceWorkflowId, name });
export const intelligenceUnregisterSystemWorkflow = (workflowId) =>
  apiFetch(`/api/intelligence/workflows/${workflowId}`, 'DELETE');

// v6.2 — Toggle system workflow flag (replaces copy-based registration)
export const intelligenceToggleSystemWorkflow = (workflowId, isSystem) =>
  apiFetch(`/api/intelligence/workflows/${workflowId}/system`, 'PUT', { isSystem });

// ─── Proactive Intelligence (Phase 4) ────────────────────────
export const intelligenceProactiveStats = () => apiFetch('/api/intelligence/proactive/stats');
export const intelligenceForecasts = () => apiFetch('/api/intelligence/proactive/forecasts');
export const intelligenceForecastsHighRisk = () => apiFetch('/api/intelligence/proactive/forecasts/high-risk');
export const intelligenceForecastsDeclining = () => apiFetch('/api/intelligence/proactive/forecasts/declining');
export const intelligenceOptimizedQueue = () => apiFetch('/api/intelligence/proactive/queue');
export const intelligenceSkillRecommendations = () => apiFetch('/api/intelligence/proactive/recommendations');
export const intelligencePromotionCandidates = () => apiFetch('/api/intelligence/proactive/recommendations/promote');
export const intelligenceAnomalies = () => apiFetch('/api/intelligence/proactive/anomalies');
export const intelligenceBaselines = () => apiFetch('/api/intelligence/proactive/baselines');

// ─── Remediation Engine (Phase 2) ────────────────────────────
export const intelligenceRemediationStats = () => apiFetch('/api/intelligence/remediation/stats');
export const intelligenceRemediationExecutions = (hours = 24) =>
  apiFetch(`/api/intelligence/remediation/executions?hours=${hours}`);
export const intelligenceRemediationActive = () => apiFetch('/api/intelligence/remediation/active');
export const intelligenceRemediationPlaybooks = () => apiFetch('/api/intelligence/remediation/playbooks');
export const intelligenceHealConnector = (connectorId) =>
  apiFetch('/api/intelligence/remediation/heal-connector', 'POST', { connectorId });
export const intelligenceRetryWorkflow = (runId) =>
  apiFetch('/api/intelligence/remediation/retry-workflow', 'POST', { runId });
export const intelligenceTriggerAlert = (alertType, targetName, severity, details) =>
  apiFetch('/api/intelligence/remediation/trigger-alert', 'POST', { alertType, targetName, severity, details });
export const intelligenceCancelRemediation = (id) =>
  apiFetch(`/api/intelligence/remediation/cancel/${id}`, 'POST');
export const intelligenceRerunRemediation = (id) =>
  apiFetch(`/api/intelligence/remediation/rerun/${id}`, 'POST');

// ─── AI Model Configuration ──────────────────────────────────────
export const getModelConfigs = () => apiFetch('/api/v1/model-config');
export const getModelConfig = (featureKey) => apiFetch(`/api/v1/model-config/${featureKey}`);
export const upsertModelConfig = (featureKey, config) => apiFetch(`/api/v1/model-config/${featureKey}`, 'PUT', config);
export const deleteModelConfig = (featureKey) => apiFetch(`/api/v1/model-config/${featureKey}`, 'DELETE');
export const getAvailableModels = () => apiFetch('/api/v1/model-config/available-models');
export const getModelFeatures = () => apiFetch('/api/v1/model-config/features');
// Probe Anthropic /v1/models with the configured API key — returns the
// authoritative list of model IDs the deployment can actually call.
// Use to populate the model picker with verified-accessible IDs instead
// of guessing snapshot strings from marketing copy.
export const probeAnthropicModels = () => apiFetch('/api/v1/model-config/probe-anthropic');

// ─── Template Center ─────────────────────────────────────────────
export const getTemplates = (type, category, status) => {
  const params = new URLSearchParams();
  if (type) params.append('type', type);
  if (category) params.append('category', category);
  if (status) params.append('status', status);
  const qs = params.toString();
  return apiFetch(`/api/v1/templates${qs ? '?' + qs : ''}`);
};
export const getTemplate = (id) => apiFetch(`/api/v1/templates/${id}`);
export const createTemplate = (payload) => apiFetch('/api/v1/templates', 'POST', payload);
export const updateTemplate = (id, payload) => apiFetch(`/api/v1/templates/${id}`, 'PUT', payload);
export const publishTemplate = (id) => apiFetch(`/api/v1/templates/${id}/publish`, 'PUT');
export const archiveTemplate = (id) => apiFetch(`/api/v1/templates/${id}/archive`, 'PUT');
export const deleteTemplate = (id) => apiFetch(`/api/v1/templates/${id}`, 'DELETE');
export const duplicateTemplate = (id) => apiFetch(`/api/v1/templates/${id}/duplicate`, 'POST');
// Bridge: turn a curated Template Center 'intent' template into a real IntentTemplate + v1 DRAFT
// (author then edits → submits → approves → binds). Requires compliance.admin/policy.admin.
export const createIntentFromTemplate = (templateId) =>
  apiFetch(`/api/v1/intent-templates/from-template/${templateId}`, 'POST');
// Gated by `features.ai.templateGenerator`.
export const aiGenerateTemplate = (prompt, templateType) => {
  if (!isAIFeatureEnabled('templateGenerator')) return Promise.resolve(aiBlockedResponse('templateGenerator', 'aiGenerateTemplate'));
  return apiFetch('/api/v1/templates/ai/generate', 'POST', { prompt, templateType }, 120000);
};

// ─── Email Log ───────────────────────────────────────────────────
export const getEmailLogs = (hours = 168, status, workflowName) => {
  const params = new URLSearchParams();
  params.append('hours', hours);
  if (status) params.append('status', status);
  if (workflowName) params.append('workflowName', workflowName);
  return apiFetch(`/api/v1/emails?${params.toString()}`);
};
export const getEmailLog = (id) => apiFetch(`/api/v1/emails/${id}`);
export const getEmailStats = (hours = 168) => apiFetch(`/api/v1/emails/stats?hours=${hours}`);
export const sendTemplateTestEmail = (payload) => apiFetch('/api/v1/emails/test', 'POST', payload);

// ─── Phase 5: Policy Skill Configuration ──────────────────────────
export const getPolicyConfigs = (status) => {
  const params = new URLSearchParams();
  if (status) params.append('status', status);
  const qs = params.toString();
  return apiFetch(`/api/v1/policy-config/configurations${qs ? '?' + qs : ''}`);
};
export const getPolicyConfig = (id) => apiFetch(`/api/v1/policy-config/configurations/${id}`);
export const getPolicyConfigBySkill = (skillId) => apiFetch(`/api/v1/policy-config/configurations/skill/${skillId}`);
export const createPolicyConfig = (payload) => apiFetch('/api/v1/policy-config/configurations', 'POST', payload);
export const updatePolicyConfig = (id, payload) => apiFetch(`/api/v1/policy-config/configurations/${id}`, 'PUT', payload);
export const updatePolicyConfigStatus = (id, status) => apiFetch(`/api/v1/policy-config/configurations/${id}/status`, 'POST', { status });
export const getPolicyConfigStats = () => apiFetch('/api/v1/policy-config/configurations/stats');

// ─── Enforcement Extraction ────────────────────────────────────────
// Gated by `features.ai.policyExtractor`.
export const triggerExtraction = (documentId, skillId) => {
  if (!isAIFeatureEnabled('policyExtractor')) return Promise.resolve(aiBlockedResponse('policyExtractor', 'triggerExtraction'));
  return apiFetch('/api/v1/policy-config/extract', 'POST', { documentId, skillId }, 120000);
};
export const getEnforcementCandidates = (skillId, status) => {
  const params = new URLSearchParams();
  params.append('skillId', skillId);
  if (status) params.append('status', status);
  return apiFetch(`/api/v1/policy-config/candidates?${params.toString()}`);
};
export const getCandidateStats = (skillId) => apiFetch(`/api/v1/policy-config/candidates/stats?skillId=${skillId}`);
// scope: 'agent' (default — control gates every skill of the agent) | 'skill' (this skill only)
export const acceptCandidate = (id, scope) => apiFetch(
  `/api/v1/policy-config/candidates/${id}/accept${scope ? `?scope=${encodeURIComponent(scope)}` : ''}`, 'POST');
export const rejectCandidate = (id, reason) => apiFetch(`/api/v1/policy-config/candidates/${id}/reject`, 'POST', { reason });
export const deferCandidate = (id) => apiFetch(`/api/v1/policy-config/candidates/${id}/defer`, 'POST');
export const customizeCandidate = (id, payload) => apiFetch(`/api/v1/policy-config/candidates/${id}/customize`, 'POST', payload);
export const batchAcceptCandidates = (skillId, threshold) => apiFetch('/api/v1/policy-config/candidates/batch-accept', 'POST', { skillId, threshold });
export const batchUpdateCandidates = (candidateIds, status) => apiFetch('/api/v1/policy-config/candidates/batch-update', 'POST', { candidateIds, status });
export const acceptPatternsAsRules = (skillId, patternIds) => apiFetch('/api/v1/policy-config/candidates/accept-patterns', 'POST', { skillId, patternIds });
// Controls a policy skill currently materializes (history), so the wizard isn't a blind restart.
export const getPolicySkillMaterialized = (skillId) => apiFetch(`/api/v1/policy-config/skills/${skillId}/materialized`);

// ─── Enforcement Pattern Library ───────────────────────────────────
export const getEnforcementPatterns = (domain) => {
  const params = new URLSearchParams();
  if (domain) params.append('domain', domain);
  const qs = params.toString();
  return apiFetch(`/api/v1/policy-config/patterns${qs ? '?' + qs : ''}`);
};
export const getPatternStats = () => apiFetch('/api/v1/policy-config/patterns/stats');
export const createPattern = (payload) => apiFetch('/api/v1/policy-config/patterns', 'POST', payload);
export const updatePattern = (id, payload) => apiFetch(`/api/v1/policy-config/patterns/${id}`, 'PUT', payload);
export const deletePattern = (id) => apiFetch(`/api/v1/policy-config/patterns/${id}`, 'DELETE');

// ─── Compliance Scoring & Autonomy ─────────────────────────────────
export const getComplianceOverview = () => apiFetch('/api/v1/policy-config/compliance/overview');
export const getAgentCompliance = (agentId) => apiFetch(`/api/v1/policy-config/compliance/agents/${agentId}`);
export const recalculateCompliance = (agentId) => apiFetch(`/api/v1/policy-config/compliance/agents/${agentId}/recalculate`, 'POST');
export const promoteAgentAutonomy = (agentId, targetLevel, reason) => apiFetch(`/api/v1/policy-config/compliance/agents/${agentId}/promote`, 'POST', { targetLevel, reason });
export const regressAgentAutonomy = (agentId, targetLevel, reason) => apiFetch(`/api/v1/policy-config/compliance/agents/${agentId}/regress`, 'POST', { targetLevel, reason });
export const getAutonomyTransitions = (agentId) => apiFetch(`/api/v1/policy-config/compliance/agents/${agentId}/transitions`);
export const getComplianceAlerts = (threshold = 75) => apiFetch(`/api/v1/policy-config/compliance/alerts?threshold=${threshold}`);
export const getPromotionEligible = (minScore = 90, minEvals = 100) => apiFetch(`/api/v1/policy-config/compliance/promotion-eligible?minScore=${minScore}&minEvals=${minEvals}`);

// ── Compliance Certificates ──────────────────────────────────
export const generateSoc2Certificate = (workflowRunId) => apiFetch(`/api/v1/compliance/certificates/soc2/${workflowRunId}`, 'POST');
export const generateHipaaCertificate = (workflowRunId) => apiFetch(`/api/v1/compliance/certificates/hipaa/${workflowRunId}`, 'POST');

// ── Demo Simulation Endpoints ────────────────────────────────
export const simulateDemoExecution = (workflowRunId) => apiFetch(`/api/v1/demo/simulate-execution/${workflowRunId}`, 'POST');
export const simulateDemoDpgScan = (workflowRunId) => apiFetch(`/api/v1/demo/simulate-dpg-scan/${workflowRunId}`, 'POST');
export const simulateDemoPolicyViolation = (workflowRunId, body) => apiFetch(`/api/v1/demo/simulate-policy-violation/${workflowRunId}`, 'POST', body);
export const simulateDemoFullSequence = (workflowRunId) => apiFetch(`/api/v1/demo/simulate-full-sequence/${workflowRunId}`, 'POST');

// ── Agent Trust Center ───────────────────────────────────────
export const getTrustDashboard = ({ orgId, tenantId, scope } = {}) => {
  const params = new URLSearchParams();
  if (isUUID(orgId)) params.append('orgId', orgId);
  if (isUUID(tenantId)) params.append('tenantId', tenantId);
  // Omitted when it adds nothing: the endpoint already defaults to the whole fleet.
  if (scope && scope !== 'all') params.append('scope', scope);
  const qs = params.toString();
  return apiFetch(`/api/v1/trust/dashboard${qs ? `?${qs}` : ''}`);
};

export const getTrustProfiles = ({ orgId, tenantId, riskLevel, autonomyLevel, onProbation } = {}) => {
  const params = new URLSearchParams();
  if (isUUID(orgId)) params.append('orgId', orgId);
  if (isUUID(tenantId)) params.append('tenantId', tenantId);
  if (riskLevel) params.append('riskLevel', riskLevel);
  if (autonomyLevel) params.append('autonomyLevel', autonomyLevel);
  if (onProbation !== undefined) params.append('onProbation', onProbation);
  const qs = params.toString();
  return apiFetch(`/api/v1/trust/profiles${qs ? `?${qs}` : ''}`);
};

export const getTrustProfile = (agentId) => apiFetch(`/api/v1/trust/profiles/${agentId}`);

export const getTrustProfilesToWatch = ({ orgId, tenantId } = {}) => {
  const params = new URLSearchParams();
  if (isUUID(orgId)) params.append('orgId', orgId);
  if (isUUID(tenantId)) params.append('tenantId', tenantId);
  const qs = params.toString();
  return apiFetch(`/api/v1/trust/profiles/watch${qs ? `?${qs}` : ''}`);
};

export const recalculateTrustProfile = (agentId) => apiFetch(`/api/v1/trust/profiles/${agentId}/recalculate`, 'POST');

export const triggerProbation = (agentId, reason, expiresAt) =>
  apiFetch(`/api/v1/trust/profiles/${agentId}/probation`, 'POST', { reason, expiresAt });

export const releaseProbation = (agentId, reason = 'Score recovered to acceptable level') =>
  apiFetch(`/api/v1/trust/profiles/${agentId}/probation`, 'DELETE', { reason });

export const manualAutonomyOverride = (agentId, newLevel, reason) =>
  apiFetch(`/api/v1/trust/profiles/${agentId}/autonomy`, 'PUT', { newLevel, reason });

export const getAutonomyHistory = (agentId, page = 0, size = 50) =>
  apiFetch(`/api/v1/trust/profiles/${agentId}/history?page=${page}&size=${size}`);

export const getAgentScoreTrends = (agentId, days = 30) => {
  const since = new Date(Date.now() - days * 86400000).toISOString();
  return apiFetch(`/api/v1/trust/profiles/${agentId}/trends?since=${since}`);
};

export const takeTrustSnapshots = ({ orgId, tenantId } = {}) => {
  const params = new URLSearchParams();
  if (isUUID(orgId)) params.append('orgId', orgId);
  if (isUUID(tenantId)) params.append('tenantId', tenantId);
  const qs = params.toString();
  return apiFetch(`/api/v1/trust/snapshots${qs ? `?${qs}` : ''}`, 'POST');
};

/**
 * Score a manifest somebody pasted, with no account and nothing stored.
 *
 * The one call in this file that deliberately carries no credentials. It is sent with plain
 * `fetch` rather than `apiFetch` because this page is reached by people who have never signed in,
 * and the shared helper attaches auth and treats a 401 as a session problem — neither of which
 * applies to an endpoint that has no session to speak of.
 *
 * @param {Object} manifest — already-parsed JSON
 * @returns {Promise<Object|{ok:false,error:string}>}
 */
export const previewTrustScore = async (manifest) => {
  try {
    const resp = await fetch(`${API_URL}/api/v1/trust/scores/preview`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(manifest ?? {}),
    });
    const body = await resp.json().catch(() => null);
    if (!resp.ok) {
      // The endpoint answers a refusal in words meant for the person reading the screen —
      // what is missing from the manifest, or that they have scored too many at once — so
      // pass its own message through rather than replacing it with a status code.
      return { ok: false, error: body?.message || `HTTP ${resp.status}` };
    }
    return body;
  } catch (err) {
    return { ok: false, error: err?.message || 'Could not reach the scorer.' };
  }
};

export const getTrustBoardReport = ({ orgId, tenantId, dateRange = '30d', scope } = {}) => {
  const params = new URLSearchParams();
  if (isUUID(orgId)) params.append('orgId', orgId);
  if (isUUID(tenantId)) params.append('tenantId', tenantId);
  params.append('dateRange', dateRange);
  // Omitted when it adds nothing: the endpoint already defaults to the whole fleet.
  if (scope && scope !== 'all') params.append('scope', scope);
  const qs = params.toString();
  return apiFetch(`/api/v1/trust/board-report${qs ? `?${qs}` : ''}`);
};

/**
 * Download the board report as a DOCX or PPTX.
 *
 * The scope is sent rather than applied here: the file is rendered server side from the same
 * report the page is showing, so filtering in the browser would hand somebody a pack describing a
 * different fleet from the screen it was exported from.
 *
 * @param {'docx'|'pptx'} format
 * @param {Object} opts — { dateRange, scope }
 * @returns {Promise<{ok: boolean, error?: string}>}
 */
export const downloadTrustBoardReport = async (format, { dateRange = '30d', scope } = {}) => {
  if (format !== 'docx' && format !== 'pptx') {
    return { ok: false, error: `Unsupported format: ${format}` };
  }
  const params = new URLSearchParams();
  params.append('dateRange', dateRange);
  if (scope && scope !== 'all') params.append('scope', scope);
  const url = `${API_URL}/api/v1/trust/board-report.${format}?${params.toString()}`;
  return downloadBinary(url, `agentlets-board-trust-report-${new Date().toISOString().split('T')[0]}.${format}`);
};

/**
 * Download a Trust Score as the one-page PDF a customer can send to a reviewer.
 *
 * A reviewer reaches a score one of two ways: onboarded to the tenant as a Viewer, or handed a
 * file the customer exported. This is the second. The endpoint is tenant-scoped like every other
 * read of a score, so this only ever fetches a score the signed-in caller could already open.
 *
 * @param {Object} target — { scoreId } or { slug }
 * @returns {Promise<{ok: boolean, error?: string}>}
 */
export const downloadTrustScorePdf = async ({ scoreId, slug } = {}) => {
  const path = isUUID(scoreId)
    ? `/api/v1/trust/scores/${scoreId}/export.pdf`
    : slug
      ? `/api/v1/trust/scores/by-slug/${encodeURIComponent(slug)}/export.pdf`
      : null;
  if (!path) return { ok: false, error: 'A score id or address is required.' };
  return downloadBinary(`${API_URL}${path}`, `trust-score-${slug || scoreId}.pdf`);
};

/**
 * Fetch an authenticated binary endpoint and hand the bytes to the browser as a download.
 *
 * Built outside `apiFetch` because that helper parses every body as JSON. Lifted out of
 * downloadTrustBoardReport when a second export needed the identical dance — read the blob, prefer
 * the server's Content-Disposition filename, click a transient anchor, revoke the object URL once
 * the download has had time to start.
 *
 * @param {string} url — absolute, already carrying its query string
 * @param {string} fallbackFilename — used only when the server sent no Content-Disposition
 */
const downloadBinary = async (url, fallbackFilename) => {
  const headers = await getHeaders();
  // Binary response — strip Content-Type from the request so the browser
  // doesn't claim we're sending JSON. Keep the Authorization + tenant
  // headers intact so the same auth that loaded the JSON view succeeds
  // here.
  delete headers['Content-Type'];
  try {
    const resp = await fetch(url, { method: 'GET', headers });
    if (!resp.ok) {
      let detail = `HTTP ${resp.status}`;
      try { detail = (await resp.json())?.error || detail; } catch { /* not JSON */ }
      return { ok: false, error: detail };
    }
    const blob = await resp.blob();
    // Prefer the server's filename if it set Content-Disposition.
    const cd = resp.headers.get('Content-Disposition') || '';
    const m = /filename="?([^"]+)"?/i.exec(cd);
    const filename = m ? m[1] : fallbackFilename;
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    // Defer revoke so the browser has time to start the download.
    setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
    return { ok: true };
  } catch (err) {
    return { ok: false, error: err.message };
  }
};

// ── Trust Scoring Configuration ──────────────────────────────
export const getTrustScoringConfig = (tenantId, scope, agentId) => {
  const params = new URLSearchParams();
  if (isUUID(tenantId)) params.append('tenantId', tenantId);
  if (scope) params.append('scope', scope);
  if (isUUID(agentId)) params.append('agentId', agentId);
  const qs = params.toString();
  return apiFetch(`/api/v1/trust/scoring-config${qs ? `?${qs}` : ''}`);
};

export const listTrustScoringConfigs = (tenantId) => {
  const params = new URLSearchParams();
  if (isUUID(tenantId)) params.append('tenantId', tenantId);
  const qs = params.toString();
  return apiFetch(`/api/v1/trust/scoring-configs${qs ? `?${qs}` : ''}`);
};

export const updateTrustScoringConfig = (config) =>
  apiFetch('/api/v1/trust/scoring-config', 'PUT', config);

// ── AI Model Config (Admin Center "AI Models" tab) ───────────────
// PLATFORM → ORG → TENANT → AGENT override hierarchy, mirroring Trust/Score
// Config. Reads need Super Admin; PLATFORM writes need Platform Admin.
export const getAiModelConfigResolved = (orgId, tenantId, agentId) => {
  const p = new URLSearchParams();
  if (isUUID(orgId)) p.append('orgId', orgId);
  if (isUUID(tenantId)) p.append('tenantId', tenantId);
  if (isUUID(agentId)) p.append('agentId', agentId);
  const qs = p.toString();
  return apiFetch(`/api/v1/admin/ai-model-config/resolved${qs ? `?${qs}` : ''}`);
};
export const getAiModelConfigAtScope = (scope, orgId, tenantId, agentId) => {
  const p = new URLSearchParams();
  p.append('scope', scope);
  if (isUUID(orgId)) p.append('orgId', orgId);
  if (isUUID(tenantId)) p.append('tenantId', tenantId);
  if (isUUID(agentId)) p.append('agentId', agentId);
  return apiFetch(`/api/v1/admin/ai-model-config?${p.toString()}`);
};
// S3a — a tenant's connected LLM connectors projected as managed models
// (dynamic, read-only; the credential stays in the connector).
export const getAiModelConnectorModels = (tenantId) => {
  const p = new URLSearchParams();
  if (isUUID(tenantId)) p.append('tenantId', tenantId);
  const qs = p.toString();
  return apiFetch(`/api/v1/admin/ai-model-config/connector-models${qs ? `?${qs}` : ''}`);
};
export const upsertAiModelConfig = (config) =>
  apiFetch('/api/v1/admin/ai-model-config', 'PUT', config);
export const resetAiModelConfig = (scope, orgId, tenantId, agentId) => {
  const p = new URLSearchParams();
  p.append('scope', scope);
  if (isUUID(orgId)) p.append('orgId', orgId);
  if (isUUID(tenantId)) p.append('tenantId', tenantId);
  if (isUUID(agentId)) p.append('agentId', agentId);
  return apiFetch(`/api/v1/admin/ai-model-config?${p.toString()}`, 'DELETE');
};

// The Crossing — ExteLet conformance golden baseline (reference oracle).
// register: { input, goldenOutput, comparatorMode?, description? }
export const registerConformanceBaseline = (agentId, body) =>
  apiFetch(`/api/v1/agents/${agentId}/conformance/baseline`, 'POST', body);
export const listConformanceBaselines = (agentId) =>
  apiFetch(`/api/v1/agents/${agentId}/conformance/baseline`);
export const deleteConformanceBaseline = (agentId, baselineId) =>
  apiFetch(`/api/v1/agents/${agentId}/conformance/baseline/${baselineId}`, 'DELETE');

// RBAC management Phase 0 — server-authoritative role → level → permissions
// catalog for the role/rights matrix (replaces the stale FE ROLE_PERMS_MAP).
export const getRoleCatalog = () =>
  apiFetch('/api/v1/rbac/roles');

// RBAC management Phase 0 — recent RBAC governance seals (ROLE_GRANTED/REVOKED/
// CHANGED) from the audit chain, so role changes are actually visible.
// UI_DISPLAY R6 — server-side paginated; returns { items, total, page, size }.
export const getRbacAudit = (page = 0, size = 10) =>
  apiFetch(`/api/v1/rbac/audit?page=${page}&size=${size}`);

// RBAC Phase 1b — edit / reset a role's permission set (Platform Admin only;
// backend-enforced). Both return the fresh catalog { roles, permsRev }.
export const updateRolePermissions = (role, permissions) =>
  apiFetch(`/api/v1/rbac/roles/${encodeURIComponent(role)}/permissions`, 'PUT', { permissions });
export const resetRolePermissions = (role) =>
  apiFetch(`/api/v1/rbac/roles/${encodeURIComponent(role)}/reset`, 'POST');

// RBAC Phase 2 — org-scoped custom roles. List/create/update/delete return
// { customRoles, roles (palette), permsRev }.
export const getCustomRoles = () =>
  apiFetch('/api/v1/rbac/roles/custom');
export const createCustomRole = (name, permissions) =>
  apiFetch('/api/v1/rbac/roles/custom', 'POST', { name, permissions });
export const updateCustomRole = (name, permissions) =>
  apiFetch(`/api/v1/rbac/roles/custom/${encodeURIComponent(name)}/permissions`, 'PUT', { permissions });
export const deleteCustomRole = (name) =>
  apiFetch(`/api/v1/rbac/roles/custom/${encodeURIComponent(name)}`, 'DELETE');

// RBAC Phase 3 — JIT / temporary elevation.
export const listElevations = () =>
  apiFetch('/api/v1/rbac/elevations');
export const grantElevation = (subjectUserId, role, expiresAt, reason, orgId, tenantId) =>
  apiFetch('/api/v1/rbac/elevations', 'POST', { subjectUserId, role, expiresAt, reason, orgId, tenantId });
export const revokeElevation = (grantId) =>
  apiFetch(`/api/v1/rbac/elevations/${encodeURIComponent(grantId)}`, 'DELETE');

// RBAC Phase 4 (Access Governance Center) — read-only analytics.
export const getSodConflicts = () =>
  apiFetch('/api/v1/rbac/governance/sod-conflicts');
export const getEffectiveAccess = (userId) =>
  apiFetch(`/api/v1/rbac/governance/effective-access/${encodeURIComponent(userId)}`);

// RBAC Phase 4 Slice B — attestation / recertification campaigns.
export const listAttestations = () =>
  apiFetch('/api/v1/rbac/governance/attestations');
export const createAttestation = (name, scopeType, dueAt) =>
  apiFetch('/api/v1/rbac/governance/attestations', 'POST', { name, scopeType, dueAt });
export const getAttestationItems = (id) =>
  apiFetch(`/api/v1/rbac/governance/attestations/${encodeURIComponent(id)}/items`);
export const decideAttestationItem = (itemId, decision, reason) =>
  apiFetch(`/api/v1/rbac/governance/attestations/items/${encodeURIComponent(itemId)}/decide`, 'POST', { decision, reason });
export const closeAttestation = (id, revokeUncertified) =>
  apiFetch(`/api/v1/rbac/governance/attestations/${encodeURIComponent(id)}/close`, 'POST', { revokeUncertified });

export const deleteTrustScoringConfig = (scope, tenantId, agentId) => {
  const params = new URLSearchParams();
  params.append('scope', scope);
  if (isUUID(tenantId)) params.append('tenantId', tenantId);
  if (isUUID(agentId)) params.append('agentId', agentId);
  return apiFetch(`/api/v1/trust/scoring-config?${params.toString()}`, 'DELETE');
};

// ── Policy Audit Trail ───────────────────────────────────────
export const getPolicyAuditTrail = ({ eventType, decision, agentId, hours = 720, page = 0, size = 100 } = {}) => {
  const params = new URLSearchParams();
  if (eventType) params.append('eventType', eventType);
  if (decision) params.append('decision', decision);
  if (isUUID(agentId)) params.append('agentId', agentId);
  params.append('hours', String(hours));
  params.append('page', String(page));
  params.append('size', String(Math.min(size, 200)));
  return apiFetch(`/api/v1/policy/audit?${params.toString()}`);
};

/**
 * Decision totals across the audit window.
 *
 * Separate from getPolicyAuditTrail because that endpoint returns a PAGE — size clamped
 * to 200 just below — and counting a page is what produced "Total events: 200" on a
 * window holding fourteen thousand. This is one GROUP BY, server side.
 */
/**
 * Re-link one agent's hash chain.
 *
 * Admin-only, scoped server-side to the caller's tenant. Only offer this where every
 * record's own hash verifies — a rebuild recomputes hashes from whatever the rows now
 * say, so running it over an altered record makes the alteration permanent.
 */
export const materializeAgentChain = (agentId) =>
  apiFetch(`/api/v1/policy/audit/agent/${agentId}/materialize-chain`, 'POST');

export const getPolicyAuditSummary = ({ hours = 24, agentId } = {}) => {
  const params = new URLSearchParams();
  params.append('hours', String(hours));
  if (isUUID(agentId)) params.append('agentId', agentId);
  return apiFetch(`/api/v1/policy/audit/summary?${params.toString()}`);
};

export const getWorkflowAuditTrail = (workflowExecutionId) => apiFetch(`/api/v1/policy/audit/workflow/${workflowExecutionId}`);

/**
 * The rules actually holding ONE agent, as the platform resolves them.
 *
 * Agent-scoped and tenant-scoped server-side, gated on `policy.read`. This is
 * the count a protection surface may state: the tenant's data-classification
 * rules (`/dpg/rules`) answer a different question and must never be summed
 * into this one.
 */
export const getEffectiveRules = (agentId) =>
  apiFetch(`/api/v1/policy/effective-rules?agentId=${encodeURIComponent(agentId)}`);

/**
 * Verify the integrity of the policy-audit hash chain.
 *
 * Two call signatures:
 *   verifyAuditIntegrity(720)
 *     — legacy form: trailing-N-hours window from now (default 720h = 30d)
 *   verifyAuditIntegrity({ hours, fromInstant, toInstant, signal })
 *     — modern form: lets callers pass an explicit ISO-8601 time slice and
 *       a cancellation signal. When both fromInstant and toInstant are
 *       supplied the server uses them and ignores hours; this is how the
 *       UI implements batched chain verification at enterprise scale
 *       (G334). The optional AbortSignal is forwarded to apiFetch so a
 *       multi-batch scan can be cancelled mid-flight without leaking
 *       in-flight requests.
 */
export const verifyAuditIntegrity = (arg = 720) => {
  if (typeof arg === 'number') {
    return apiFetch(`/api/v1/policy/audit/verify-integrity?hours=${arg}`, 'POST');
  }
  // P0-12 Day 3 — agentId scopes verification to a single Agentlet's
  // chain (DV-2 Phase B). When absent, the backend walks the full
  // tenant chain as before.
  const { hours = 720, agentId, fromInstant, toInstant, signal } = arg || {};
  const q = new URLSearchParams();
  q.set('hours', String(hours));
  if (agentId && isUUID(agentId)) q.set('agentId', agentId);
  if (fromInstant) q.set('fromInstant', fromInstant);
  if (toInstant)   q.set('toInstant',   toInstant);
  return apiFetch(`/api/v1/policy/audit/verify-integrity?${q.toString()}`, 'POST', null, undefined, signal);
};

/**
 * P0-12 — Download the tamper-evident Audit Certificate as a JSON file.
 *
 * Calls the backend GET /api/v1/policy/audit/certificate endpoint which
 * returns a self-hashed certificate bundling the verifyIntegrity result +
 * event summary + framework-binding rollups for the given time window.
 * The certificate body is the canonical attestation; PDF rendering ships
 * later but the JSON is byte-for-byte replayable today.
 *
 * Two call signatures (matches verifyAuditIntegrity for consistency):
 *
 *   downloadAuditCertificate(720)              // trailing N-hours window
 *   downloadAuditCertificate({                 // explicit window or agent scope
 *     hours: 720,
 *     agentId: 'uuid',                         // optional — scopes to agent chain
 *     fromInstant: '2026-05-01T00:00:00Z',
 *     toInstant:   '2026-05-15T00:00:00Z'
 *   })
 *
 * Behaviour: streams the certificate as a JSON blob, names the file from
 * the server's Content-Disposition header (which includes the certificate
 * id), and triggers download. Returns {ok, certificateId?, certificateHash?,
 * valid?, error?} so callers can surface a success toast or error inline.
 *
 * @returns {Promise<{ok: boolean, certificateId?: string, certificateHash?: string, valid?: boolean, error?: string}>}
 */
/**
 * P0-12 Sprint 2 — Fetch the most-recent external-mirror witness anchor
 * for the calling tenant.
 *
 * <p>Returns a normalised result:</p>
 *
 * <pre>{@code
 *   { ok: true, entry: { id, backend, status, witnessId, witnessUrl,
 *                        witnessResponseHash, headHash, tailHash,
 *                        anchoredAt, failureReason, certificateId } }
 *   { ok: true, entry: null }                       // no anchor yet (204)
 *   { ok: false, error: 'HTTP 5xx' }                // request failure
 * }</pre>
 *
 * <p>The witness anchors the audit chain head + tail hashes to an
 * external append-only store (Sigstore Rekor, customer transparency
 * log) so a determined DB admin cannot rewrite the chain undetected.
 * The Trust Center surfaces the status pill from this call.</p>
 */
export const getLatestAuditWitness = async () => {
  try {
    const resp = await apiFetch('/api/v1/compliance-center/audit/witness/latest');
    // apiFetch may surface either a parsed body OR a {ok, ...} envelope
    // depending on backend version. We accept both shapes — the entry
    // is whichever is non-empty.
    if (resp === null || resp === undefined) {
      return { ok: true, entry: null };
    }
    if (resp?.ok === false) {
      // Treat 204 / no-content as "no anchor yet" rather than an error.
      const msg = String(resp.error || '');
      if (msg.includes('204') || msg.toLowerCase().includes('no content')) {
        return { ok: true, entry: null };
      }
      return { ok: false, error: resp.error };
    }
    // Some endpoints wrap as { data } — unwrap defensively.
    const entry = resp?.data && typeof resp.data === 'object' ? resp.data : resp;
    if (!entry || typeof entry !== 'object' || !entry.id) {
      return { ok: true, entry: null };
    }
    return { ok: true, entry };
  } catch (e) {
    return { ok: false, error: e?.message || String(e) };
  }
};

/**
 * P0-12 Sprint 2 — Fetch the witness anchor history for the calling
 * tenant. Default window: trailing 30 days.
 *
 * @param {object} [opts]
 * @param {string} [opts.fromInstant] ISO-8601
 * @param {string} [opts.toInstant]   ISO-8601
 */
export const getAuditWitnessHistory = async (opts = {}) => {
  const params = new URLSearchParams();
  if (opts?.fromInstant) params.set('from', opts.fromInstant);
  if (opts?.toInstant)   params.set('to',   opts.toInstant);
  const qs = params.toString();
  try {
    const resp = await apiFetch(
      `/api/v1/compliance-center/audit/witness${qs ? `?${qs}` : ''}`);
    if (resp?.ok === false) return { ok: false, error: resp.error };
    const rows = Array.isArray(resp) ? resp
              : Array.isArray(resp?.data) ? resp.data
              : [];
    return { ok: true, rows };
  } catch (e) {
    return { ok: false, error: e?.message || String(e) };
  }
};

export const downloadAuditCertificate = async (arg = 720) => {
  const cfg = typeof arg === 'number' ? { hours: arg } : (arg || {});
  const { hours = 720, agentId, fromInstant, toInstant } = cfg;

  const params = new URLSearchParams();
  params.set('hours', String(hours));
  if (isUUID(agentId)) params.append('agentId', agentId);
  if (fromInstant) params.append('fromInstant', fromInstant);
  if (toInstant)   params.append('toInstant',   toInstant);

  const url = `${API_URL}/api/v1/policy/audit/certificate?${params.toString()}`;
  const headers = await getHeaders();
  // GET binary-ish JSON; strip Content-Type so the browser doesn't send
  // a stray application/json header for a request with no body.
  delete headers['Content-Type'];
  try {
    const resp = await fetch(url, { method: 'GET', headers });
    if (!resp.ok) {
      let detail = `HTTP ${resp.status}`;
      try { detail = (await resp.json())?.error || detail; } catch { /* not JSON */ }
      return { ok: false, error: detail };
    }
    // Read the server-side metadata BEFORE consuming the body — the
    // backend stamps the certificate id + hash + valid status on
    // response headers so the toast can show them without re-parsing
    // the JSON body.
    const certificateId   = resp.headers.get('X-Audit-Certificate-Id') || null;
    const certificateHash = resp.headers.get('X-Audit-Certificate-Hash') || null;
    const validHeader     = resp.headers.get('X-Audit-Certificate-Valid');
    const valid = validHeader === null ? null : validHeader === 'true';

    // P0-1 — Read the body once as text so we can BOTH save it AND
    // peek at the framework binding transitions block. The cost is
    // an extra blob construction; the alternative would be to fetch
    // twice. The body is canonical JSON so JSON.parse is safe.
    const bodyText = await resp.text();
    let bodyJson = null;
    try { bodyJson = JSON.parse(bodyText); } catch { /* not JSON — leave null */ }
    const blob = new Blob([bodyText], { type: 'application/json' });
    const cd = resp.headers.get('Content-Disposition') || '';
    const m = /filename="?([^"]+)"?/i.exec(cd);
    const fallback = `audit-certificate-${new Date().toISOString().split('T')[0]}.json`;
    const filename = m ? m[1] : fallback;
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
    // P0-1 — Surface the new evidence counts so the UI can show
    // "Certificate covers N events + M framework binding transitions
    // across K bindings" without forcing the user to open the JSON.
    const transitionsCount = Array.isArray(bodyJson?.frameworkBindingTransitions)
      ? bodyJson.frameworkBindingTransitions.length : 0;
    const bindingChainsCount = Array.isArray(bodyJson?.bindingChainsSummary)
      ? bodyJson.bindingChainsSummary.length : 0;
    const totalEvents = bodyJson?.summary?.totalEvents ?? null;
    return {
      ok: true,
      certificateId,
      certificateHash,
      valid,
      transitionsCount,
      bindingChainsCount,
      totalEvents,
    };
  } catch (err) {
    return { ok: false, error: err?.message || String(err) };
  }
};

/**
 * G345 — Repair the audit hash chain after the timestamp-precision fix.
 *
 * One-shot remediation: walks the tenant's events in createdAt order
 * and re-stitches previousEventHash + eventHash using the corrected
 * (microsecond-truncated) algorithm. Idempotent. Requires policy.admin
 * permission on the backend. Use after deploying the G345 service fix
 * so the existing chain becomes verifiable again.
 */
export const rebuildAuditChain = ({ hours = 720, fromInstant, toInstant, signal } = {}) => {
  const q = new URLSearchParams();
  q.set('hours', String(hours));
  if (fromInstant) q.set('fromInstant', fromInstant);
  if (toInstant)   q.set('toInstant',   toInstant);
  return apiFetch(`/api/v1/policy/audit/rebuild-chain?${q.toString()}`, 'POST', null, undefined, signal);
};

/**
 * G349 — Verify a single audit event's hash on the server.
 *
 * The browser used to do this in JS with Web Crypto, but JavaScript's
 * Date only carries millisecond precision and the canonical hash input
 * is microsecond-truncated. Round-tripping through this endpoint lets
 * the same algorithm produce both the stored and the recomputed hash,
 * eliminating the precision mismatch.
 *
 * Returns: { eventId, valid, computedHash, storedHash, message? }
 */
export const verifyAuditEventHash = (eventId) =>
  apiFetch(`/api/v1/policy/audit/events/${encodeURIComponent(eventId)}/verify-hash`, 'POST');

// ── Compliance Center API ──────────────────────────────────
export const getComplianceDashboard = () => apiFetch('/api/v1/compliance/dashboard');
export const getComplianceCertificates = (type, page = 0, size = 20) => apiFetch(`/api/v1/compliance/certificates?${type ? `type=${type}&` : ''}page=${page}&size=${size}`);
export const getComplianceCertificateById = (id) => apiFetch(`/api/v1/compliance/certificates/${id}`);
export const generateComplianceCertificate = (workflowRunId, frameworkType) => apiFetch('/api/v1/compliance/certificates/generate', 'POST', { workflowRunId, frameworkType });
export const revokeComplianceCertificate = (id, reason) => apiFetch(`/api/v1/compliance/certificates/${id}/revoke`, 'PUT', { reason });
export const verifyComplianceCertificate = (id) => apiFetch(`/api/v1/compliance/certificates/${id}/verify`, 'POST');
export const getComplianceFrameworks = () => apiFetch('/api/v1/compliance/frameworks');
export const createComplianceFramework = (data) => apiFetch('/api/v1/compliance/frameworks', 'POST', data);
export const updateComplianceFramework = (id, data) => apiFetch(`/api/v1/compliance/frameworks/${id}`, 'PUT', data);
export const deleteComplianceFramework = (id) => apiFetch(`/api/v1/compliance/frameworks/${id}`, 'DELETE');
export const getDataFlowMap = (workflowExecutionId) => apiFetch(`/api/v1/compliance/data-flow/${workflowExecutionId}`);
export const getDataFlowSummary = () => apiFetch('/api/v1/compliance/data-flow/summary');

// ── Governed Execution Layer (GEL) API ───────────────────────

// — Governance Envelopes —
export const gelExecute = (payload) => apiFetch('/api/v1/gel/execute', 'POST', payload);
export const gelListEnvelopes = ({ tenantId, agentId, page = 0, size = 20 } = {}) => {
  const params = new URLSearchParams();
  if (isUUID(tenantId)) params.append('tenantId', tenantId);
  if (isUUID(agentId)) params.append('agentId', agentId);
  params.append('page', String(page));
  params.append('size', String(Math.min(size, 100)));
  return apiFetch(`/api/v1/gel/envelopes?${params.toString()}`);
};
export const gelGetEnvelope = (executionId) => apiFetch(`/api/v1/gel/envelopes/${encodeURIComponent(executionId)}`);
export const gelVerifyHashChain = (agentId) => apiFetch(`/api/v1/gel/envelopes/agent/${agentId}/verify`);
export const gelGetMetrics = (tenantId) => {
  const params = new URLSearchParams();
  if (isUUID(tenantId)) params.append('tenantId', tenantId);
  return apiFetch(`/api/v1/gel/metrics?${params.toString()}`);
};
export const gelGetWorkflowEnvelopes = (workflowExecutionId) => apiFetch(`/api/v1/gel/envelopes/workflow/${workflowExecutionId}`);

// — Platform Connectors —
export const gelCreateConnector = (payload) => apiFetch('/api/v1/gel/connectors', 'POST', payload);
export const gelListConnectors = async (tenantId) => {
  const params = new URLSearchParams();
  if (isUUID(tenantId)) params.append('tenantId', tenantId);
  const res = await apiFetch(`/api/v1/gel/connectors?${params.toString()}`);
  if (!res?.ok) return res;
  return { ...res, data: asConnectorArray(res.data).map(flattenConnectorEnvelope) };
};

export const gelGetConnector = async (id) => {
  const res = await apiFetch(`/api/v1/gel/connectors/${id}`);
  if (!res?.ok || !res.data) return res;
  return { ...res, data: flattenConnectorEnvelope(res.data) };
};
export const gelUpdateConnector = (id, payload) => apiFetch(`/api/v1/gel/connectors/${id}`, 'PUT', payload);
export const gelDeleteConnector = (id) => apiFetch(`/api/v1/gel/connectors/${id}`, 'DELETE');
export const gelTestConnector = (id) => apiFetch(`/api/v1/gel/connectors/${id}/test`, 'POST');
export const gelGetSupportedPlatforms = () => apiFetch('/api/v1/gel/connectors/platforms');

/* V226 / CONN-SCOPE — cross-tenant share grants for Platform Connectors.
 * Mirror of the generic-connector scope endpoints. BE permission gate:
 * Org Admin / Super Admin / Platform Admin only. */
export const gelAssignConnectorTenantScopes = (connectorId, tenantIds) =>
  apiFetch(`/api/v1/gel/connectors/${connectorId}/scope-tenants`, 'POST', { tenantIds: Array.isArray(tenantIds) ? tenantIds : [] });
export const gelRevokeConnectorTenantScope = (connectorId, tenantId) =>
  apiFetch(`/api/v1/gel/connectors/${connectorId}/scope-tenants/${encodeURIComponent(tenantId)}`, 'DELETE');

/* V227 / CONN-SCOPE — per-instance grant variants for Platform Connectors.
 * See assignConnectorTenantGrants / revokeConnectorTenantInstanceScope for
 * the contract — semantics are identical, just the GEL path prefix. */
export const gelAssignConnectorTenantGrants = (connectorId, grants) =>
  apiFetch(`/api/v1/gel/connectors/${connectorId}/scope-tenants`, 'POST', { grants: Array.isArray(grants) ? grants : [] });
export const gelRevokeConnectorTenantInstanceScope = (connectorId, tenantId, instanceId) =>
  apiFetch(`/api/v1/gel/connectors/${connectorId}/scope-tenants/${encodeURIComponent(tenantId)}/instances/${encodeURIComponent(instanceId)}`, 'DELETE');

// — External Agent Registrations —
export const gelRegisterExternalAgent = (payload) => apiFetch('/api/v1/gel/registrations', 'POST', payload);

/* EXTELET-V3 · Phase 2-5 — Atomic onboarding endpoint used by the V3 wizard.
 * Posts a single OnboardRequest containing registration fields + integrationPath
 * (CODED or MCP) and receives back the registration row, all minted keys
 * (Gateway + SDK or MCP, plaintext shown ONCE), and ready-to-paste
 * Python / Java / MCP snippets. The legacy gelRegisterExternalAgent above is
 * preserved untouched so the V1 form on /gel-agents keeps working. */
export const gelOnboardExteLet = (payload) => apiFetch('/api/v1/gel/registrations/onboard', 'POST', payload);

export const gelListRegistrations = (tenantId) => {
  const params = new URLSearchParams();
  if (isUUID(tenantId)) params.append('tenantId', tenantId);
  return apiFetch(`/api/v1/gel/registrations?${params.toString()}`);
};
export const gelGetRegistration = (id) => apiFetch(`/api/v1/gel/registrations/${id}`);
export const gelGetRegistrationByAgent = (agentId) => apiFetch(`/api/v1/gel/registrations/agent/${agentId}`);
export const gelVerifyRegistration = (id) => apiFetch(`/api/v1/gel/registrations/${id}/verify`, 'PUT');
/* R2.3.a — Live Verify probe. Hits a real HTTP endpoint on the bound
 * connector's healthCheckUrl and flips status to VERIFIED only on 2xx.
 * Response envelope (under apiFetch.data): {statusCode, latencyMs,
 * snippet, verified, probedUrl}. gelVerifyRegistration remains as the
 * "Acknowledge" fallback for code-discovered ExteLets with no live
 * endpoint to probe. */
export const gelProbeRegistration = (id) => apiFetch(`/api/v1/gel/registrations/${id}/probe`, 'POST');
export const gelSuspendRegistration = (id, reason) => apiFetch(`/api/v1/gel/registrations/${id}/suspend`, 'PUT', { reason });
export const gelRevokeRegistration = (id) => apiFetch(`/api/v1/gel/registrations/${id}/revoke`, 'PUT');
export const gelUpdateRegistrationConfig = (id, payload) => apiFetch(`/api/v1/gel/registrations/${id}/config`, 'PUT', payload);
export const gelListRegistrationsByConnector = (connectorId) => apiFetch(`/api/v1/gel/registrations/connector/${connectorId}`);

// ── B-D-A Lifecycle ──────────────────────────────────────────────────
export const gelRecordIntegrationTest = (id, testName, status, details) =>
    apiFetch(`/api/v1/gel/registrations/${id}/lifecycle/test`, 'POST', { testName, status, details });
export const gelUpdateSkillMappings = (id, skillMappings) =>
    apiFetch(`/api/v1/gel/registrations/${id}/skill-mappings`, 'PUT', skillMappings);
export const gelDiscoverCapabilities = (id) =>
    apiFetch(`/api/v1/gel/registrations/${id}/discover-capabilities`, 'POST');
/* P7J-RUN-036 — ExteLet integration tests · live probe.
 *
 * The MANUAL record path is already served by `gelTestRegistration`
 * (POST /api/v1/gel/registrations/{id}/lifecycle/test) — no duplication
 * here. The NEW capability is the live probe: hit the registered
 * endpoint with a real HTTP exchange, capture status + latency +
 * truncated body, append the row automatically. 2xx → PASSED. Both
 * paths write to the same integration_test_results JSONB column, which
 * is what the BUILD-phase `at_least_one_test_passed` gate counts. */
export const gelRunIntegrationProbe = (id) =>
    apiFetch(`/api/v1/gel/registrations/${id}/integration-tests/probe`, 'POST');
export const gelAdvanceToDecide = (id) =>
    apiFetch(`/api/v1/gel/registrations/${id}/lifecycle/advance-decide`, 'PUT');
export const gelAttachGovernancePolicies = (id, policies) =>
    apiFetch(`/api/v1/gel/registrations/${id}/lifecycle/policies`, 'PUT', policies);
export const gelAdvanceToAct = (id) =>
    apiFetch(`/api/v1/gel/registrations/${id}/lifecycle/advance-act`, 'PUT');
// W5 (Prove 6) — Execution Ready (D-2 replaces Publish for ExteLets).
export const gelGetExecutionReadiness = (id) =>
    apiFetch(`/api/v1/gel/registrations/${id}/execution-readiness`);
export const gelMarkExecutionReady = (id) =>
    apiFetch(`/api/v1/gel/registrations/${id}/lifecycle/execution-ready`, 'PUT');

// ─── GEL Policy Bridge ──────────────────────────────────────────────
export const gelMaterializeProxySkills = (id) =>
    apiFetch(`/api/v1/gel/registrations/${id}/policy-bridge/materialize-skills`, 'POST');
/**
 * Keep one answer with the agent, replacing any answer that step already had.
 *
 * Sends the rendered node — the line the trail already shows — and never the step's raw value.
 * A step's value is whatever that step collected, and the credentials step's value sits one
 * component edit away from carrying key material; the record has nowhere to put it.
 */
export const recordBuildAnswer = (agentId, answer) =>
    apiFetch(`/api/v1/agents/${agentId}/build-answers/${encodeURIComponent(answer.stepKey)}`,
             'PUT', {
               stage: answer.stage ?? null,
               ordinal: answer.ordinal ?? null,
               question: answer.question ?? null,
               label: answer.label ?? null,
               detail: answer.detail ?? null,
             });

/** Every answer an agent holds, in the order the questions were asked. */
export const listBuildAnswers = (agentId) =>
    apiFetch(`/api/v1/agents/${agentId}/build-answers`);

export const gelMaterializePolicies = (id, governancePolicies, policySkillId) =>
    apiFetch(`/api/v1/gel/registrations/${id}/policy-bridge/materialize-policies`
             + (policySkillId ? `?policySkillId=${encodeURIComponent(policySkillId)}` : ''),
             'POST', governancePolicies);
// W1 (D-1) — materialize an ExteLet's Governance Policy Skill into GEL_* rules.
export const gelMaterializeGovernancePolicySkill = (id, policySkillId) =>
    apiFetch(`/api/v1/gel/registrations/${id}/policy-bridge/materialize-skill`, 'POST', { policySkillId });
export const gelActivatePolicies = (id) =>
    apiFetch(`/api/v1/gel/registrations/${id}/policy-bridge/activate`, 'POST');
export const gelValidatePolicyReadiness = (id) =>
    apiFetch(`/api/v1/gel/registrations/${id}/policy-bridge/validate`);
export const gelGetUnifiedEnforcementView = (id) =>
    apiFetch(`/api/v1/gel/registrations/${id}/policy-bridge/unified-view`);

// ═══════════════════════════════════════════════════════════════════
// Governed Gateway — API Key Management
// ═══════════════════════════════════════════════════════════════════

export const gatewayListKeys = (status) => {
  const params = new URLSearchParams();
  if (status) params.append('status', status);
  return apiFetch(`/api/v1/gateway/keys?${params.toString()}`);
};
export const gatewayGetKey = (keyId) => apiFetch(`/api/v1/gateway/keys/${keyId}`);
export const gatewayCreateKey = (payload) => apiFetch('/api/v1/gateway/keys', 'POST', payload);
/* EXTELET-V3 · Phase 7 — list keys scoped to one agent for the
 * Registration tab Credentials card. Plaintext never returned. */
export const gatewayListKeysByAgent = (agentId) => apiFetch(`/api/v1/gateway/keys/by-agent/${agentId}`);
/* Re-show the Python/Java/MCP snippet from the Registration tab.
 * Plaintext SDK / MCP keys are placeholder-templated. */
export const gelRegenerateSnippets = (registrationId) => apiFetch(`/api/v1/gel/registrations/${registrationId}/snippets`);
export const gatewayRotateKey = (keyId, graceHours) => apiFetch(`/api/v1/gateway/keys/${keyId}/rotate`, 'POST', { graceHours });
export const gatewayRevokeKey = (keyId, reason) => apiFetch(`/api/v1/gateway/keys/${keyId}/revoke`, 'POST', { reason });
export const gatewaySuspendKey = (keyId, reason) => apiFetch(`/api/v1/gateway/keys/${keyId}/suspend`, 'POST', { reason });
export const gatewayReactivateKey = (keyId) => apiFetch(`/api/v1/gateway/keys/${keyId}/reactivate`, 'POST');
export const gatewayUpdateKeyScope = (keyId, scopedAgentIds) => apiFetch(`/api/v1/gateway/keys/${keyId}/scope`, 'PUT', { scopedAgentIds });

/* Connected Applications (Identity) — OAuth client applications for the governed MCP surface.
 * client_id is a public identifier (no secret is issued), so it is safe to display and copy. */
export const listOAuthClients = () => apiFetch('/api/v1/oauth-clients');
export const registerOAuthClient = (payload) => apiFetch('/api/v1/oauth-clients', 'POST', payload);
export const updateOAuthClient = (clientId, payload) => apiFetch(`/api/v1/oauth-clients/${clientId}`, 'PUT', payload);
export const revokeOAuthClient = (clientId) => apiFetch(`/api/v1/oauth-clients/${clientId}/revoke`, 'POST');
// Self-service — the applications a signed-in user has allowed to act on their behalf, and withdrawing one.
export const listMyOAuthConsents = () => apiFetch('/api/v1/oauth-consents/me');
export const revokeMyOAuthConsent = (id) => apiFetch(`/api/v1/oauth-consents/${id}`, 'DELETE');
export const reactivateOAuthClient = (clientId) => apiFetch(`/api/v1/oauth-clients/${clientId}/reactivate`, 'POST');

// Federated SSO — Identity → Single sign-on console (identity providers + group/role mappings).
// The client secret is write-only: sent on create/update, never returned.
export const listSsoProviders = () => apiFetch('/api/v1/identity/sso/providers');
export const getSsoProvider = (id) => apiFetch(`/api/v1/identity/sso/providers/${id}`);
export const createSsoProvider = (payload) => apiFetch('/api/v1/identity/sso/providers', 'POST', payload);
export const updateSsoProvider = (id, payload) => apiFetch(`/api/v1/identity/sso/providers/${id}`, 'PUT', payload);
export const enableSsoProvider = (id) => apiFetch(`/api/v1/identity/sso/providers/${id}/enable`, 'POST');
export const disableSsoProvider = (id) => apiFetch(`/api/v1/identity/sso/providers/${id}/disable`, 'POST');
export const deleteSsoProvider = (id) => apiFetch(`/api/v1/identity/sso/providers/${id}`, 'DELETE');
export const listSsoGroupMappings = (id) => apiFetch(`/api/v1/identity/sso/providers/${id}/group-mappings`);
export const addSsoGroupMapping = (id, payload) => apiFetch(`/api/v1/identity/sso/providers/${id}/group-mappings`, 'POST', payload);
export const deleteSsoGroupMapping = (id, mappingId) => apiFetch(`/api/v1/identity/sso/providers/${id}/group-mappings/${mappingId}`, 'DELETE');
export const listSsoRoleMappings = (id) => apiFetch(`/api/v1/identity/sso/providers/${id}/role-mappings`);
export const addSsoRoleMapping = (id, payload) => apiFetch(`/api/v1/identity/sso/providers/${id}/role-mappings`, 'POST', payload);
export const deleteSsoRoleMapping = (id, mappingId) => apiFetch(`/api/v1/identity/sso/providers/${id}/role-mappings/${mappingId}`, 'DELETE');
// Domain ownership — verified domains route sign-in deterministically (globally unique to one provider).
export const listSsoDomains = (id) => apiFetch(`/api/v1/identity/sso/providers/${id}/domains`);
export const claimSsoDomain = (id, domain) => apiFetch(`/api/v1/identity/sso/providers/${id}/domains`, 'POST', { domain });
export const verifySsoDomain = (claimId) => apiFetch(`/api/v1/identity/sso/domains/${claimId}/verify`, 'POST');
export const removeSsoDomain = (claimId) => apiFetch(`/api/v1/identity/sso/domains/${claimId}`, 'DELETE');
// A user's linked external identities (Team → Users slideout — read-only, subject masked server-side).
export const listUserSsoIdentities = (userId) => apiFetch(`/api/v1/identity/sso/users/${userId}/identities`);
// Slice D / D-2 — grantable API scope vocabulary + per-key scope grants.
export const gatewayGetAvailableScopes = () => apiFetch('/api/v1/gateway/keys/available-scopes');
export const gatewayUpdateApiScopes = (keyId, scopes) => apiFetch(`/api/v1/gateway/keys/${keyId}/scopes`, 'PUT', { scopes });
// Per-direction data-protection actions (raise-only vs the agent policy). Each field is an action
// name (Mask/Redact/Tokenize/Block/Log) or null/'' to clear (inherit). See GatewayApiKeyController.
export const gatewayUpdateKeyDpg = (keyId, actions) => apiFetch(`/api/v1/gateway/keys/${keyId}/dpg`, 'PUT', actions);
// Slice G1 — dry-run access check: what verdict would this key get for method+path (no state change).
export const gatewayTestKeyAccess = (keyId, method, path) =>
  apiFetch(`/api/v1/gateway/keys/${keyId}/test`, 'POST', { method, path });
export const gatewayGetKeyAudit = (keyId) => apiFetch(`/api/v1/gateway/keys/${keyId}/audit`);
export const gatewayGetTenantAudit = () => apiFetch('/api/v1/gateway/keys/audit');
// Slice G3 — tenant gateway usage rollup (key counts by status + summed invocation/block/error
// counters + busiest keys) for the API-keys dashboard. Read-only, RBAC gateway.key.read.
export const gatewayGetDashboard = () => apiFetch('/api/v1/gateway/keys/dashboard');
export const gatewayHealth = () => apiFetch('/api/v1/gateway/health');

// ═══════════════════════════════════════════════════════════════════
// Compliance Center V2 — /api/v1/compliance-center/*
// ═══════════════════════════════════════════════════════════════════

// ── Frameworks ──────────────────────────────────────────────────
export const ccListFrameworks = ({ sector, region, level } = {}) => {
  const params = new URLSearchParams();
  if (sector) params.append('sector', sector);
  if (region) params.append('region', region);
  if (level) params.append('level', level);
  const qs = params.toString();
  return apiFetch(`/api/v1/compliance-center/frameworks${qs ? `?${qs}` : ''}`);
};
export const ccGetFramework = (id) => apiFetch(`/api/v1/compliance-center/frameworks/${id}`);
export const ccGetFrameworkControls = (id) => apiFetch(`/api/v1/compliance-center/frameworks/${id}/controls`);
export const ccCreateFramework = (data) => apiFetch('/api/v1/compliance-center/frameworks', 'POST', data);
export const ccEnableFramework = (id) => apiFetch(`/api/v1/compliance-center/frameworks/${id}/enable`, 'PUT');
export const ccDisableFramework = (id) => apiFetch(`/api/v1/compliance-center/frameworks/${id}/disable`, 'PUT');

// ── Configuration ───────────────────────────────────────────────
export const ccListConfigs = ({ orgId, tenantId, agentId } = {}) => {
  const params = new URLSearchParams();
  if (isUUID(orgId)) params.append('orgId', orgId);
  if (isUUID(tenantId)) params.append('tenantId', tenantId);
  if (isUUID(agentId)) params.append('agentId', agentId);
  return apiFetch(`/api/v1/compliance-center/config?${params.toString()}`);
};
export const ccGetEffectiveCompliance = ({ agentId, tenantId, orgId } = {}) => {
  const params = new URLSearchParams();
  if (isUUID(agentId)) params.append('agentId', agentId);
  if (isUUID(tenantId)) params.append('tenantId', tenantId);
  if (isUUID(orgId)) params.append('orgId', orgId);
  return apiFetch(`/api/v1/compliance-center/config/effective?${params.toString()}`);
};
/**
 * Unified coverage projection — joins the three rule-creation paths
 * (Control Config / Policy Skill wizard / Policy Library) into a single
 * truth surface for FrameworkCoveragePanel and ControlConfigPanel.
 *
 * Response shape: see backend CoverageProjection DTO. Key fields:
 *   frameworks[].aggregates.{coveredCount, attestedCount, partialCount, gapCount, combinedCoveragePercent}
 *   frameworks[].controls[].{status, rules[], matchingPatterns[], attestations[]}
 *   frameworks[].primaryBindingStatus  ("PENDING" | "SKILL_CREATED" | "RULES_GENERATED" | "TESTING" | "ACTIVE")
 *   agents[].{pepActive, policySkillId, activeRuleCount, frameworkIds}
 *
 * When agentId is provided, agents[] is empty (single-agent queries skip the rollup).
 * When frameworkId is provided, frameworks[] is filtered to that framework only.
 */
export const ccGetCoverage = ({ agentId, tenantId, frameworkId } = {}) => {
  const params = new URLSearchParams();
  if (isUUID(agentId)) params.append('agentId', agentId);
  if (isUUID(tenantId)) params.append('tenantId', tenantId);
  if (isUUID(frameworkId)) params.append('frameworkId', frameworkId);
  const qs = params.toString();
  return apiFetch(`/api/v1/compliance-center/coverage${qs ? `?${qs}` : ''}`);
};
export const ccAddFrameworkRequirement = (data) => apiFetch('/api/v1/compliance-center/config', 'POST', data);
export const ccUpdateConfig = (id, data) => apiFetch(`/api/v1/compliance-center/config/${id}`, 'PUT', data);
export const ccRemoveConfig = (id) => apiFetch(`/api/v1/compliance-center/config/${id}`, 'DELETE');

// ── Framework Bindings (Binding Provenance Layer) ──────────────
export const ccCreateFrameworkBinding = (data) => apiFetch('/api/v1/compliance-center/bindings', 'POST', data);
export const ccListFrameworkBindings = ({ frameworkId, agentId, orgId } = {}) => {
  const params = new URLSearchParams();
  if (isUUID(frameworkId)) params.append('frameworkId', frameworkId);
  if (isUUID(agentId)) params.append('agentId', agentId);
  if (isUUID(orgId)) params.append('orgId', orgId);
  const qs = params.toString();
  return apiFetch(`/api/v1/compliance-center/bindings${qs ? `?${qs}` : ''}`);
};
export const ccGetFrameworkBinding = (id) => apiFetch(`/api/v1/compliance-center/bindings/${id}`);
export const ccAdvanceFrameworkBinding = (id, data) => apiFetch(`/api/v1/compliance-center/bindings/${id}/advance`, 'PUT', data);
export const ccArchiveFrameworkBinding = (id) => apiFetch(`/api/v1/compliance-center/bindings/${id}`, 'DELETE');
export const ccGetBindingCoverage = (id) => apiFetch(`/api/v1/compliance-center/bindings/${id}/coverage`);
/**
 * P0-1 — Fetch the hash-chained transition history for a framework
 * binding. Returns an array of transition rows ordered oldest → newest;
 * each row carries fromStatus, toStatus, actor, reason, transitionedAt,
 * coverageSnapshot, and the per-binding hash chain (previousTransitionHash
 * + transitionHash) so an auditor can independently verify integrity.
 */
export const ccGetBindingTransitions = (id) =>
  apiFetch(`/api/v1/compliance-center/bindings/${id}/transitions`);

/**
 * P0-1 Sprint 2 — Per-control evidence rollup.
 *
 *   ccGetControlEvidence({ frameworkCode, controlCode, from, to })
 *
 * Returns an array of ControlEvidenceEntry — one row per (binding,
 * control) pair, with the binding's transition history embedded.
 * All filters optional; window defaults to the last 30 days on the
 * backend. Auth: Viewer minimum.
 */
export const ccGetControlEvidence = ({ frameworkCode, controlCode, from, to } = {}) => {
  const q = new URLSearchParams();
  if (frameworkCode) q.set('frameworkCode', frameworkCode);
  if (controlCode)   q.set('controlCode', controlCode);
  if (from)          q.set('from', from);
  if (to)            q.set('to', to);
  const qs = q.toString();
  return apiFetch(`/api/v1/compliance-center/control-evidence${qs ? `?${qs}` : ''}`);
};

/**
 * P0-2 — Upload the .wasm binary for a wasm_skill. The skill row's
 * skillType must already be 'wasm_skill' (set at skill creation).
 *
 *   uploadWasmSkillBlob(agentId, skillId, file, {
 *     entryFunction:        'execute',                  // legacy single
 *     entryFunctions:       ['start', 'stop', 'status'],// Step 3 multi-export
 *     maxMemoryMb:          16,
 *     maxExecutionMs:       5000,
 *     // Step 3 — supply-chain provenance fields. All optional.
 *     artifactUrl:          'oci://ghcr.io/acme/wasm-fleet-controller@sha256:…',
 *     signatureUrl:         'oci://ghcr.io/acme/wasm-fleet-controller@sha256:…sig',
 *     expectedSpiffeIssuer: 'spiffe://acme.example/ci/build-bot',
 *   })
 *
 * Step 3 success response shape:
 *   {
 *     skill: {...the persisted Skill row...},
 *     wasmModuleDigest: '<lowercase 64-char hex>',
 *     wasmEntryFunctions: ['start','stop','status'],
 *     cosignStatus: 'unverified' | 'stub-skipped' | 'verified' | 'failed',
 *     cosignReason: '...',
 *     cosignVerifiedAt: '<ISO ts or "">',
 *   }
 *
 * Returns the response envelope on success, or {ok:false,error} on
 * any non-2xx (including 413 for oversized payloads).
 */
export const uploadWasmSkillBlob = async (agentId, skillId, file, opts = {}) => {
  if (!file) return { ok: false, error: 'file required' };
  if (file.size > 8 * 1024 * 1024) {
    return { ok: false, error: `Wasm module is ${file.size} bytes; cap is 8 MiB.` };
  }
  const params = new URLSearchParams();
  if (opts.entryFunction) params.set('entryFunction', opts.entryFunction);
  if (opts.maxMemoryMb != null) params.set('maxMemoryMb', String(opts.maxMemoryMb));
  if (opts.maxExecutionMs != null) params.set('maxExecutionMs', String(opts.maxExecutionMs));
  /* Step 3 — supply-chain + multi-export fields. The backend
   * accepts entryFunctions as a comma-separated string; we join the
   * array here so the API caller can pass an idiomatic JS array. */
  if (Array.isArray(opts.entryFunctions) && opts.entryFunctions.length > 0) {
    params.set('entryFunctions',
      opts.entryFunctions.map((s) => String(s).trim()).filter(Boolean).join(','));
  }
  if (opts.artifactUrl) params.set('artifactUrl', opts.artifactUrl);
  if (opts.signatureUrl) params.set('signatureUrl', opts.signatureUrl);
  if (opts.expectedSpiffeIssuer) params.set('expectedSpiffeIssuer', opts.expectedSpiffeIssuer);
  const qs = params.toString();
  const url = `${API_URL}/api/v1/agents/${agentId}/skills/${skillId}/wasm-blob${qs ? `?${qs}` : ''}`;
  const headers = await getHeaders();
  // Browser sets the multipart boundary; strip our default Content-Type
  // so it doesn't collide.
  delete headers['Content-Type'];
  const fd = new FormData();
  fd.append('file', file, file.name || 'skill.wasm');
  try {
    const resp = await fetch(url, { method: 'POST', headers, body: fd });
    if (!resp.ok) {
      let detail = `HTTP ${resp.status}`;
      try { detail = (await resp.json())?.error || detail; } catch { /* not JSON */ }
      return { ok: false, error: detail, status: resp.status };
    }
    return await resp.json();
  } catch (err) {
    return { ok: false, error: err?.message || String(err) };
  }
};

/**
 * P0-2 — Execute a wasm_skill end-to-end (RBAC + policy gate + Wasm
 * sandbox). Returns the JSON output the skill produced, or a
 * structured error object on policy block / sandbox trap / timeout.
 */
export const executeWasmSkill = (agentId, skillId, input) =>
  apiFetch(`/api/v1/agents/${agentId}/skills/${skillId}/execute-wasm`, 'POST', input || {});

/**
 * Step 6 (Platform-Eng workflow) — structured skill invocation.
 *
 * <p>Powers the {@code SkillInvocationForm} slideout. Currently
 * supports {@code wasm_skill} only; other skill types respond
 * with HTTP 400 {@code UNSUPPORTED_SKILL_TYPE} and the form
 * surfaces a clear "use the conversational test-run" advisory.</p>
 *
 * <pre>{@code
 * invokeSkill(agentId, skillId, {
 *   export: 'start',          // optional; must be in skill.wasmEntryFunctions
 *   args:   { region: 'us-east-1', dryRun: true },  // optional; defaults to {}
 * })
 *
 * Success response shape:
 *   { output, export, elapsedMs, decision: 'allowed', policyDecisionsRecorded }
 *
 * Blocked response (HTTP 403, returned as {ok:false} via apiFetch envelope):
 *   { blocked: true, decision: 'blocked', reason, kind: 'POLICY_BLOCKED', ... }
 * }</pre>
 *
 * <p>RBAC server-side: {@code skill.execute}.</p>
 */
export const invokeSkill = (agentId, skillId, { export: exportName, args } = {}) =>
  apiFetch(
    `/api/v1/agents/${agentId}/skills/${skillId}/invoke`,
    'POST',
    {
      // The backend record field is named `export` but that's a
      // reserved keyword in ES module syntax; using the property
      // shorthand `export: exportName` above is fine here, but
      // we serialize explicitly so a minifier can't rename it.
      export: exportName || null,
      args: args == null ? {} : args,
    },
  );

/**
 * P0-4 Sprint 1 — Rego (OPA) policy authoring API surface.
 *
 *   getRegoCapability()        → {configured: bool, queryDefault: string}
 *   putRuleRego(ruleId, body)  → updated PolicyRule (or {ok:false,error} on validation fail)
 *   testRego(body)             → {verdict, reason, details?}
 *
 * The frontend should call getRegoCapability() once on mount and hide
 * the Rego authoring UI entirely when configured=false so authors
 * don't see a feature that can't run.
 */
export const getRegoCapability = () =>
  apiFetch('/api/v1/policy/rules/rego/capability');

export const putRuleRego = (ruleId, { regoModule, regoQuery } = {}) =>
  apiFetch(`/api/v1/policy/rules/${ruleId}/rego`, 'PUT', { regoModule, regoQuery });

export const testRego = ({ regoModule, regoQuery, input } = {}) =>
  apiFetch('/api/v1/policy/rules/rego/test', 'POST', { regoModule, regoQuery, input });

/**
 * Step 4 (Platform-Eng workflow) — PolicyRule + binding REST surface.
 *
 * <p>The Rego authoring slideout uses these helpers to drive the two-
 * step create flow: first POST /api/v1/policy/rules to create the
 * rule shell, then PUT /api/v1/policy/rules/{id}/rego to attach the
 * Rego module (which runs the OPA dry-run validation as a side
 * effect). If the operator selected a binding scope (skill_id or
 * agent_id), createPolicyBinding finishes the wiring.</p>
 *
 * <p>RBAC notes (enforced server-side): listPolicyRules requires
 * {@code policy.read}; createPolicyRule and createPolicyBinding
 * both require {@code policy.admin}.</p>
 */
export const listPolicyRules = (params = {}) => {
  const qs = new URLSearchParams();
  if (params.tenantId) qs.set('tenantId', params.tenantId);
  const q = qs.toString();
  return apiFetch(`/api/v1/policy/rules${q ? `?${q}` : ''}`);
};

export const createPolicyRule = (payload) =>
  apiFetch('/api/v1/policy/rules', 'POST', payload);

export const createPolicyBinding = (payload) =>
  apiFetch('/api/v1/policy/bindings', 'POST', payload);

/**
 * Step 7 (Platform-Eng workflow) — OPA decision viewer.
 *
 * <p>The {@code policy_decisions} table has been written into since
 * V35 by {@code PolicyEnforcementGate}, but no UI rendered the rows
 * until this slideout shipped. These two helpers drive the new
 * "Decisions" tab on {@code /policy-config}.</p>
 *
 *   listPolicyDecisions({agentId?, skillId?, userId?, decision?, hours?, page?, size?})
 *     → { items: DecisionSummary[], page, size, totalElements, totalPages }
 *
 *   getPolicyDecision(decisionId)
 *     → DecisionDetail with full contextData (the input bundle the
 *       rule actually evaluated against).
 *
 * <p>Cross-tenant lookups return 404 (never 403) so the existence
 * of decisions in another tenant isn't disclosed. RBAC server-side:
 * {@code policy.read} for both.</p>
 */
export const listPolicyDecisions = (params = {}) => {
  const qs = new URLSearchParams();
  if (params.agentId)  qs.set('agentId',  params.agentId);
  if (params.skillId)  qs.set('skillId',  params.skillId);
  if (params.userId)   qs.set('userId',   params.userId);
  if (params.decision) qs.set('decision', params.decision);
  if (params.hours != null) qs.set('hours', String(params.hours));
  if (params.page  != null) qs.set('page',  String(params.page));
  if (params.size  != null) qs.set('size',  String(params.size));
  const q = qs.toString();
  return apiFetch(`/api/v1/policy/decisions${q ? `?${q}` : ''}`);
};

export const getPolicyDecision = (decisionId) =>
  apiFetch(`/api/v1/policy/decisions/${decisionId}`);

/**
 * P0-3 Sprint 1 — SPIFFE identity API surface.
 *
 *   getSpiffeCapability()      → {configured, trustDomain, platformVersion, sprintReady, sprint2, sprint3}
 *   getAgentSpiffe(agentId)    → {spiffeId, trustDomain, aiSystemMetadata, registrationEntryYaml, ...}
 *   refreshAgentSpiffe(id)     → updated agent identity row
 *   downloadAgentSpiffeYaml(id)→ triggers a YAML download
 *
 * The frontend uses getSpiffeCapability() to decide whether to render
 * the full SPIFFE panel or a 'configure trust domain to enable'
 * placeholder. Same pattern as P0-4 Rego.
 */
export const getSpiffeCapability = () =>
  apiFetch('/api/v1/spiffe/capability');

export const getAgentSpiffe = (agentId) =>
  apiFetch(`/api/v1/agents/${agentId}/spiffe`);

export const refreshAgentSpiffe = (agentId) =>
  apiFetch(`/api/v1/agents/${agentId}/spiffe/refresh`, 'POST', {});

/**
 * P0-3 Sprint 2 — Force a fresh SVID mint via the SPIRE Workload
 * API. The backend endpoint is {@code POST /api/v1/agents/{id}/spiffe/mint}
 * (already exposed by {@code SpiffeController.mintAgentSvid}). On
 * success the response carries the new {@code svidSerial},
 * {@code svidIssuedAt}, and {@code svidExpiresAt} so the panel can
 * refresh its row without re-fetching detail.
 *
 * <p>Degrades to {@code 501 NOT_IMPLEMENTED} when the platform is
 * running on the NoOp Workload API client, and {@code 503 SERVICE_UNAVAILABLE}
 * when SPIRE is configured but unreachable. The frontend treats
 * both as {@code ok:false} via apiFetch's standard error mapping
 * and surfaces the {@code error} string verbatim.</p>
 *
 * <p>Step 2 of the platform-engineer end-to-end workflow — closes
 * the Rotate-now button on {@code SpiffeIdentityPanel}. RBAC:
 * {@code agent.write} (enforced server-side).</p>
 */
export const mintAgentSvid = (agentId) =>
  apiFetch(`/api/v1/agents/${agentId}/spiffe/mint`, 'POST', {});

/**
 * Trigger a YAML download of the SPIRE registration entry for an
 * agent. Uses fetch + blob to honour Content-Disposition.
 */
/**
 * Intent capability probe — tells the FE whether Stage 7 is wired
 * (always true post-Phase-5; kept for forward-compat with future
 * tenant-level intent-disabled toggles).
 *
 * The legacy getAgentIntentProfiles + putAgentIntentProfiles helpers
 * were retired in Phase 5 together with the agents.intent_profiles
 * JSONB column. Use the catalog bindings surface
 * (listAgentIntentBindings + bindAgentToIntentTemplateVersion etc.)
 * for the catalog-driven equivalent.
 */
export const getIntentCapability = () =>
  apiFetch('/api/v1/agents/intent/capability');

/* ──────────────────────────────────────────────────────────────────
 * Intent Profiles Catalog v1.0 — Phase 1E.1 (read-only foundation)
 *
 * Backend surface: IntentTemplateController @ /api/v1/intent-templates
 *
 *   listIntentTemplates()                → IntentTemplate[]   (tenant-visible)
 *   getIntentTemplateById(id)            → IntentTemplate
 *   listIntentTemplateVersions(id)       → IntentTemplateVersion[] (newest first)
 *
 * The inline per-agent surface above (getAgentIntentProfiles /
 * putAgentIntentProfiles) is the LEGACY runtime path and stays live
 * until Phase 5. Catalog endpoints below are the v1.0 future-state
 * surface — Phase 1D's runtime cutover is feature-flagged off by
 * default so these reads are "shadow" data until ops flips the flag.
 *
 * The write-side endpoints (POST/PATCH/DELETE) intentionally aren't
 * wrapped here yet — Phase 1E.2 adds the authoring helpers
 * (createIntentTemplate, editIntentTemplateDraft, submit/approve/reject,
 * deprecate, flagSecurityCritical, archive). Keeping the foundation
 * read-only minimises Phase 1E.1's risk surface.
 * ────────────────────────────────────────────────────────────────── */
export const listIntentTemplates = () =>
  apiFetch('/api/v1/intent-templates');

export const getIntentTemplateById = (templateId) =>
  apiFetch(`/api/v1/intent-templates/${templateId}`);

export const listIntentTemplateVersions = (templateId) =>
  apiFetch(`/api/v1/intent-templates/${templateId}/versions`);

/**
 * Phase 2B-i — flat skill catalog for the matcher editor's skill picker.
 * Returns `[{id, name, agentId, agentName}, ...]` scoped to the caller's
 * role:
 *   - Platform Admin → all skills platform-wide
 *   - Anyone else    → their tenant's skills
 *
 * Used by EditIntentTemplateVersionSlideout's MatcherRow to render the
 * combobox (explicit-skill mode), the type-ahead (skill-name mode), and
 * the "matches N skills" preview (skill-glob mode). Backend excludes
 * soft-deleted agents; Phase 2B-ii (queued into Phase 3) will add
 * reactive narrowing when a Skill row itself is deleted.
 */
export const listIntentMatcherSkillCatalog = () =>
  apiFetch('/api/v1/intent-templates/skill-catalog');

/**
 * Phase 2D — start an IntentTemplateConsolidationWorkflow.
 *
 *   payload = { targetVersionId, sourceVersionIds: [uuid...], reason? }
 *   returns 202 { workflowId, targetVersionId, sourceCount, startedAt, status, pollProgressAt }
 *
 * Workflow is Temporal-backed: the response returns the workflowId
 * immediately and the FE polls progress + waits for terminal state via
 * the workflow's @QueryMethod (queryIntentConsolidationProgress below).
 *
 * Failure modes:
 *   400 — missing required fields
 *   403 — caller lacks compliance.admin / policy.admin
 *   404 — target version not visible
 * Validation failures inside the workflow (target not APPROVED, source
 * = target, etc.) surface as a FAILED workflow status — poll progress
 * to see them.
 */
export const startIntentTemplateConsolidation = (payload) =>
  apiFetch('/api/v1/intent-templates/_consolidate', 'POST', payload);

/**
 * Phase 2D — query a running consolidation workflow's progress.
 *   returns 200 { workflowId, currentPhase, bindingsScanned, bindingsMigrated,
 *                  bindingsSkipped, sourcesProcessed, cancelRequested }
 *   returns 404 if the workflow id is unknown to Temporal (likely it
 *           completed and Temporal's retention dropped the record; FE
 *           should fall back to "completed (status unknown)").
 */
export const queryIntentConsolidationProgress = (workflowId) =>
  apiFetch(`/api/v1/intent-templates/_consolidate/${encodeURIComponent(workflowId)}/progress`);

/**
 * Phase 4 — start the break-glass IntentTemplatePurgeWorkflow.
 *
 *   payload = { ackToken, reason? }
 *   ackToken MUST equal the template's `name` field (case-sensitive).
 *   returns 202 { workflowId, templateId, startedAt, status, pollProgressAt, warning }
 *
 * IRREVERSIBLE. Use archiveIntentTemplate (DELETE) for the reversible
 * archive operation. Purge is Platform Admin only — non-PA callers get
 * 403 from this endpoint.
 *
 * Validation failure (ackToken != template.name) surfaces as a REJECTED
 * workflow status (visible via queryIntentTemplatePurgeProgress).
 */
export const startIntentTemplatePurge = (templateId, ackToken, reason) =>
  apiFetch(`/api/v1/intent-templates/${templateId}/_purge`, 'POST',
    { ackToken, reason: reason || null });

export const queryIntentTemplatePurgeProgress = (workflowId) =>
  apiFetch(`/api/v1/intent-templates/_purge/${encodeURIComponent(workflowId)}/progress`);

/* ── Phase 1E.2 — write surface ──────────────────────────────────────
 * All mutators are gated server-side on compliance.admin OR policy.admin
 * (see IntentTemplateController). The FE permission gate hides the
 * action buttons that drive these calls; the calls themselves still
 * defer authoritative authorization to the backend.
 *
 * Body shapes mirror the controller's record types exactly:
 *   - createIntentTemplateDraft   → IntentTemplateController.CreateDraftRequest
 *   - editIntentTemplateDraft     → IntentTemplateController.EditDraftRequest
 *   - rejectIntentTemplateVersion → IntentTemplateController.RejectRequest
 *   - deprecateIntentTemplateVersion → IntentTemplateController.DeprecateRequest
 *   - flagIntentTemplateVersionCritical → IntentTemplateController.FlagCriticalRequest
 *
 * Etag concurrency (Phase 2A): every mutator accepts an optional `ifMatch`
 * argument. When supplied, it's sent as the RFC 7232 If-Match header; the
 * backend returns 412 Precondition Failed on mismatch and the apiFetch
 * wrapper surfaces that as `{ ok:false, status:412, etagMismatch:true }`.
 * Successful responses carry the freshly-regenerated etag on the returned
 * object (`res.etag`) so callers can thread it into the next mutation
 * without a round-trip GET. Backwards-compatible: callers that omit
 * `ifMatch` get the pre-Phase-2A last-write-wins semantics.
 *
 * `createIntentTemplateDraft` does NOT take ifMatch — the resource doesn't
 * exist yet, so there's nothing to preconditionally match against. The
 * response still carries an etag for the freshly-created template.
 * ────────────────────────────────────────────────────────────────── */
export const createIntentTemplateDraft = (payload) =>
  apiFetch('/api/v1/intent-templates', 'POST', payload);

export const editIntentTemplateDraft = (versionId, patch, ifMatch) =>
  apiFetch(`/api/v1/intent-templates/versions/${versionId}`, 'PATCH', patch,
    undefined, undefined, ifMatch ? { 'If-Match': ifMatch } : null);

// J5 — dry-run authored intent Rego against a sample input (no persistence).
// body: { rego?: string (defaults to the version's saved intent_rego), input?: object }
export const dryRunIntentRego = (versionId, body) =>
  apiFetch(`/api/v1/intent-templates/versions/${versionId}/rego/dry-run`, 'POST', body);

export const submitIntentTemplateVersion = (versionId, ifMatch) =>
  apiFetch(`/api/v1/intent-templates/versions/${versionId}/submit`, 'POST', null,
    undefined, undefined, ifMatch ? { 'If-Match': ifMatch } : null);

export const approveIntentTemplateVersion = (versionId, ifMatch) =>
  apiFetch(`/api/v1/intent-templates/versions/${versionId}/approve`, 'POST', null,
    undefined, undefined, ifMatch ? { 'If-Match': ifMatch } : null);

export const rejectIntentTemplateVersion = (versionId, reason, ifMatch) =>
  apiFetch(`/api/v1/intent-templates/versions/${versionId}/reject`, 'POST', { reason },
    undefined, undefined, ifMatch ? { 'If-Match': ifMatch } : null);

export const deprecateIntentTemplateVersion = (versionId, body, ifMatch) =>
  apiFetch(`/api/v1/intent-templates/versions/${versionId}/deprecate`, 'POST', body,
    undefined, undefined, ifMatch ? { 'If-Match': ifMatch } : null);

export const flagIntentTemplateVersionCritical = (versionId, reason, ifMatch) =>
  apiFetch(`/api/v1/intent-templates/versions/${versionId}/flag-security-critical`, 'POST', { reason },
    undefined, undefined, ifMatch ? { 'If-Match': ifMatch } : null);

export const archiveIntentTemplate = (templateId, ifMatch) =>
  apiFetch(`/api/v1/intent-templates/${templateId}`, 'DELETE', null,
    undefined, undefined, ifMatch ? { 'If-Match': ifMatch } : null);

// PROVE7-050 — create a new DRAFT version (v(N+1)) of an existing template by
// cloning the latest version's content. Backend: POST /{templateId}/versions.
// 409 if archived or an open draft already exists; 412 on etag mismatch.
export const createIntentTemplateVersion = (templateId, ifMatch) =>
  apiFetch(`/api/v1/intent-templates/${templateId}/versions`, 'POST', null,
    undefined, undefined, ifMatch ? { 'If-Match': ifMatch } : null);

/* ── Phase 1E.3 — per-agent intent bindings surface ──────────────────
 * Backend: IntentBindingController @ /api/v1/agents/{agentId}/intent-bindings
 *
 * Bindings join Developer-owned agents to Compliance-authored template
 * versions. Distinct from the legacy inline {@code agents.intent_profiles}
 * JSONB surface (which stays live as the runtime source-of-truth until
 * Phase 1D's feature flag flips). The catalog binding writes go through
 * here; the runtime read still reads inline unless the flag is on.
 *
 * Endpoints:
 *   listAgentIntentBindings(agentId)          → AgentIntentBinding[]   (any state)
 *   listAgentActiveIntentBindings(agentId)    → AgentIntentBinding[]   (ACTIVE only)
 *   bindAgentToIntentTemplateVersion(...)     → AgentIntentBinding     (idempotent)
 *   unbindAgentIntentBinding(...)             → void                   (204)
 *   narrowAgentIntentBinding(...)             → AgentIntentBinding
 *   rebindAgentIntentBinding(...)             → AgentIntentBinding
 *   detectAgentIntentBindingDrift(...)        → { driftDetected, ... }
 *   listBlockingIntentBindingsForPhase(...)   → AgentIntentBinding[]   (phase-gate query)
 *
 * Backend cross-tenant guard: every binding-id-keyed endpoint verifies
 * binding.agent_id === path.agentId. A caller with access to agent A
 * in tenant 1 cannot operate on a bindingId belonging to agent B in
 * tenant 2 (DEF-INTENT-PROFILES-CROSS-TENANT-READ-001 lesson).
 * ────────────────────────────────────────────────────────────────── */
export const listAgentIntentBindings = (agentId) =>
  apiFetch(`/api/v1/agents/${agentId}/intent-bindings`);

export const listAgentActiveIntentBindings = (agentId) =>
  apiFetch(`/api/v1/agents/${agentId}/intent-bindings/active`);

export const bindAgentToIntentTemplateVersion = (agentId, templateVersionId) =>
  apiFetch(`/api/v1/agents/${agentId}/intent-bindings`, 'POST', { templateVersionId });

export const unbindAgentIntentBinding = (agentId, bindingId) =>
  apiFetch(`/api/v1/agents/${agentId}/intent-bindings/${bindingId}`, 'DELETE');

export const narrowAgentIntentBinding = (agentId, bindingId, additionalExcludedSkillIds) =>
  apiFetch(`/api/v1/agents/${agentId}/intent-bindings/${bindingId}/narrow`, 'PATCH', { additionalExcludedSkillIds });

export const rebindAgentIntentBinding = (agentId, bindingId) =>
  apiFetch(`/api/v1/agents/${agentId}/intent-bindings/${bindingId}/rebind`, 'POST');

// Bindings enriched with the source intent's name + version (for legible display).
export const listAgentIntentBindingsEnriched = (agentId) =>
  apiFetch(`/api/v1/agents/${agentId}/intent-bindings/enriched`);

// Authorization coverage — which of the agent's callable connector tools this intent authorizes.
export const getAgentIntentBindingCoverage = (agentId, bindingId) =>
  apiFetch(`/api/v1/agents/${agentId}/intent-bindings/${bindingId}/coverage`);

export const detectAgentIntentBindingDrift = (agentId, bindingId) =>
  apiFetch(`/api/v1/agents/${agentId}/intent-bindings/${bindingId}/detect-drift`, 'POST');

export const listBlockingIntentBindingsForPhase = (agentId, currentPhase) =>
  apiFetch(`/api/v1/agents/${agentId}/intent-bindings/blocking-phase-advance?currentPhase=${encodeURIComponent(currentPhase)}`);

// Phase 2C runtime-source helpers DELETED in V219 (Phase 5). The
// per-agent intent_runtime_source column and its REST surface were
// retired together. The catalog is now the sole runtime source — no
// flip operation exists.

export const downloadAgentSpiffeYaml = async (agentId) => {
  const url = `${API_URL}/api/v1/agents/${agentId}/spiffe/registration.yaml`;
  const headers = await getHeaders();
  delete headers['Content-Type'];
  try {
    const resp = await fetch(url, { method: 'GET', headers });
    if (!resp.ok) {
      let detail = `HTTP ${resp.status}`;
      try { detail = (await resp.json())?.error || detail; } catch { /* not JSON */ }
      return { ok: false, error: detail };
    }
    const text = await resp.text();
    const blob = new Blob([text], { type: 'text/yaml' });
    const cd = resp.headers.get('Content-Disposition') || '';
    const m = /filename="?([^"]+)"?/i.exec(cd);
    const filename = m ? m[1] : `agentlets-spiffe-${agentId.slice(0, 8)}.yaml`;
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = blobUrl; a.download = filename;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
    return { ok: true, filename };
  } catch (err) {
    return { ok: false, error: err?.message || String(err) };
  }
};
export const ccReconcileBinding = (id) => apiFetch(`/api/v1/compliance-center/bindings/${id}/reconcile`, 'POST');
export const ccGetLowCoverageBindings = (threshold = 80) => apiFetch(`/api/v1/compliance-center/bindings/low-coverage?threshold=${threshold}`);

// ── Agent Compliance ────────────────────────────────────────────
export const ccGetAgentSummary = (agentId) => apiFetch(`/api/v1/compliance-center/agents/${agentId}/summary`);
export const ccGetAgentControls = (agentId, frameworkId) => {
  const params = new URLSearchParams();
  if (isUUID(frameworkId)) params.append('frameworkId', frameworkId);
  const qs = params.toString();
  return apiFetch(`/api/v1/compliance-center/agents/${agentId}/controls${qs ? `?${qs}` : ''}`);
};
export const ccRecalculateCompliance = (agentId) => apiFetch(`/api/v1/compliance-center/agents/${agentId}/recalculate`, 'POST');

// ── Dashboard & Heatmap ─────────────────────────────────────────
export const ccGetDashboard = () => apiFetch('/api/v1/compliance-center/dashboard');
export const ccGetHeatmap = (tenantId, agentType) => {
  const params = new URLSearchParams();
  if (isUUID(tenantId)) params.append('tenantId', tenantId);
  if (agentType) params.append('agentType', agentType);
  return apiFetch(`/api/v1/compliance-center/heatmap?${params.toString()}`);
};

// ── Attestations ────────────────────────────────────────────────
export const ccListAttestations = ({ assignedTo, status, agentId } = {}) => {
  const params = new URLSearchParams();
  if (isUUID(assignedTo)) params.append('assignedTo', assignedTo);
  if (status) params.append('status', status);
  if (isUUID(agentId)) params.append('agentId', agentId);
  return apiFetch(`/api/v1/compliance-center/attestations?${params.toString()}`);
};
export const ccGetAttestation = (id) => apiFetch(`/api/v1/compliance-center/attestations/${id}`);
export const ccCreateAttestation = (data) => apiFetch('/api/v1/compliance-center/attestations', 'POST', data);
export const ccSubmitAttestation = (id, data) => apiFetch(`/api/v1/compliance-center/attestations/${id}/submit`, 'POST', data);
export const ccReviewAttestation = (id, data) => apiFetch(`/api/v1/compliance-center/attestations/${id}/review`, 'POST', data);
export const ccGetMyAttestationQueue = () => apiFetch('/api/v1/compliance-center/attestations/my-queue');
export const ccGetReviewQueue = () => apiFetch('/api/v1/compliance-center/attestations/review-queue');
export const ccBulkCreateAttestations = (data) => apiFetch('/api/v1/compliance-center/attestations/bulk-create', 'POST', data);

// ── ComplianceAssist (AI) ───────────────────────────────────────
export const ccAnalyzeControl = (data) => apiFetch('/api/v1/compliance-center/assist/analyze', 'POST', data);
export const ccGetAssistResults = ({ taskId, agentId } = {}) => {
  const params = new URLSearchParams();
  if (isUUID(taskId)) params.append('taskId', taskId);
  if (isUUID(agentId)) params.append('agentId', agentId);
  return apiFetch(`/api/v1/compliance-center/assist/results?${params.toString()}`);
};
export const ccPreFillAttestation = (taskId) => apiFetch(`/api/v1/compliance-center/assist/pre-fill/${taskId}`, 'POST');
export const ccGetAssistMetrics = (orgId) => {
  const params = new URLSearchParams();
  if (isUUID(orgId)) params.append('orgId', orgId);
  return apiFetch(`/api/v1/compliance-center/assist/metrics?${params.toString()}`);
};

// ── Drift Detection ─────────────────────────────────────────────
export const ccGetDriftAlerts = ({ agentId, severity, status } = {}) => {
  const params = new URLSearchParams();
  if (isUUID(agentId)) params.append('agentId', agentId);
  if (severity) params.append('severity', severity);
  if (status) params.append('status', status);
  return apiFetch(`/api/v1/compliance-center/drift/alerts?${params.toString()}`);
};
export const ccUpdateDriftAlert = (id, data) => apiFetch(`/api/v1/compliance-center/drift/alerts/${id}`, 'PUT', data);
export const ccGetBaselines = (agentId) => {
  const params = new URLSearchParams();
  if (isUUID(agentId)) params.append('agentId', agentId);
  return apiFetch(`/api/v1/compliance-center/drift/baselines?${params.toString()}`);
};
export const ccGetDriftDashboard = () => apiFetch('/api/v1/compliance-center/drift/dashboard');

// ── Control Mapping ─────────────────────────────────────────────
export const ccListCanonicalControls = (orgId, tenantId) => {
  const params = new URLSearchParams();
  if (isUUID(orgId)) params.append('orgId', orgId);
  if (isUUID(tenantId)) params.append('tenantId', tenantId);
  return apiFetch(`/api/v1/compliance-center/mappings/canonical?${params.toString()}`);
};
export const ccGetControlMapping = (controlId) => apiFetch(`/api/v1/compliance-center/mappings/control/${controlId}`);
export const ccGetEquivalentControls = (controlId) => apiFetch(`/api/v1/compliance-center/mappings/equivalents/${controlId}`);
export const ccGetEfficiencyReport = (orgId) => {
  const params = new URLSearchParams();
  if (isUUID(orgId)) params.append('orgId', orgId);
  return apiFetch(`/api/v1/compliance-center/mappings/efficiency-report?${params.toString()}`);
};
export const ccVerifyMapping = (mappingId) => apiFetch(`/api/v1/compliance-center/mappings/verify/${mappingId}`, 'POST');
export const ccTriggerMappingScan = (frameworkId) => apiFetch('/api/v1/compliance-center/mappings/trigger-scan', 'POST', { frameworkId });
export const ccApproveMapping = (mappingId) => apiFetch(`/api/v1/compliance-center/mappings/${mappingId}/approve`, 'POST');
export const ccRejectMapping = (mappingId) => apiFetch(`/api/v1/compliance-center/mappings/${mappingId}/reject`, 'POST');
export const ccGetPendingSuggestions = (orgId, tenantId) => {
  const params = new URLSearchParams();
  if (isUUID(orgId)) params.append('orgId', orgId);
  if (isUUID(tenantId)) params.append('tenantId', tenantId);
  return apiFetch(`/api/v1/compliance-center/mappings/suggestions?${params.toString()}`);
};
export const ccGetFrameworkMappingStatuses = (orgId, tenantId) => {
  const params = new URLSearchParams();
  if (isUUID(orgId)) params.append('orgId', orgId);
  if (isUUID(tenantId)) params.append('tenantId', tenantId);
  return apiFetch(`/api/v1/compliance-center/mappings/framework-statuses?${params.toString()}`);
};

// ── Remediation ─────────────────────────────────────────────────
export const ccListRemediationTasks = ({ assignedTo, status, priority } = {}) => {
  const params = new URLSearchParams();
  if (isUUID(assignedTo)) params.append('assignedTo', assignedTo);
  if (status) params.append('status', status);
  if (priority) params.append('priority', priority);
  return apiFetch(`/api/v1/compliance-center/remediation?${params.toString()}`);
};
export const ccGetRemediationTask = (id) => apiFetch(`/api/v1/compliance-center/remediation/${id}`);
export const ccUpdateRemediationTask = (id, data) => apiFetch(`/api/v1/compliance-center/remediation/${id}`, 'PUT', data);
export const ccResolveRemediation = (id, data) => apiFetch(`/api/v1/compliance-center/remediation/${id}/resolve`, 'POST', data);
export const ccVerifyRemediation = (id) => apiFetch(`/api/v1/compliance-center/remediation/${id}/verify`, 'POST');
export const ccGetSLAMetrics = (orgId) => {
  const params = new URLSearchParams();
  if (isUUID(orgId)) params.append('orgId', orgId);
  return apiFetch(`/api/v1/compliance-center/remediation/metrics?${params.toString()}`);
};

// ── SLA Configuration ──────────────────────────────────────────
export const ccGetSLAConfig = () => apiFetch('/api/v1/compliance-center/remediation/sla-config');
export const ccSaveSLAConfig = (data) => apiFetch('/api/v1/compliance-center/remediation/sla-config', 'PUT', data);
export const ccDeleteSLAConfig = () => apiFetch('/api/v1/compliance-center/remediation/sla-config', 'DELETE');

// ── Regulatory Changes ──────────────────────────────────────────
export const ccListChanges = (frameworkId) => {
  const params = new URLSearchParams();
  if (isUUID(frameworkId)) params.append('frameworkId', frameworkId);
  return apiFetch(`/api/v1/compliance-center/changes?${params.toString()}`);
};
export const ccGetChange = (id) => apiFetch(`/api/v1/compliance-center/changes/${id}`);
export const ccGetChangeGapAnalysis = (id, orgId) => apiFetch(`/api/v1/compliance-center/changes/${id}/gap-analysis?orgId=${orgId}`);
export const ccGenerateMigrationTasks = (id, data) => apiFetch(`/api/v1/compliance-center/changes/${id}/generate-migration-tasks`, 'POST', data);
export const ccGetMigrationStatus = (id, orgId) => apiFetch(`/api/v1/compliance-center/changes/${id}/migration-status?orgId=${orgId}`);

// ── Reports ─────────────────────────────────────────────────────
export const ccGenerateReport = (data) => apiFetch('/api/v1/compliance-center/reports/generate', 'POST', data);
export const ccListReports = ({ type, orgId } = {}) => {
  const params = new URLSearchParams();
  if (type) params.append('type', type);
  if (isUUID(orgId)) params.append('orgId', orgId);
  return apiFetch(`/api/v1/compliance-center/reports?${params.toString()}`);
};
export const ccGetReport = (id) => apiFetch(`/api/v1/compliance-center/reports/${id}`);

// ── Notifications ───────────────────────────────────────────────
export const ccGetNotificationPrefs = () => apiFetch('/api/v1/compliance-center/notifications/preferences');
export const ccUpdateNotificationPrefs = (prefs) => apiFetch('/api/v1/compliance-center/notifications/preferences', 'PUT', prefs);

// ── Evidence Management ─────────────────────────────────────────
export const ccUploadEvidence = (taskId, data) => apiFetch(`/api/v1/compliance-center/attestations/${taskId}/evidence`, 'POST', data);
export const ccGetEvidence = (taskId) => apiFetch(`/api/v1/compliance-center/attestations/${taskId}/evidence`);
export const ccDeleteEvidence = (taskId, evidenceId) => apiFetch(`/api/v1/compliance-center/attestations/${taskId}/evidence/${evidenceId}`, 'DELETE');
export const ccDownloadEvidenceUrl = (taskId, evidenceId) => `/api/v1/compliance-center/attestations/${taskId}/evidence/${evidenceId}/download`;

// ── Attestation Extensions ──────────────────────────────────────
export const ccUpdateAttestation = (id, data) => apiFetch(`/api/v1/compliance-center/attestations/${id}`, 'PUT', data);
export const ccEscalateAttestation = (id, data) => apiFetch(`/api/v1/compliance-center/attestations/${id}/escalate`, 'POST', data);
export const ccAttestControl = (taskId, data) => apiFetch(`/api/v1/compliance-center/attestations/${taskId}/attest-control`, 'POST', data);

// ── AI Extensions ───────────────────────────────────────────────
export const ccScanDocument = (data) => apiFetch('/api/v1/compliance-center/assist/scan-document', 'POST', data);
export const ccSuggestMappings = (data) => apiFetch('/api/v1/compliance-center/assist/suggest-mappings', 'POST', data);

// ── Remediation Extensions ──────────────────────────────────────
export const ccCreateRemediation = (data) => apiFetch('/api/v1/compliance-center/remediation', 'POST', data);
export const ccGetRemediationQueue = () => apiFetch('/api/v1/compliance-center/remediation/queue');
export const ccEscalateRemediation = (id, data) => apiFetch(`/api/v1/compliance-center/remediation/${id}/escalate`, 'POST', data);
export const ccGetEscalationChain = (id) => apiFetch(`/api/v1/compliance-center/remediation/${id}/escalation-chain`);

// ── Escalation Policies ────────────────────────────────────────
export const ccGetEscalationPolicies = (orgId) => apiFetch(`/api/v1/compliance-center/escalation-policies${orgId ? `?orgId=${orgId}` : ''}`);
export const ccGetEscalationPolicy = (id) => apiFetch(`/api/v1/compliance-center/escalation-policies/${id}`);
export const ccCreateEscalationPolicy = (data) => apiFetch('/api/v1/compliance-center/escalation-policies', 'POST', data);
export const ccUpdateEscalationPolicy = (id, data) => apiFetch(`/api/v1/compliance-center/escalation-policies/${id}`, 'PUT', data);
export const ccDeleteEscalationPolicy = (id) => apiFetch(`/api/v1/compliance-center/escalation-policies/${id}`, 'DELETE');
export const ccImportEscalationPolicies = (data) => apiFetch('/api/v1/compliance-center/escalation-policies/import', 'POST', data);
export const ccSyncEscalationPoliciesFromIga = (data) => apiFetch('/api/v1/compliance-center/escalation-policies/iga-sync', 'POST', data);
export const ccTriggerScimSync = (connectorId) => apiFetch(`/api/v1/compliance-center/escalation-policies/scim-sync/${connectorId}`, 'POST');

// ── Report Scheduling ───────────────────────────────────────────
export const ccScheduleReport = (data) => apiFetch('/api/v1/compliance-center/reports/schedule', 'POST', data);
export const ccDeleteScheduledReport = (type, orgId) => apiFetch(`/api/v1/compliance-center/reports/schedule/${type}${orgId ? `?orgId=${orgId}` : ''}`, 'DELETE');
export const ccDownloadReport = (id) => apiFetch(`/api/v1/compliance-center/reports/${id}/download`);
export const ccGetReportWorkflowStatus = (workflowId) => apiFetch(`/api/v1/compliance-center/reports/workflow-status/${workflowId}`);

// ── Notification Extensions ─────────────────────────────────────
export const ccGetNotifications = (unreadOnly = false, limit = 50) => apiFetch(`/api/v1/compliance-center/notifications?unreadOnly=${unreadOnly}&limit=${limit}`);
export const ccMarkNotificationsRead = (notificationIds) => apiFetch('/api/v1/compliance-center/notifications/mark-read', 'PUT', { notificationIds });

// ── Config Extensions ───────────────────────────────────────────
export const ccGetInheritanceChain = (agentId, tenantId, orgId) => {
  const params = new URLSearchParams();
  if (agentId) params.append('agentId', agentId);
  if (tenantId) params.append('tenantId', tenantId);
  if (orgId) params.append('orgId', orgId);
  return apiFetch(`/api/v1/compliance-center/config/inheritance-chain?${params.toString()}`);
};
export const ccExcludeControl = (configId, data) => apiFetch(`/api/v1/compliance-center/config/${configId}/exclude-control`, 'POST', data);
export const ccImportFramework = (data) => apiFetch('/api/v1/compliance-center/frameworks/import', 'POST', data);
export const ccReimportAllFrameworks = () => apiFetch('/api/v1/compliance-center/frameworks/reimport', 'POST');
export const ccReimportSingleFramework = (id) => apiFetch(`/api/v1/compliance-center/frameworks/${id}/reimport`, 'POST');
export const ccGetFrameworkImportStatus = () => apiFetch('/api/v1/compliance-center/frameworks/reimport/status');

// ── Framework Extensions ────────────────────────────────────────
export const ccGetAutomatableControls = (frameworkId) => apiFetch(`/api/v1/compliance-center/frameworks/${frameworkId}/automatable-controls`);

// ── Enforcement Rules Management ───────────────────────────────
export const ccGetFrameworkEnforcementRules = (frameworkId) =>
  apiFetch(`/api/v1/compliance-center/frameworks/${frameworkId}/enforcement-rules`);
export const ccToggleEnforcementRule = (ruleId, enabled) =>
  apiFetch(`/api/v1/compliance-center/enforcement-rules/${ruleId}/toggle?enabled=${enabled}`, 'PATCH');
export const ccBulkToggleEnforcementRules = (ruleIds, enabled) =>
  apiFetch('/api/v1/compliance-center/enforcement-rules/bulk-toggle', 'PATCH', { ruleIds, enabled });

// ── Agent Compliance Extensions ─────────────────────────────────
export const ccGetFailingAgents = (threshold = 80.0) => apiFetch(`/api/v1/compliance-center/agents/failing?threshold=${threshold}`);

// ═══════════════════════════════════════════════════════════════════
// Gap Analysis Fixes — Phase 1-3 API Extensions
// ═══════════════════════════════════════════════════════════════════

// ── G03: Batch Binding ──────────────────────────────────────────
export const ccBatchCreateBindings = (bindingRequests) =>
  apiFetch('/api/v1/compliance-center/bindings/batch', 'POST', { requests: bindingRequests });

// ── G11: Multi-Framework Agent View ─────────────────────────────
export const ccGetAgentFrameworkBindings = (agentId) =>
  apiFetch(`/api/v1/compliance-center/agents/${agentId}/framework-bindings`);

// ── G13: Coverage Export ────────────────────────────────────────
export const ccExportBindingCoverage = (bindingId) =>
  apiFetch(`/api/v1/compliance-center/bindings/${bindingId}/export-coverage`, 'POST');

// ── G01+G02+G15: Agent Pipelines ────────────────────────────────
export const ccListPipelines = ({ orgId, tenantId } = {}) => {
  const params = new URLSearchParams();
  if (isUUID(orgId)) params.append('orgId', orgId);
  if (isUUID(tenantId)) params.append('tenantId', tenantId);
  const qs = params.toString();
  return apiFetch(`/api/v1/compliance-center/pipelines${qs ? `?${qs}` : ''}`);
};
export const ccGetPipeline = (id) => apiFetch(`/api/v1/compliance-center/pipelines/${id}`);
export const ccCreatePipeline = (data) => apiFetch('/api/v1/compliance-center/pipelines', 'POST', data);
export const ccUpdatePipeline = (id, data) => apiFetch(`/api/v1/compliance-center/pipelines/${id}`, 'PUT', data);
export const ccArchivePipeline = (id) => apiFetch(`/api/v1/compliance-center/pipelines/${id}`, 'DELETE');
export const ccRecalculatePipelineScore = (id) =>
  apiFetch(`/api/v1/compliance-center/pipelines/${id}/recalculate`, 'POST');
export const ccGetPipelinesForAgent = (agentId) =>
  apiFetch(`/api/v1/compliance-center/pipelines?agentId=${agentId}`);

// ── G07: Drift Exception Windows ────────────────────────────────
export const ccListDriftExceptionWindows = (tenantId) =>
  apiFetch(`/api/v1/compliance-center/drift/exception-windows${tenantId ? `?tenantId=${tenantId}` : ''}`);
export const ccCreateDriftExceptionWindow = (data) =>
  apiFetch('/api/v1/compliance-center/drift/exception-windows', 'POST', data);
export const ccUpdateDriftExceptionWindow = (id, data) =>
  apiFetch(`/api/v1/compliance-center/drift/exception-windows/${id}`, 'PUT', data);
export const ccDeleteDriftExceptionWindow = (id) =>
  apiFetch(`/api/v1/compliance-center/drift/exception-windows/${id}`, 'DELETE');

// ── G09: Autonomy Ceiling Check ─────────────────────────────────
export const ccValidateAutonomyTransition = (agentId, targetLevel) =>
  apiFetch(`/api/v1/compliance-center/agents/${agentId}/validate-autonomy?targetLevel=${targetLevel}`);

// ── G04: Agent Classification Update ────────────────────────────
export const ccUpdateAgentClassification = (agentId, data) =>
  apiFetch(`/api/v1/compliance-center/agents/${agentId}/classification`, 'PUT', data);

// ═══════════════════════════════════════════════════════════════════
// Shadow Agent Discovery
// ═══════════════════════════════════════════════════════════════════

export const shadowDiscoveryAgents = (params = {}) => {
  const q = new URLSearchParams();
  if (params.status) q.set('status', params.status);
  if (params.riskTier) q.set('riskTier', params.riskTier);
  if (params.page != null) q.set('page', params.page);
  if (params.size) q.set('size', params.size);
  return apiFetch(`/api/v1/discovery/agents?${q}`);
};
export const shadowDiscoveryAgent = (id) => apiFetch(`/api/v1/discovery/agents/${id}`);
export const shadowDiscoveryUpdateStatus = (id, body) => apiFetch(`/api/v1/discovery/agents/${id}/status`, 'PUT', body);
export const shadowDiscoveryEnroll = (id, body) => apiFetch(`/api/v1/discovery/agents/${id}/enroll`, 'POST', body);
export const shadowDiscoveryDismiss = (id, body) => apiFetch(`/api/v1/discovery/agents/${id}/dismiss`, 'POST', body);
export const shadowDiscoveryBulkStatus = (body) => apiFetch('/api/v1/discovery/agents/bulk-status', 'POST', body);
/* Phase A · Agent Discovery — additive bulk endpoints.
 * bulkPromote does the real per-row enrollAgent loop (creates governed
 * Agent rows). bulkDismiss requires reason. engineStatus returns the
 * 7-engine strip data. None of these collide with the existing helpers. */
export const shadowDiscoveryBulkPromote = (body) => apiFetch('/api/v1/discovery/agents/bulk-promote', 'POST', body);
export const shadowDiscoveryBulkDismiss = (body) => apiFetch('/api/v1/discovery/agents/bulk-dismiss', 'POST', body);
export const shadowDiscoveryEngineStatus = () => apiFetch('/api/v1/discovery/engine-status');
/* Bug #86 Phase 1 — per-engine rule readiness. Drives the Discovery hub
 * banner, the V2 rule-status slideout, and the Run-scan button gate.
 * Response shape: { totalEnabledRules, totalSupportedRules,
 *   engines: [{ engineType, label, iconHint, supportedRules: [...],
 *     enabledCount, totalCount }, ...] } */
export const shadowDiscoveryEngineRuleStatus = () => apiFetch('/api/v1/discovery/engine-rule-status');
export const shadowDiscoveryDashboard = () => apiFetch('/api/v1/discovery/dashboard');
export const shadowDiscoveryBaseline = () => apiFetch('/api/v1/discovery/baseline');
export const shadowDiscoveryComputeBaseline = () => apiFetch('/api/v1/discovery/baseline/compute', 'POST');
export const shadowDiscoveryGraph = () => apiFetch('/api/v1/discovery/graph');
export const shadowDiscoveryAgentGraph = (agentId) => apiFetch(`/api/v1/discovery/graph/${agentId}`);
export const shadowDiscoveryScanTrigger = (body) => apiFetch('/api/v1/discovery/scans', 'POST', body);
// Phase H — async scan returns { runId, phase, startedAt, engines } immediately;
// the live progress drawer polls shadowDiscoveryScanRun(runId) until phase is
// COMPLETED or FAILED. Existing sync trigger above is preserved for callers
// that don't want progress tracking (cron, integration tests).
export const shadowDiscoveryScanTriggerAsync = (body) => apiFetch('/api/v1/discovery/scans/async', 'POST', body || {});
export const shadowDiscoveryScanRun = (runId) => apiFetch(`/api/v1/discovery/scans/runs/${runId}`);

/* Phase 3 (task #100) — Temporal signals for pause / resume / destructive stop.
 * Each fires {@code POST /api/v1/discovery/scans/runs/{runId}/{signal}} which
 * the controller binds to a DiscoveryScanWorkflow stub by runId and invokes
 * the matching @SignalMethod. Returns 202 on delivery, 404 if the workflow
 * is no longer in Temporal history (terminal + retention expired). */
export const shadowDiscoveryScanPause  = (runId) => apiFetch(`/api/v1/discovery/scans/runs/${runId}/pause`, 'POST', {});
export const shadowDiscoveryScanResume = (runId) => apiFetch(`/api/v1/discovery/scans/runs/${runId}/resume`, 'POST', {});
export const shadowDiscoveryScanStop   = (runId) => apiFetch(`/api/v1/discovery/scans/runs/${runId}/stop`, 'POST', {});
export const shadowDiscoveryScans = () => apiFetch('/api/v1/discovery/scans');
/* DISC-IMPR-B + C — Per-engine scan catalog: targets + last-scanned-at
 * + runsLast24h. Powers the scan-trigger modal so the operator can pick
 * engines and avoid redundant re-scans. */
export const shadowDiscoveryScanCatalog = () => apiFetch('/api/v1/discovery/scan-catalog');
export const shadowDiscoverySignals = (agentId) => {
  const qs = agentId ? `?agentId=${agentId}` : '';
  return apiFetch(`/api/v1/discovery/signals${qs}`);
};
/* DEF-DISC-SIGNAL-RESOLVE-001 — Send a body so Jackson on the BE
 * doesn't 400 with "Malformed Request Body" on empty PUTs. The BE now
 * also accepts an empty body and defaults resolved=true, but sending
 * the explicit value keeps the wire contract self-describing and
 * preserves the option to call this helper with resolved=false in the
 * future. */
export const shadowDiscoveryResolveSignal = (id, resolved = true) =>
    apiFetch(`/api/v1/discovery/signals/${id}/resolve`, 'PUT', { resolved });
export const shadowDiscoveryRules = (engineType) => apiFetch(`/api/v1/discovery/rules${engineType ? `?engineType=${engineType}` : ''}`);
export const shadowDiscoveryCreateRule = (body) => apiFetch('/api/v1/discovery/rules', 'POST', body);
export const shadowDiscoveryUpdateRule = (id, body) => apiFetch(`/api/v1/discovery/rules/${id}`, 'PUT', body);
export const shadowDiscoveryToggleRule = (id, body) => apiFetch(`/api/v1/discovery/rules/${id}/toggle`, 'PUT', body);
export const shadowDiscoveryDeleteRule = (id) => apiFetch(`/api/v1/discovery/rules/${id}`, 'DELETE');
export const shadowDiscoveryCorrelate = () => apiFetch('/api/v1/discovery/correlate', 'POST');

/* Phase 4 (task #95) — Starter pack installer. POST returns
 * { installed, skipped, total, installedRuleNames }. Idempotent — safe
 * to call again; existing rules are skipped not replaced. */
export const shadowDiscoveryInstallStarterPack = () => apiFetch('/api/v1/discovery/preset/install', 'POST', {});

/* Phase E — Auto-enrollment policies (JSONLogic-based rules that auto-promote
 * NEW DiscoveredAgents to ENROLLED). See SYSTEM_WORKFLOW_RULES.md §2.6 and
 * AutoEnrollmentPolicyController for the backend contract. */
export const listAutoEnrollmentPolicies = () => apiFetch('/api/v1/discovery/auto-enrollment-policies');
export const getAutoEnrollmentPolicy   = (id) => apiFetch(`/api/v1/discovery/auto-enrollment-policies/${id}`);
export const createAutoEnrollmentPolicy = (body) => apiFetch('/api/v1/discovery/auto-enrollment-policies', 'POST', body);
export const updateAutoEnrollmentPolicy = (id, body) => apiFetch(`/api/v1/discovery/auto-enrollment-policies/${id}`, 'PUT', body);
export const deleteAutoEnrollmentPolicy = (id) => apiFetch(`/api/v1/discovery/auto-enrollment-policies/${id}`, 'DELETE');
export const dryRunAutoEnrollmentPolicy = (expression, sampleLimit = 50) =>
  apiFetch('/api/v1/discovery/auto-enrollment-policies/dry-run', 'POST', { expression, sampleLimit });

// ── External Workflow Discovery & Connector Management ──────────────
// Connector configs (polymorphic: Temporal OSS, Conductor, Argo, Step Functions, etc.)
export const discoveryGetConnectorTypes = () => apiFetch('/api/v1/discovery/connectors/types');
export const discoveryCreateConnectorConfig = (payload) => apiFetch('/api/v1/discovery/connectors/configs', 'POST', payload);
export const discoveryUpdateConnectorConfig = (configId, payload) => apiFetch(`/api/v1/discovery/connectors/configs/${configId}`, 'PUT', payload);
export const discoveryListConnectorConfigs = ({ type, status } = {}) => {
  const p = new URLSearchParams();
  if (type) p.append('type', type);
  if (status) p.append('status', status);
  const qs = p.toString();
  return apiFetch(`/api/v1/discovery/connectors/configs${qs ? `?${qs}` : ''}`);
};
export const discoveryTestConnector = (configId) => apiFetch(`/api/v1/discovery/connectors/configs/${configId}/test`, 'POST');
export const discoveryTriggerDiscover = (configId) => apiFetch(`/api/v1/discovery/connectors/configs/${configId}/discover`, 'POST', null, 120000);
// Orphan reconciliation — see backend OrphanReconciler / WorkflowDiscoveryController.
// confirm: tombstones the supplied IDs (status=ORPHANED + 7-day grace clock for non-converted)
// restore: clears the tombstone (used for explicit "I changed my mind" within the grace period)
export const discoveryReconcileConfirm = (configId, orphanIds) =>
  apiFetch(`/api/v1/discovery/connectors/configs/${configId}/reconcile/confirm`, 'POST', { orphanIds });
export const discoveryReconcileRestore = (configId, orphanIds) =>
  apiFetch(`/api/v1/discovery/connectors/configs/${configId}/reconcile/restore`, 'POST', { orphanIds });

// Temporal OSS connections (legacy — kept for backward compat)
export const discoveryCreateTemporalConnection = (payload) => apiFetch('/api/v1/discovery/temporal/connections', 'POST', payload);
export const discoveryListTemporalConnections = ({ status } = {}) => {
  const p = new URLSearchParams();
  if (status) p.append('status', status);
  const qs = p.toString();
  return apiFetch(`/api/v1/discovery/temporal/connections${qs ? `?${qs}` : ''}`);
};
export const discoveryTestTemporalConnection = (id) => apiFetch(`/api/v1/discovery/temporal/connections/${id}/test`, 'POST');
export const discoveryTriggerTemporalDiscover = (id) => apiFetch(`/api/v1/discovery/temporal/connections/${id}/discover`, 'POST', null, 120000);
export const discoveryUpdateTemporalScanConfig = (id, payload) => apiFetch(`/api/v1/discovery/temporal/connections/${id}/config`, 'PUT', payload);
export const discoveryDeleteTemporalConnection = (id) => apiFetch(`/api/v1/discovery/temporal/connections/${id}`, 'DELETE');

// Discovered workflows
export const discoveryListWorkflows = ({ connectionId, status, riskTier, page = 0, size = 50 } = {}) => {
  const p = new URLSearchParams();
  if (connectionId) p.append('connectionId', connectionId);
  if (status) p.append('status', status);
  if (riskTier) p.append('riskTier', riskTier);
  p.append('page', page);
  p.append('size', size);
  return apiFetch(`/api/v1/discovery/workflows?${p.toString()}`);
};
export const discoveryGetWorkflow = (id) => apiFetch(`/api/v1/discovery/workflows/${id}`);
export const discoveryAnalyzeWorkflow = (id) => apiFetch(`/api/v1/discovery/workflows/${id}/analyze`, 'POST', null, 60000);
export const discoveryProtectWorkflow = (id) => apiFetch(`/api/v1/discovery/workflows/${id}/protect`, 'POST');
export const discoveryGetWorkflowSummary = () => apiFetch('/api/v1/discovery/workflows/summary');

// GEL Registration Pipeline
export const discoveryGelRegisterAgent = (discoveredAgentId) => apiFetch(`/api/v1/discovery/gel/register/agent/${discoveredAgentId}`, 'POST');
export const discoveryGelRegisterWorkflow = (discoveredWorkflowId) => apiFetch(`/api/v1/discovery/gel/register/workflow/${discoveredWorkflowId}`, 'POST');
export const discoveryGelAdvancePipeline = (pipelineId, wizardData) => apiFetch(`/api/v1/discovery/gel/pipeline/${pipelineId}/advance`, 'POST', wizardData);
export const discoveryGelActivatePipeline = (pipelineId) => apiFetch(`/api/v1/discovery/gel/pipeline/${pipelineId}/activate`, 'POST');
export const discoveryGelGetPipeline = (pipelineId) => apiFetch(`/api/v1/discovery/gel/pipeline/${pipelineId}`);
export const discoveryGelGetSummary = () => apiFetch('/api/v1/discovery/gel/summary');

// Governance Bindings
export const discoveryListBindings = ({ status, connectionId } = {}) => {
  const p = new URLSearchParams();
  if (status) p.append('status', status);
  if (connectionId) p.append('connectionId', connectionId);
  const qs = p.toString();
  return apiFetch(`/api/v1/discovery/governance/bindings${qs ? `?${qs}` : ''}`);
};
export const discoveryUpdateBindingMode = (bindingId, mode) => apiFetch(`/api/v1/discovery/governance/bindings/${bindingId}/mode`, 'PUT', { enforcementMode: mode });
export const discoveryGetBindingStats = (bindingId) => apiFetch(`/api/v1/discovery/governance/bindings/${bindingId}/stats`);

// External workflow execution proxy
export const discoveryListExternalExecutions = (canvasWorkflowId, limit = 20) => apiFetch(`/api/v1/discovery/connectors/external/${canvasWorkflowId}/executions?limit=${limit}`);
export const discoveryGetExternalExecutionStatus = (canvasWorkflowId) => apiFetch(`/api/v1/discovery/connectors/external/${canvasWorkflowId}/status`);
export const discoveryApproveGatedActivity = (canvasWorkflowId, payload) => apiFetch(`/api/v1/discovery/connectors/external/${canvasWorkflowId}/gel/approve`, 'POST', payload);
export const discoveryRejectGatedActivity = (canvasWorkflowId, payload) => apiFetch(`/api/v1/discovery/connectors/external/${canvasWorkflowId}/gel/reject`, 'POST', payload);

// Start GEL-protected external workflow execution
export const discoveryStartExternalWorkflow = (workflowId, input = {}, executionMode = 'auto') =>
  apiFetch(`/api/v1/discovery/connectors/external/${workflowId}/start?executionMode=${executionMode}`, 'POST', input);

// ── Workflow GEL Audit & Enforcement Dashboard ───────────────────────────
export const discoveryGetAuditDashboard = () => apiFetch('/api/v1/discovery/gel/audit/dashboard');
export const discoveryGetAuditEnvelopes = (bindingId, decision, limit = 50) => {
  const params = new URLSearchParams();
  if (bindingId) params.set('bindingId', bindingId);
  if (decision) params.set('decision', decision);
  params.set('limit', limit);
  return apiFetch(`/api/v1/discovery/gel/audit/envelopes?${params.toString()}`);
};
export const discoveryGetAuditStats = (bindingId) => apiFetch(`/api/v1/discovery/gel/audit/stats/${bindingId}`);
export const discoveryVerifyAuditChain = (bindingId) => apiFetch(`/api/v1/discovery/gel/audit/verify/${bindingId}`);
export const discoveryRepairAuditChain = (bindingId) => apiFetch(`/api/v1/discovery/gel/audit/repair/${bindingId}`, 'POST');
export const discoveryRepairAllAuditChains = () => apiFetch('/api/v1/discovery/gel/audit/repair-all', 'POST');
export const discoveryRematerializeDags = () => apiFetch('/api/v1/discovery/gel/rematerialize-dags', 'POST');

// ── Native Conversion (G466 — wired-up in V2 redesign) ───────────────
// Convert a discovered workflow to a native Canvas Execution Graph (CEG).
// Used by the Workflows tab "Convert to Native" row action / slideout.
// The "preview" variant runs the full converter without persisting — used
// for the conversion-preview slideout so operators can see what the
// converter will produce BEFORE committing it.
//   body: { overrides?: Record<string, any> }  // optional per-node overrides
export const discoveryConvertNativePreview = (workflowId, body = null) =>
  apiFetch(`/api/v1/discovery/workflows/${workflowId}/convert-native/preview`, 'POST', body, 60000);
export const discoveryConvertNative = (workflowId, body = null) =>
  apiFetch(`/api/v1/discovery/workflows/${workflowId}/convert-native`, 'POST', body, 120000);
// Re-convert from the latest source — this is the endpoint that drives
// a fresh conversion when the source workflow has been updated upstream
// (or when the existing Native CEG is broken and you want to regenerate
// from a clean source). Snapshot-versioned: each call creates a new
// workflow version (v37 → v38 → ...).
export const discoveryReconvertNative = (workflowId) =>
  apiFetch(`/api/v1/discovery/workflows/${workflowId}/reconvert-native`, 'POST', null, 120000);
// Conversion report: structured findings from the last conversion
// (task mappings, fallback usage, governance score, validation warnings).
// Used by the workflow detail slideout's "Conversion Report" tab.
export const discoveryGetConversionReport = (workflowId) =>
  apiFetch(`/api/v1/discovery/workflows/${workflowId}/conversion-report`);

// ── Visual Workflow Mapper (3-step pipeline) ────────────────────────
export const discoveryMapperAnalyze = (workflowId) => apiFetch(`/api/v1/discovery/workflows/${workflowId}/mapper/analyze`, 'POST', null, 60000);
export const discoveryMapperMap = (workflowId) => apiFetch(`/api/v1/discovery/workflows/${workflowId}/mapper/map`, 'POST', null, 60000);
export const discoveryMapperMaterialize = (workflowId) => apiFetch(`/api/v1/discovery/workflows/${workflowId}/mapper/materialize`, 'POST', null, 60000);
export const discoveryMapperSourceJson = (workflowId) => apiFetch(`/api/v1/discovery/workflows/${workflowId}/mapper/source-json`);
// Commit body: { name?, description?, canvasDefinition? } — canvasDefinition replaces the materialized Canvas JSON when provided (from Review JSON step).
export const discoveryMapperCommit = (workflowId, body = null) => apiFetch(`/api/v1/discovery/workflows/${workflowId}/mapper/commit`, 'POST', body, 120000);
export const discoveryMapperStatus = (workflowId) => apiFetch(`/api/v1/discovery/workflows/${workflowId}/mapper/status`);
export const discoveryMapperReset = (workflowId) => apiFetch(`/api/v1/discovery/workflows/${workflowId}/mapper/reset`, 'POST');
export const discoveryMapperExecuteWorkflow = (workflowId) => apiFetch(`/api/v1/discovery/workflows/${workflowId}/mapper/execute`, 'POST', null, 120000);
export const discoveryMapperDebugJson = (workflowId) => apiFetch(`/api/v1/discovery/workflows/${workflowId}/mapper/debug-json`);

// ─────────────────────────────────────────────────────────────────────
// Data Protection Gate — Classification Rules (admin)
// ─────────────────────────────────────────────────────────────────────
//
// Backs the "Data Classification Rules" tab in PolicyConfig and the
// "Data Protections" panel on a workflow's detail page. The runtime
// (DataProtectionGate.scan) reads from the same DB rows directly; this
// surface is purely for tenant admins to manage them.
export const listDpgRules = ({ orgId, enabled } = {}) => {
  const qs = new URLSearchParams();
  if (orgId) qs.set('orgId', orgId);
  if (typeof enabled === 'boolean') qs.set('enabled', String(enabled));
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return apiFetch(`/api/v1/dpg/rules${suffix}`);
};
export const getDpgRuleStats = () => apiFetch('/api/v1/dpg/rules/stats');
export const getDpgRule = (id) => apiFetch(`/api/v1/dpg/rules/${id}`);
export const createDpgRule = (payload) => apiFetch('/api/v1/dpg/rules', 'POST', payload);
export const updateDpgRule = (id, payload) => apiFetch(`/api/v1/dpg/rules/${id}`, 'PUT', payload);
export const toggleDpgRule = (id, enabled) => apiFetch(`/api/v1/dpg/rules/${id}/toggle`, 'POST', { enabled });
export const deleteDpgRule = (id) => apiFetch(`/api/v1/dpg/rules/${id}`, 'DELETE');
// Test a regex / dictionary / checksum pattern against sample text — sandbox,
// no save. Returns { method, matched, matches: [{text,start,end}], error }.
export const testDpgPattern = ({ detectionMethod, detectionConfig, sampleText }) =>
  apiFetch('/api/v1/dpg/rules/test', 'POST', { detectionMethod, detectionConfig, sampleText });
// Per-workflow scope: returns the effective rule set + any DPG nodes already
// auto-injected into the workflow's canvas. Powers the WorkflowProtections
// panel on WorkflowDetailPage.
export const getWorkflowDpgRules = (workflowId) => apiFetch(`/api/v1/workflows/${workflowId}/dpg-rules`);

// ─────────────────────────────────────────────────────────────────────
// Per-workflow DPG overrides — the "Customize protections" subsection
// of the WorkflowProtections panel. Read by EXECUTE; write by MANAGE.
// ─────────────────────────────────────────────────────────────────────
export const getWorkflowDpgOverrides = (workflowId) =>
  apiFetch(`/api/v1/workflows/${workflowId}/dpg-overrides`);
export const setWorkflowDpgOverrides = (workflowId, payload) =>
  apiFetch(`/api/v1/workflows/${workflowId}/dpg-overrides`, 'PUT', payload);
export const clearWorkflowDpgOverrides = (workflowId) =>
  apiFetch(`/api/v1/workflows/${workflowId}/dpg-overrides`, 'DELETE');
export const getWorkflowDpgOverrideAudit = (workflowId, page = 0, size = 50) =>
  apiFetch(`/api/v1/workflows/${workflowId}/dpg-override-audit?page=${page}&size=${size}`);
export const getWorkflowDpgOverrideCandidates = (workflowId) =>
  apiFetch(`/api/v1/workflows/${workflowId}/dpg-override-candidates`);

// ─────────────────────────────────────────────────────────────────────
// Per-run enforcement chain (Pre-Gate → DPG → Policy Eval → Decision →
// Execution → Audit → Post-Record). Aggregates policy_audit_events +
// governance_audit_envelopes for a single workflow run.
// ─────────────────────────────────────────────────────────────────────
export const getRunEnforcementChain = (runId) =>
  apiFetch(`/api/v1/workflows/runs/${runId}/enforcement-chain`);

// External (GEL Protected) runs aren't keyed by Agentlets workflow_run.id;
// their identity is the GEL externalRunId string. The backend exposes a
// parallel endpoint that drives the same 7-stage timeline shape directly
// off the governance envelope chain so the canvas / audit page can use a
// single component for both archetypes.
export const getExternalRunEnforcementChain = (externalRunId) =>
  apiFetch(`/api/v1/workflows/external-runs/enforcement-chain?externalRunId=${encodeURIComponent(externalRunId)}`);

// ─────────────────────────────────────────────────────────────────────
// Policy coverage preview — design-time list of rules that would fire
// at a given enforcement point. Used by the canvas Policy Coverage tab.
// ─────────────────────────────────────────────────────────────────────
export const getPolicyCoverage = ({ enforcementPoint = 'WORKFLOW_ENTRY', agentId, tenantId } = {}) => {
  const params = new URLSearchParams();
  if (enforcementPoint) params.set('enforcementPoint', enforcementPoint);
  if (agentId) params.set('agentId', agentId);
  if (tenantId) params.set('tenantId', tenantId);
  return apiFetch(`/api/v1/policy/coverage?${params.toString()}`);
};

// ─────────────────────────────────────────────────────────────────────
// Unified Workflow Audit — backs /workflow-audit page. Returns one row
// per workflow run regardless of source (native or external/GEL), so
// the page can render both archetypes side-by-side.
//
// Filters: source ('native'|'external'|'all'|null), decision
// ('ALLOW'|'WARN'|'BLOCK'|null), workflowId, ISO from/to, limit (max 500).
// ─────────────────────────────────────────────────────────────────────
export const getAuditRuns = ({
  source, decision, workflowId, from, to, limit = 100,
} = {}) => {
  const params = new URLSearchParams();
  if (source) params.set('source', source);
  if (decision) params.set('decision', decision);
  if (workflowId) params.set('workflowId', workflowId);
  if (from) params.set('from', from);
  if (to) params.set('to', to);
  if (limit) params.set('limit', String(limit));
  return apiFetch(`/api/v1/audit/runs?${params.toString()}`);
};

// ─────────────────────────────────────────────────────────────────────
// Workflow Intelligence — analytics over historical runs.
// Powers the /workflow-intelligence page (Performance, Hot-spots, Soak,
// Governance views). Backend lives in WorkflowIntelligenceController.
// ─────────────────────────────────────────────────────────────────────
const _wiQs = ({ workflowId, from, to, bucketSize, topN, slowSampleN } = {}) => {
  const p = new URLSearchParams();
  if (workflowId) p.set('workflowId', workflowId);
  if (from) p.set('from', from);
  if (to) p.set('to', to);
  if (bucketSize) p.set('bucketSize', bucketSize);
  if (topN != null) p.set('topN', String(topN));
  if (slowSampleN != null) p.set('slowSampleN', String(slowSampleN));
  return p.toString();
};

export const getWorkflowIntelligencePerformance = (opts = {}) =>
  apiFetch(`/api/v1/workflow-intelligence/performance?${_wiQs(opts)}`);

export const getWorkflowIntelligenceTimeSeries = (opts = {}) =>
  apiFetch(`/api/v1/workflow-intelligence/time-series?${_wiQs(opts)}`);

export const getWorkflowIntelligenceNodeHotspots = (opts = {}) =>
  apiFetch(`/api/v1/workflow-intelligence/node-hotspots?${_wiQs(opts)}`);

export const getWorkflowIntelligenceGovernance = (opts = {}) =>
  apiFetch(`/api/v1/workflow-intelligence/governance?${_wiQs(opts)}`);

export const getWorkflowIntelligenceWorkflows = (opts = {}) =>
  apiFetch(`/api/v1/workflow-intelligence/workflows?${_wiQs(opts)}`);

// RC-39 — Per-run drill-down for the Fleet Metrics / Workflow
// Intelligence "Runs" sub-tab. Different shape than the analytics
// endpoints above (filter / pagination / sort instead of aggregation),
// so it builds its own querystring rather than reusing _wiQs.
export const getWorkflowIntelligenceRuns = ({
  workflowId, from, to,
  status, durationMinMs, sortBy,
  limit, offset,
} = {}) => {
  const p = new URLSearchParams();
  if (workflowId) p.set('workflowId', workflowId);
  if (from) p.set('from', from);
  if (to) p.set('to', to);
  if (status) p.set('status', status);
  if (durationMinMs != null) p.set('durationMinMs', String(durationMinMs));
  if (sortBy) p.set('sortBy', sortBy);
  if (limit != null) p.set('limit', String(limit));
  if (offset != null) p.set('offset', String(offset));
  return apiFetch(`/api/v1/workflow-intelligence/runs?${p.toString()}`);
};

// AI Analyst — server assembles the briefing from the same dashboards data,
// asks Claude for a structured analysis, and caches by content hash.
// Pass force=true to bypass the cache after a tuning change.
//
// Custom 210s timeout: the backend's `llm.timeout-seconds=180` is the
// per-call wall clock for the Anthropic stream, plus we need headroom for
// briefing assembly (DB reads) and JSON parsing. The default 30s
// REQUEST_TIMEOUT was firing the AbortController before Sonnet finished
// streaming an 8K-token response ("signal is aborted without reason").
const AI_ANALYST_TIMEOUT_MS = 210000;
export const getWorkflowIntelligenceAIAnalysis = (opts = {}) => {
  const p = new URLSearchParams();
  if (opts.workflowId) p.set('workflowId', opts.workflowId);
  if (opts.from)       p.set('from', opts.from);
  if (opts.to)         p.set('to', opts.to);
  if (opts.bucketSize) p.set('bucketSize', opts.bucketSize);
  if (opts.force)      p.set('force', 'true');
  return apiFetch(`/api/v1/workflow-intelligence/ai-analyst?${p.toString()}`,
    'GET', null, AI_ANALYST_TIMEOUT_MS);
};

export const clearWorkflowIntelligenceAICache = () =>
  apiFetch('/api/v1/workflow-intelligence/ai-analyst/clear-cache', 'POST');

// ─────────────────────────────────────────────────────────────────────
// Admin Center — Temporal OSS runtime health, worker config, in-flight
// workflow operations. Read endpoints are gated to Tenant Admin+;
// destructive endpoints (terminate, terminate-wedged) require
// Super / Platform Admin.
// ─────────────────────────────────────────────────────────────────────
export const getTemporalHealth = () =>
  apiFetch('/api/v1/admin/temporal/health');

export const getTemporalWorkerConfig = () =>
  apiFetch('/api/v1/admin/temporal/worker-config');

export const getTemporalInFlight = ({ onlyWedged = false, minAgeMinutes = 30, limit = 100 } = {}) => {
  const p = new URLSearchParams();
  p.set('onlyWedged', String(onlyWedged));
  p.set('minAgeMinutes', String(minAgeMinutes));
  p.set('limit', String(limit));
  return apiFetch(`/api/v1/admin/temporal/in-flight?${p.toString()}`);
};

export const terminateTemporalWorkflow = ({ workflowId, runId, reason } = {}) =>
  apiFetch('/api/v1/admin/temporal/terminate', 'POST', { workflowId, runId, reason });

export const terminateWedgedTemporalWorkflows = ({ minAgeMinutes = 30, limit = 100, reason } = {}) =>
  apiFetch('/api/v1/admin/temporal/terminate-wedged', 'POST', { minAgeMinutes, limit, reason });

// targets: [{ workflowId, runId? }, ...]
export const terminateBulkTemporalWorkflows = ({ targets = [], reason } = {}) =>
  apiFetch('/api/v1/admin/temporal/terminate-bulk', 'POST', { targets, reason });

// Mass-purge — paginates Temporal OSS and terminates every in-flight workflow.
// Backend caps at 50k per call.
export const terminateAllTemporalWorkflows = ({ reason, maxBatch } = {}) =>
  apiFetch('/api/v1/admin/temporal/terminate-all', 'POST', { reason, maxBatch });

// ─────────────────────────────────────────────────────────────────────
// Unified Policy Assignment Pipeline (PolicyAssignmentController)
// Spec: docs/UNIFIED_POLICY_PIPELINE_SPEC.md
//
// 5 stages: Source → Extract → Review → Configure → Activate
//
// The wizard calls each helper in turn. Stage 3 (Review) is purely
// frontend — admin selects from the candidates returned by extract.
// ─────────────────────────────────────────────────────────────────────

// Stage 1 — list a framework's controls split into automatable + manual
export const listFrameworkControlsAsSource = (frameworkId) =>
  apiFetch(`/api/v1/policy/sources/framework-controls?frameworkId=${encodeURIComponent(frameworkId)}`);

// Stage 2 — extract candidates from selected sources
export const extractPolicyCandidates = (body) =>
  apiFetch('/api/v1/policy/extract', 'POST', body);

// Stage 4 — resolve adaptive PEPs for a scope context
export const getApplicablePepSet = (scope) =>
  apiFetch('/api/v1/policy/applicable-pep-set', 'POST', scope);

// Stage 4 — read inherited bindings cascading INTO this scope
export const getInheritedPolicyBindings = ({ orgId, tenantId, agentId } = {}) => {
  const p = new URLSearchParams();
  if (orgId) p.set('orgId', orgId);
  if (tenantId) p.set('tenantId', tenantId);
  if (agentId) p.set('agentId', agentId);
  return apiFetch(`/api/v1/policy/inherited-bindings?${p.toString()}`);
};

// Stage 5 — atomic activation
export const activatePolicyAssignment = (body) =>
  apiFetch('/api/v1/policy/activate', 'POST', body);

// Phase 5 — list activations for the Compliance Center's Auto-Evidenced
// Controls sub-view. Returns the immutable PolicyAssignmentActivation
// snapshots (most recent first) with embedded ruleIds / bindingIds /
// attestationTaskIds JSONB arrays. Filter precedence on the server is
// frameworkBindingId > agentId > tenantId (defaults to caller's tenant).
export const listPolicyActivations = ({ frameworkBindingId, agentId, tenantId, limit } = {}) => {
  const p = new URLSearchParams();
  if (frameworkBindingId) p.set('frameworkBindingId', frameworkBindingId);
  if (agentId) p.set('agentId', agentId);
  if (tenantId) p.set('tenantId', tenantId);
  if (limit) p.set('limit', String(limit));
  const qs = p.toString();
  return apiFetch(`/api/v1/policy/activations${qs ? `?${qs}` : ''}`);
};

// Phase 7 — idempotent get-or-create of the wizard's auto-skill.
// Returns the existing skill when one already exists for the
// (agent, frameworkBindingId) pair; otherwise creates a new one with
// auto_generated=true, skill_kind=AUTO_FRAMEWORK_BINDING. This replaces
// the wizard's previous "always POST /skills" auto-create path.
export const autoCreatePolicySkill = (agentId, body) =>
  apiFetch(`/api/v1/agents/${agentId}/skills/auto-create`, 'POST', body);

// Phase 7 — Managed Skills listing (Admin Center).
// Returns every skill in the platform (or in a tenant) with
// auto_generated=true. Used by the Admin Center's Managed Skills panel.
//
// `skillKind` filters by Skill.skillKind — e.g. 'AUTO_FRAMEWORK_BINDING'
// to scope the panel strictly to wizard-owned policy skills, excluding
// other auto-generated kinds (e.g. AUTO_RUNBOOK_BINDING from Auto Create).
// Pass null/omit to keep legacy unscoped behavior.
export const listManagedSkills = ({ tenantId, skillKind } = {}) => {
  const params = new URLSearchParams();
  if (tenantId)  params.set('tenantId',  tenantId);
  if (skillKind) params.set('skillKind', skillKind);
  const qs = params.toString();
  return apiFetch(`/api/v1/agents/skills/managed${qs ? `?${qs}` : ''}`);
};

// ─────────────────────────────────────────────────────────────────────
// Skill Center — V161 Skill Registry (May 15 launch)
// ─────────────────────────────────────────────────────────────────────
// Powers the global /skills page. Mirrors the Connectors page UX:
// Marketplace (default) + Installed Skills + Wizard-Managed +
// Pending Approval + Drift Watch + Dashboard.
//
// Backend: SkillRegistryController @ /api/v1/skill-registry
// Service: SkillRegistryService (bootstrap projects from manifests)

/**
 * Marketplace tab — paginated list with per-row installed indicator.
 *
 * P7J-RUN-037 — Skill Unification Phase 3. Optional `origin` and
 * `lifecycleState` filters narrow to a single 4-facet bucket. Backward
 * compatible: omitting both behaves identically to the legacy call.
 */
export const listSkillRegistry = ({ page = 0, size = 50, origin = null, lifecycleState = null } = {}) => {
  const params = new URLSearchParams();
  params.set('page', String(page));
  params.set('size', String(size));
  if (origin && String(origin).trim())             params.set('origin', String(origin).trim());
  if (lifecycleState && String(lifecycleState).trim()) params.set('lifecycleState', String(lifecycleState).trim());
  return apiFetch(`/api/v1/skill-registry?${params.toString()}`);
};

/**
 * P7J-RUN-037 — Skill Center facet counts.
 * Returns { origin: {MARKETPLACE:N, …}, lifecycleState: {ACTIVE:N, …} }.
 * Hot path — the Skill Center page calls this on mount.
 */
export const getSkillRegistryFacets = () =>
  apiFetch('/api/v1/skill-registry/facets');

/**
 * P7J-RUN-037 — Skill detail Versions tab.
 * Returns { entryId, versions: [...] } where versions is the full
 * recursive chain (ancestors + descendants) sorted ascending by
 * registry_version. The slideout renders these as a timeline.
 */
export const getSkillRegistryVersions = (id) =>
  apiFetch(`/api/v1/skill-registry/${encodeURIComponent(id)}/versions`);

/**
 * P7J-RUN-037 — Skill detail Consumers tab.
 * Returns { entryId, totalConsumers, byVersion: [{version, count,
 * autoTrackingCount, items:[…]}…] } so the UI can render per-version
 * groupings + cascade-impact summaries.
 */
export const getSkillRegistryConsumers = (id) =>
  apiFetch(`/api/v1/skill-registry/${encodeURIComponent(id)}/consumers`);

/**
 * P7J-RUN-037 — Skill Unification Phase 3d. Mutate a registry entry's
 * lifecycle state: DRAFT → ACTIVE → DEPRECATED → RETIRED (with a single
 * un-deprecate exception). Backend validates the transition + emits an
 * audit event. Pass optional `reason` (stored on the row) and
 * `deprecationPointerEntryId` (for the "use X instead" UX hint).
 */
export const changeSkillRegistryLifecycle = (id, { newState, reason = null, deprecationPointerEntryId = null } = {}) =>
  apiFetch(`/api/v1/skill-registry/${encodeURIComponent(id)}/lifecycle`, 'PUT', {
    newState,
    reason,
    deprecationPointerEntryId,
  });

/** Single skill registry entry — used by the detail modal. */
export const getSkillRegistryEntry = (id) =>
  apiFetch(`/api/v1/skill-registry/${encodeURIComponent(id)}`);

/** Distinct categories — used by the category filter. */
export const listSkillRegistryCategories = () =>
  apiFetch('/api/v1/skill-registry/categories');

/** Semantic search — voyage-3 + textual fallback. */
export const searchSkillRegistry = ({ query, kind, k = 25 } = {}) =>
  apiFetch('/api/v1/skill-registry/search', 'POST', { query, kind, k });

/** Installed Skills tab — granted + verified-active for current tenant. */
export const listInstalledSkills = ({ page = 0, size = 50 } = {}) => {
  const params = new URLSearchParams();
  params.set('page', String(page));
  params.set('size', String(size));
  return apiFetch(`/api/v1/skill-registry/installed?${params.toString()}`);
};

/**
 * Install (grant) a skill to the current tenant. Idempotent.
 *
 * When `agentId` is supplied, the backend install bridge ALSO materialises
 * an AUTO_REGISTRY_INSTALL Skill row on that agent so the skill is
 * immediately invocable from the agent's canvas. When omitted, the install
 * is purely a tenant-scope grant (legacy behaviour).
 *
 * Reference: WORKFLOW_SKILL_RELATIONSHIP_DESIGN.md §5 item 2.
 */
export const installSkill = (id, { restrictions, agentId } = {}) => {
  const body = {};
  if (restrictions) body.restrictions = restrictions;
  if (agentId)      body.agentId      = agentId;
  return apiFetch(
    `/api/v1/skill-registry/${encodeURIComponent(id)}/install`,
    'POST',
    body
  );
};

/** Disable (revoke) a previously installed skill. */
export const disableSkill = (id) =>
  apiFetch(`/api/v1/skill-registry/${encodeURIComponent(id)}/disable`, 'POST', {});

/** Super-admin only — re-runs the bootstrap projection from manifests. */
export const rebuildSkillRegistry = () =>
  apiFetch('/api/v1/skill-registry/rebuild', 'POST', {});

/**
 * Publish a tenant workflow as a Skill Registry entry of kind=workflow_skill.
 * Source workflow must be in status=active. Idempotent — re-publishing
 * updates display name, description, and version stamp.
 *
 * Reference: WORKFLOW_SKILL_RELATIONSHIP_DESIGN.md §5 item 1.
 */
export const publishWorkflowAsSkill = ({ workflowId, displayName, description } = {}) =>
  apiFetch('/api/v1/skill-registry/publish-workflow', 'POST', {
    workflowId,
    displayName: displayName || undefined,
    description: description || undefined,
  });

// ─────────────────────────────────────────────────────────────────────
// Phase-12B — Benchmark runs (Workflow Intelligence Benchmarks tab)
// ─────────────────────────────────────────────────────────────────────
//
// Stress / endurance / soak test runs are persisted as immutable rows
// in the benchmark_runs table. The harness creates a row at start,
// posts periodic snapshots during the run, finalises with aggregate
// metrics on completion. The UI reads these for list/detail/compare.

export const listBenchmarks = () =>
  apiFetch('/api/v1/benchmarks');

export const getBenchmark = (id) =>
  apiFetch(`/api/v1/benchmarks/${id}`);

// Perf-debug 2026-05-02 (#131): server-measured workflow performance for
// this benchmark's wall-clock window. Independent of aggregateMetrics
// (which is whatever the loadtest harness POSTed from the client side).
// Returns the same shape as /api/v1/workflow-intelligence/performance —
// percentiles, taskMetrics, workflowsPerSec, successRate, etc.
// Returns null on 204 (run has no startedAt).
//
// tailSec controls the post-completion buffer: pass 60 (default) for the
// better-aggregated latency view that catches workflows that finished
// just after the harness stopped firing, or 0 for the "firing window
// only" view that matches the harness's own reported request rate
// exactly. Other integer values are accepted and clamped server-side.
export const getBenchmarkServerMetrics = (id, tailSec) => {
  const t = (tailSec === undefined || tailSec === null) ? 60 : Math.max(0, Math.floor(Number(tailSec) || 0));
  return apiFetch(`/api/v1/benchmarks/${id}/server-metrics?tailSec=${t}`);
};

export const compareBenchmarks = (idA, idB) =>
  apiFetch(`/api/v1/benchmarks/compare?a=${encodeURIComponent(idA)}&b=${encodeURIComponent(idB)}`);

// N-way compare — first id is the baseline, deltas reported vs baseline.
// Backend caps at 6 runs per call.
export const compareBenchmarksN = (ids = []) => {
  const qs = ids.map((id) => `ids=${encodeURIComponent(id)}`).join('&');
  return apiFetch(`/api/v1/benchmarks/compare-n?${qs}`);
};

// Export — fetches the binary report and triggers a browser download.
// Uses the project's existing auth header pattern (getHeaders()) since
// apiFetch only handles JSON responses, not binary blobs.
export const exportBenchmark = async (id, format /* 'pdf' | 'xlsx' */) => {
  const headers = await getHeaders();
  const resp = await fetch(
    `${API_URL}/api/v1/benchmarks/${id}/export?format=${encodeURIComponent(format)}`,
    { method: 'GET', headers });
  if (!resp.ok) {
    const text = await resp.text().catch(() => '');
    throw new Error(`Export failed: ${resp.status} ${text.slice(0, 200)}`);
  }
  const blob = await resp.blob();
  const cd = resp.headers.get('Content-Disposition') || '';
  const m = cd.match(/filename="?([^"]+)"?/);
  const filename = (m && m[1]) || `benchmark_${String(id).slice(0, 8)}.${format}`;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
  return { ok: true, filename };
};

// ─── Agent kill switch (W4 / A2) ─────────────────────────────────────
// Reversible runtime halt enforced at the GEL gateway — engaging halts ALL
// governed execution for the agent instantly (GovernedExecutionService
// fail-close), releasing resumes it. Distinct from the permanent identity
// revoke and the inference-only kill switch. Backed by AgentKillSwitchController
// (POST engage/release, GET status). The agent-header Engage/Release buttons in
// AgentDetailPageV2 call these.
export const engageKillSwitch = (agentId, reason) =>
  apiFetch(`/api/v1/agents/${agentId}/kill-switch/engage`, 'POST', { reason });

export const releaseKillSwitch = (agentId) =>
  apiFetch(`/api/v1/agents/${agentId}/kill-switch/release`, 'POST', {});

export const getKillSwitch = (agentId) =>
  apiFetch(`/api/v1/agents/${agentId}/kill-switch`);

// ─── More soft-dep stubs (UI-side runtime guards over namespace import) ──
// Same rationale as engageKillSwitch above: these names are referenced via
// `api.<X>` from V2 pages (AgentDetailPageV2, DiscoveryAgentsPageV2,
// DiscoveryWorkflowsPageV2) under `typeof api.<X> === 'function'` guards
// that intend "call if wired, no-op if not". CRA 5's webpack does static
// analysis on namespace-import property access and fails the build with
// "Attempted import error" when the symbol is absent from the module's
// export list. Each stub satisfies the static check and the runtime
// guard correctly identifies them as functions, so the call goes
// through and resolves to the benign envelope below. When the backend
// endpoints land, replace each stub with the real fetch (same async
// signature) and the UI lights up with no caller changes.

// Persist user-applied skill decisions (autonomy/risk overrides) for an
// AutoLet during Decide phase. Caller: AgentDetailPageV2.jsx:508.
export const saveSkillDecisions = async (agentId, decisions) => {
  // eslint-disable-next-line no-console
  console.warn('[api] saveSkillDecisions called but backend endpoint is not implemented yet',
    { agentId, decisionsCount: Array.isArray(decisions) ? decisions.length : 0 });
  return { ok: false, error: 'skill-decisions endpoint not implemented yet', data: null };
};

// Surface "shadow agent" discovery (agents observed but not yet
// registered). Two callers; the second is a back-compat fallback.
// Caller: DiscoveryAgentsPageV2.jsx:44.
export const listShadowAgents = async () => {
  // eslint-disable-next-line no-console
  console.warn('[api] listShadowAgents called but backend endpoint is not implemented yet');
  return { ok: false, error: 'shadow-agents endpoint not implemented yet', data: [] };
};

// Caller: DiscoveryAgentsPageV2.jsx:45 (back-compat fallback for
// listShadowAgents on older API revisions).
export const getShadowDiscovery = async () => {
  // eslint-disable-next-line no-console
  console.warn('[api] getShadowDiscovery called but backend endpoint is not implemented yet');
  return { ok: false, error: 'shadow-discovery endpoint not implemented yet', data: [] };
};

// Surface workflow-discovery results (workflows observed in agentlets
// but not yet imported). Caller: DiscoveryWorkflowsPageV2.jsx:40.
export const listDiscoveredWorkflows = async () => {
  // eslint-disable-next-line no-console
  console.warn('[api] listDiscoveredWorkflows called but backend endpoint is not implemented yet');
  return { ok: false, error: 'discovered-workflows endpoint not implemented yet', data: [] };
};

// Caller: DiscoveryWorkflowsPageV2.jsx:41 (back-compat fallback for
// listDiscoveredWorkflows on older API revisions).
export const getWorkflowDiscovery = async () => {
  // eslint-disable-next-line no-console
  console.warn('[api] getWorkflowDiscovery called but backend endpoint is not implemented yet');
  return { ok: false, error: 'workflow-discovery endpoint not implemented yet', data: [] };
};

// ─────────────────────────────────────────────────────────────────────
// P0-IDENT — Identity plane (Phases 6F + 6G)
// ─────────────────────────────────────────────────────────────────────
// Backed by IdentityScopeController @ /api/v1/identity/*. Five read-only
// endpoints power the three Identity pages (Organization, Tenants list,
// Tenant detail, Agents list, Agent detail) — all gated by the
// identity.read permission which Tenant Admin and above carry. Returns
// resolved Org-default + per-scope override payloads ready for direct
// rendering; the frontend does no client-side inheritance resolution
// (that lives server-side in the controller / future repository).
export const getIdentityOrganization = () =>
  apiFetch('/api/v1/identity/organization');

export const listIdentityTenants = () =>
  apiFetch('/api/v1/identity/tenants');

export const getIdentityTenant = (tenantId) =>
  apiFetch(`/api/v1/identity/tenants/${encodeURIComponent(tenantId)}`);

export const listIdentityAgents = () =>
  apiFetch('/api/v1/identity/agents');

export const getIdentityAgent = (agentId) =>
  apiFetch(`/api/v1/identity/agents/${encodeURIComponent(agentId)}`);

// Identity redesign ID-0/ID-1 — honest Fleet state counts. Returns
// { mode: 'preview'|'wired', total, byState: { <STATE>: n, ... }, orgScoped }
// straight from agents.identity_state (V266). Replaces the fabricated
// landing-page KPI fixtures.
export const getIdentityFleet = () =>
  apiFetch('/api/v1/identity/fleet');

// Identity redesign ID-3 — Agent Identity lifecycle. GET returns the agent's
// posture (state, credential, verification, timeline); the action POSTs drive
// the state machine and return the updated LifecycleView.
export const getAgentIdentityLifecycle = (agentId) =>
  apiFetch(`/api/v1/identity/agents/${encodeURIComponent(agentId)}/lifecycle`);

// action ∈ assign | issue | rotate | suspend | resume | verify | revoke
export const agentIdentityAction = (agentId, action, body) =>
  apiFetch(`/api/v1/identity/agents/${encodeURIComponent(agentId)}/${action}`, 'POST', body);

// ---------------------------------------------------------------------------
// ID-7 — Platform Runtime Flags (Platform-Admin-only). Powers the server-backed
// toggles for cluster-wide backend Runtime flags in /flags. Writes are persisted
// and applied on the next application restart.
// ---------------------------------------------------------------------------
// G-2 / G-4 — Trust Graph subject view: every TrustAssertion sealed for an agent
// across the 7 proves (Discovery, Identity, Intent, RBAC, Trust, Enforcement, Audit),
// projected from the sealed assertion stream. Powers the per-agent Trust Graph viewer.
export const getTrustGraphSubject = (agentId, asOf) =>
  apiFetch(`/api/v1/trust-graph/subjects/${encodeURIComponent(agentId)}${asOf ? `?asOf=${encodeURIComponent(asOf)}` : ''}`);

// Human accountability cast for an agent (accountable owner + creator + RBAC role-holders) — the
// second axis of the Trust Ontology.
export const getTrustGraphStewardship = (agentId) =>
  apiFetch(`/api/v1/trust-graph/subjects/${encodeURIComponent(agentId)}/stewardship`);

export const getPlatformRuntimeFlags = () =>
  apiFetch('/api/v1/platform/runtime-flags');
export const setPlatformRuntimeFlag = (key, value) =>
  apiFetch(`/api/v1/platform/runtime-flags/${encodeURIComponent(key)}`, 'PUT', { value });
export const clearPlatformRuntimeFlag = (key) =>
  apiFetch(`/api/v1/platform/runtime-flags/${encodeURIComponent(key)}`, 'DELETE');

// ID-4 — revocation blast radius (read-only preview of what a revoke halts).
export const getAgentBlastRadius = (agentId) =>
  apiFetch(`/api/v1/identity/agents/${encodeURIComponent(agentId)}/blast-radius`);

// ─────────────────────────────────────────────────────────────────────
// P0-IDENT Stage 2b — write paths
// ─────────────────────────────────────────────────────────────────────
// Eight functions, four PUT + four DELETE pairs. All require the
// identity.write permission server-side (Org Admin / Tenant Admin /
// Super Admin). The PUT endpoints are partial-update — only non-null
// fields in the body overwrite existing values. The DELETE endpoints
// wipe the override row entirely, reverting that scope to inherit from
// its parent. See docs/IDENTITY_TENANCY_ALIGNMENT_ANALYSIS.md §7.

export const updateIdentityOrganization = (body) =>
  apiFetch('/api/v1/identity/organization', 'PUT', body);

export const resetIdentityOrganization = () =>
  apiFetch('/api/v1/identity/organization', 'DELETE');

export const updateIdentityTenantOverride = (tenantId, body) =>
  apiFetch(`/api/v1/identity/tenants/${encodeURIComponent(tenantId)}/override`, 'PUT', body);

export const resetIdentityTenantOverride = (tenantId) =>
  apiFetch(`/api/v1/identity/tenants/${encodeURIComponent(tenantId)}/override`, 'DELETE');

export const updateIdentityInstanceOverride = (instanceId, body) =>
  apiFetch(`/api/v1/identity/instances/${encodeURIComponent(instanceId)}/override`, 'PUT', body);

export const resetIdentityInstanceOverride = (instanceId) =>
  apiFetch(`/api/v1/identity/instances/${encodeURIComponent(instanceId)}/override`, 'DELETE');

export const updateIdentityAgentOverride = (agentId, body) =>
  apiFetch(`/api/v1/identity/agents/${encodeURIComponent(agentId)}/override`, 'PUT', body);

export const resetIdentityAgentOverride = (agentId) =>
  apiFetch(`/api/v1/identity/agents/${encodeURIComponent(agentId)}/override`, 'DELETE');

// ─────────────────────────────────────────────────────────────────────
// P0-IDENT Stage 3d — Federation peer CRUD
// ─────────────────────────────────────────────────────────────────────
// Per-org peer inventory. Read = identity.read; mutate = identity.write.
// Soft delete (default) sets is_active=false; hardDelete=true removes
// the row but only safe when no override row references the peer's UUID.

export const listIdentityFederationPeers = ({ includeRetired = false } = {}) => {
  const qs = includeRetired ? '?includeRetired=true' : '';
  return apiFetch(`/api/v1/identity/federation-peers${qs}`);
};

export const createIdentityFederationPeer = (body) =>
  apiFetch('/api/v1/identity/federation-peers', 'POST', body);

export const updateIdentityFederationPeer = (id, body) =>
  apiFetch(`/api/v1/identity/federation-peers/${encodeURIComponent(id)}`, 'PUT', body);

export const retireIdentityFederationPeer = (id, { hardDelete = false } = {}) => {
  const qs = hardDelete ? '?hardDelete=true' : '';
  return apiFetch(`/api/v1/identity/federation-peers/${encodeURIComponent(id)}${qs}`, 'DELETE');
};

export const refreshIdentityFederationPeerBundle = (id) =>
  apiFetch(`/api/v1/identity/federation-peers/${encodeURIComponent(id)}/refresh-bundle`, 'POST', {});

// Stage 4-2 — per-instance identity detail.
export const getIdentityInstance = (instanceId) =>
  apiFetch(`/api/v1/identity/instances/${encodeURIComponent(instanceId)}`);

// Stage 4-5 — compliance evidence per-org view.
export const getIdentityComplianceEvidence = () =>
  apiFetch('/api/v1/identity/compliance-evidence');


// ─────────────────────────────────────────────────────────────────────
// V191 — Multi-Tenancy (Phase 3/4/5) — additional Instance helpers
// ─────────────────────────────────────────────────────────────────────
// The base Instance CRUD client lives further up (getInstances / getInstance /
// createInstance / updateInstance / deleteInstance). Phase 5 adds two
// endpoints the legacy stub didn't cover:
//   * a tenant-scoped list (uses the new /api/v1/tenants/{tid}/instances route)
//   * the Phase-4 atomic switch-instance flow
// Both backed by MultiTenancyController + AuthService respectively. All
// endpoints honour the most-specific-wins effective-role resolution from
// RbacService.requirePermissionForScope.

/** List the Instances under a specific Tenant. 404 on cross-org probe. */
export const listInstancesForTenant = (tenantId) =>
  apiFetch(`/api/v1/tenants/${encodeURIComponent(tenantId)}/instances`);

/**
 * V208 / RBAC v2 — list customers visible to the caller. Platform Admin
 * gets the full ACTIVE customer list (used by the user-menu Customer
 * picker). Other roles get [] from the backend — they already operate
 * inside a specific customer scope.
 */
export const listCustomers = () => apiFetch('/api/v1/customers');

/**
 * G586 — Admin-initiated password reset.
 *
 * Two delivery modes:
 *   - 'LINK'          → backend issues a one-time reset token (24h TTL),
 *                       returns {mode:'LINK', resetUrl, expiresAt, ...}
 *   - 'TEMP_PASSWORD' → backend generates a strong temp password, hashes
 *                       + persists, returns {mode:'TEMP_PASSWORD',
 *                       tempPassword, ...}. The cleartext lives in this
 *                       response exactly once — surface it to the admin
 *                       immediately, do NOT log/store it client-side.
 *
 * RBAC enforced server-side. Use canResetUserPassword(userId) for the
 * cheap client-side gating probe before showing the action button.
 */
export const adminResetUserPassword = (userId, mode) =>
  apiFetch(
    `/api/v1/users/${encodeURIComponent(userId)}/password-reset`,
    'POST',
    { mode },
  );

/** G586 — Cheap probe for whether the current caller has scope to reset
 *  the target's password. Use this to gate button visibility — the actual
 *  POST will re-enforce server-side so a stale probe can't be exploited. */
export const canResetUserPassword = (userId) =>
  apiFetch(`/api/v1/users/${encodeURIComponent(userId)}/can-reset-password`);

/** V208 / Phase D-1 — fetch a single customer's full record. PA-only. */
export const getCustomer = (customerId) =>
  apiFetch(`/api/v1/customers/${encodeURIComponent(customerId)}`);

/**
 * G587 — Rich Customer onboarding with starter topology + first admin.
 *
 * Body shape (full validation server-side):
 *   {
 *     customerName, customerDisplayName?, billingEmail?,
 *     orgName, tenantName?,
 *     instances: [{ name, tier?, isProduction?, description? }, ...],
 *     adminEmail, adminName?, adminRole?,
 *     deliveryMode: 'LINK' | 'TEMP_PASSWORD'
 *   }
 *
 * Returns:
 *   { customerId, customerName, orgId, tenantId, instanceIds[],
 *     adminUserId, adminEmail,
 *     deliveryMode, resetUrl?, resetExpiresAt?, adminTempPassword?,
 *     completedAt }
 *
 * Server-side this is one @Transactional call — any validation failure
 * or DB conflict rolls back the whole topology, so the UI never has to
 * worry about half-created customers.
 */
export const onboardCustomerWithTopology = (body) =>
  apiFetch('/api/v1/customers/onboard-with-topology', 'POST', body || {});

/* Inline first-admin lookup for the onboarding wizard's smart email field.
 * Returns { exists, userId, name, email, role }. Platform-Admin-gated. */
export const lookupOnboardingAdmin = (email) =>
  apiFetch(`/api/v1/customers/onboard-with-topology/admin-lookup?email=${encodeURIComponent(email || '')}`);

/**
 * G588 — Bulk-onboard customers from an XLSX upload.
 *
 * Multipart POST; the file is the only form field. Backend parses the
 * 5-sheet workbook, then onboards each customer row in its own
 * @Transactional scope. The response is {totalRows, succeeded, failed,
 * durationMs, outcomes[]} where outcomes is a per-row {status,
 * customerName, customerId?, adminEmail?, deliveryMode?, resetUrl?,
 * adminTempPassword?, errorMessage?}.
 *
 * Because each row commits independently, a partial run that has 3
 * successful customers and 2 failures still returns 200 — the UI
 * surfaces the failures inline so the operator can retry just those
 * rows after fixing the data.
 */
export const bulkOnboardCustomers = async (file) => {
  if (!file) return { ok: false, error: 'No file provided' };
  const token = _authToken || getAccessToken();
  const fd = new FormData();
  fd.append('file', file);
  const headers = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;
  /* SEC/CSRF (RC-91 family) — raw multipart fetch bypasses apiFetch's CSRF
   * wiring. Cookie-authenticated callers (no in-memory Bearer token, e.g. after
   * a page reload under SEC-25 cookie auth) are CSRF-enforced, so echo the
   * csrf_token cookie or this 403s intermittently with "CSRF token missing or
   * invalid". (credentials:'include' is already set below.) */
  const csrf = readCsrfCookie();
  if (csrf) headers['X-CSRF-Token'] = csrf;
  try {
    const res = await fetch(`${API_URL}/api/v1/customers/onboard-with-topology/bulk`, {
      method: 'POST', headers, body: fd, credentials: 'include',
    });
    if (!res.ok) {
      let msg = `HTTP ${res.status}`;
      try {
        const cloned = res.clone();
        const j = await cloned.json();
        msg = j?.error || j?.message || msg;
      } catch (_) { /* not JSON */ }
      return { ok: false, error: msg, status: res.status };
    }
    const data = await res.json();
    return { ok: true, data };
  } catch (e) {
    return { ok: false, error: e?.message || String(e) };
  }
};

/**
 * G588 — URL for the empty bulk-onboarding XLSX template. Backend stamps
 * the workbook on the fly so the schema can evolve without a frontend
 * deploy. Use {@link downloadBulkOnboardingTemplate} to trigger a
 * download with the correct auth header attached.
 */
export const BULK_ONBOARDING_TEMPLATE_URL =
  `${API_URL}/api/v1/customers/onboard-with-topology/template`;

/**
 * G588 — Trigger a browser download of the empty bulk-onboarding XLSX.
 * Uses fetch+blob (not a plain <a href>) so the bearer token can be
 * attached. Returns the filename on success.
 */
export const downloadBulkOnboardingTemplate = async () => {
  const token = _authToken || getAccessToken();
  const headers = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const res = await fetch(BULK_ONBOARDING_TEMPLATE_URL, {
    method: 'GET', headers, credentials: 'include',
  });
  if (!res.ok) {
    let msg = `HTTP ${res.status}`;
    try { const j = await res.json(); msg = j?.error || msg; } catch (_) { /* ignore */ }
    throw new Error(msg);
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'customer-onboarding-template.xlsx';
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Revoke after a short tick so the browser has time to start the download
  setTimeout(() => URL.revokeObjectURL(url), 1000);
  return 'customer-onboarding-template.xlsx';
};

/**
 * V208 / Phase D-1 — onboard a new customer. PA-only.
 *
 * G581 — apiFetch is positional: (path, method='GET', body=null, ...).
 * Passing {method,body} as the 2nd arg meant the HTTP method became an
 * object (stringified to "[object Object]") and body was never sent,
 * so the Onboard button silently failed without creating a row.
 *
 * @param {object} body { name (required), displayName, status }
 */
export const onboardCustomer = (body) =>
  apiFetch('/api/v1/customers', 'POST', body || {});

/**
 * V208 / Phase D-1 — update a customer's displayName / status. PA-only.
 * The `name` field is immutable post-onboard.
 */
export const updateCustomer = (customerId, body) =>
  apiFetch(`/api/v1/customers/${encodeURIComponent(customerId)}`, 'PUT', body || {});

/**
 * V208 / Phase D-1 — soft-delete (offboard) a customer. Sets
 * status=OFFBOARDED + offboardedAt. PA-only. Idempotent: already-OFFBOARDED
 * customers return 204 without re-stamping.
 */
export const offboardCustomer = (customerId) =>
  apiFetch(`/api/v1/customers/${encodeURIComponent(customerId)}`, 'DELETE');

// ── Phase-1 packaging: entitlements / plan / service plan ───────────
const entBase = (customerId) => `/api/v1/customers/${encodeURIComponent(customerId)}/entitlements`;
export const getEntitlements = (customerId) => apiFetch(entBase(customerId));
export const validatePlan = (customerId, modules) =>
  apiFetch(`${entBase(customerId)}/validate`, 'POST', { modules });
export const replaceEntitlements = (customerId, body) =>
  apiFetch(entBase(customerId), 'PUT', body || {});
export const expandEntitlements = (customerId, modules, protectionMode) =>
  apiFetch(`${entBase(customerId)}/expand`, 'POST', { modules, protectionMode });
export const setModuleState = (customerId, moduleKey, state) =>
  apiFetch(`${entBase(customerId)}/module/${encodeURIComponent(moduleKey)}`, 'PUT', { state });
export const setServicePlan = (customerId, body) =>
  apiFetch(`${entBase(customerId)}/service-plan`, 'PUT', body || {});
export const setQuota = (customerId, quotaKey, soft, hard) =>
  apiFetch(`${entBase(customerId)}/quota/${encodeURIComponent(quotaKey)}`, 'PUT', { soft, hard });
export const setEnforcement = (customerId, mode) =>
  apiFetch(`${entBase(customerId)}/enforcement`, 'PUT', { mode });
// Current-session entitlement summary (FE nav/route guards read this).
export const getMyEntitlements = () => apiFetch('/api/v1/me/entitlements');

// ── Billing & Invoicing (Platform Admin; under the Customers section) ──
const billingBase = (customerId) => `/api/v1/customers/${encodeURIComponent(customerId)}/billing-config`;
export const getBillingConfig = (customerId) => apiFetch(billingBase(customerId));
export const updateBillingConfig = (customerId, body) => apiFetch(billingBase(customerId), 'PUT', body || {});
export const sendInvoiceNow = (customerId) => apiFetch(`${billingBase(customerId)}/invoice/send`, 'POST', {});
// Same-origin URL for the rendered invoice (cookie-authenticated; opened in a new tab).
// Extensionless on purpose — an ".html" path is caught by the frontend nginx static handler.
export const invoicePreviewHtmlUrl = (customerId) => `${billingBase(customerId)}/invoice-preview/html`;

// ── Rate card (Platform Admin) — the price book that turns invoice "$—" into real numbers ──
// customerId omitted → platform defaults; customerId present → that customer's overrides.
const rateCardBase = '/api/v1/admin/rate-card';
export const getRateCard = (customerId) =>
  apiFetch(customerId ? `${rateCardBase}?customerId=${encodeURIComponent(customerId)}` : rateCardBase);
export const upsertRateCard = (body) => apiFetch(rateCardBase, 'PUT', body || {});

// ── Instance metering / usage (Platform Admin) ──
// Current-month per-instance usage for a customer; rollup aggregates raw events into the store.
export const getCustomerUsage = (customerId) =>
  apiFetch(`/api/v1/admin/metering/usage/${encodeURIComponent(customerId)}`);
export const runUsageRollup = () => apiFetch('/api/v1/admin/metering/rollup', 'POST', {});
// Self-serve: the current session's own current-month usage (customer resolved from the session).
export const getMyUsage = () => apiFetch('/api/v1/me/usage');

// LLM billing mode (contract, per customer × provider). PA read/write; self-serve read.
export const getLlmBillingModes = (customerId) =>
  apiFetch(`/api/v1/customers/${encodeURIComponent(customerId)}/llm-billing-mode`);
export const setLlmBillingMode = (customerId, provider, mode) =>
  apiFetch(`/api/v1/customers/${encodeURIComponent(customerId)}/llm-billing-mode`, 'PUT', { provider, mode });
export const getMyLlmBillingMode = () => apiFetch('/api/v1/me/llm-billing-mode');

// Product catalog (PA): derived edition plans + module SKUs + cloud marketplace listings.
export const getProductCatalog = (cloud) =>
  apiFetch(cloud ? `/api/v1/admin/product-catalog?cloud=${encodeURIComponent(cloud)}` : '/api/v1/admin/product-catalog');
export const setMarketplaceMapping = (body) =>
  apiFetch('/api/v1/admin/product-catalog/marketplace', 'PUT', body || {});

// ── Commercial Orders (Slice O5) — PA-only /api/v1/admin/orders ──
export const listOrders = (customerId, status) =>
  apiFetch(`/api/v1/admin/orders?customerId=${encodeURIComponent(customerId)}${status ? `&status=${encodeURIComponent(status)}` : ''}`);
export const getOrder = (orderId) => apiFetch(`/api/v1/admin/orders/${orderId}`);
export const createOrder = (body) => apiFetch('/api/v1/admin/orders', 'POST', body || {});
export const updateOrder = (orderId, body) => apiFetch(`/api/v1/admin/orders/${orderId}`, 'PUT', body || {});
export const deleteOrder = (orderId) => apiFetch(`/api/v1/admin/orders/${orderId}`, 'DELETE');
export const listOrderLines = (orderId) => apiFetch(`/api/v1/admin/orders/${orderId}/lines`);
export const addOrderLine = (orderId, body) => apiFetch(`/api/v1/admin/orders/${orderId}/lines`, 'POST', body || {});
export const updateOrderLine = (lineId, body) => apiFetch(`/api/v1/admin/orders/lines/${lineId}`, 'PUT', body || {});
export const deleteOrderLine = (lineId) => apiFetch(`/api/v1/admin/orders/lines/${lineId}`, 'DELETE');
export const applyOrderLine = (orderId, lineId, body) =>
  apiFetch(`/api/v1/admin/orders/${orderId}/lines/${lineId}/apply`, 'POST', body || {});
export const applyOrder = (orderId) => apiFetch(`/api/v1/admin/orders/${orderId}/apply`, 'POST', {});

/**
 * V208 / Phase D-2 — switch the Platform Admin's session into a specific
 * customer's scope. Returns the new JWT + session + memberships, same
 * shape as login. Frontend should reload after a successful switch so
 * every page re-resolves against the new cid.
 */
export const switchCustomer = (customerId) =>
  apiFetch('/api/v1/auth/switch-customer', 'POST', { customerId });

/**
 * RC-137a — Pin the Platform Admin into a specific tenant's scope.
 * BE re-issues JWT with new tid + auto-derived oid + auto-derived cid
 * (the tenant→org→customer chain is server-derived; FE only passes
 * tenantId). Returns the same shape as switchCustomer; the FE should
 * reload after success so every page re-resolves against the new tid.
 */
export const switchTenant = (tenantId) =>
  apiFetch('/api/v1/auth/switch-tenant', 'POST', { tenantId });

/**
 * RC-137a — Fetch {orgs, tenants} tree for a customer (PA-only).
 * Powers the user-dropdown ORGS section that appears under a selected
 * customer. Shape: { customerId, customerName, orgs: [{ id, name,
 * tenants: [{ id, name }] }] }.
 */
export const getScopePicker = (customerId) =>
  apiFetch(`/api/v1/auth/scope-picker?customerId=${encodeURIComponent(customerId)}`);

/**
 * V191 / Phase-7 — Instance lineage payload.
 * Returns `{instance, forkChain, forks, promotionEvents}`. Gated by
 * instance.read at scope. 404 if instance doesn't exist; 403 if no perm.
 */
export const getInstanceLineage = (instanceId) =>
  apiFetch(`/api/v1/instances/${encodeURIComponent(instanceId)}/lineage`);

// ─────────────────────────────────────────────────────────────────────
// G532 — Members listings for the Multi-Tenancy detail panels.
// Each helper returns an array of ScopeMemberDTOs: {membershipId, userId,
// name, email, status, role, isDefault, orgId, tenantId, instanceId}.
// Cross-scope grants aren't unioned — each call is scoped to that level.
// ─────────────────────────────────────────────────────────────────────
export const getOrgMembers = (orgId) =>
  apiFetch(`/api/v1/organizations/${encodeURIComponent(orgId)}/members`);

export const getTenantMembers = (tenantId) =>
  apiFetch(`/api/v1/tenants/${encodeURIComponent(tenantId)}/members`);

export const getInstanceMembers = (instanceId) =>
  apiFetch(`/api/v1/instances/${encodeURIComponent(instanceId)}/members`);

// G540 — Resources tab on InstanceDetailPanel: counts + 5-row samples of
// agents/workflows/connectors/connectorGroups pinned to the instance.
export const getInstanceResources = (instanceId) =>
  apiFetch(`/api/v1/instances/${encodeURIComponent(instanceId)}/resources`);

// G541 — Audit tab on InstanceDetailPanel: unified timeline (lifecycle +
// promotion events + member grants). Newest-first, capped at 200.
export const getInstanceAudit = (instanceId) =>
  apiFetch(`/api/v1/instances/${encodeURIComponent(instanceId)}/audit`);

// ─────────────────────────────────────────────────────────────────────
// G552 — Promotion Graph (Tenant detail panel)
// Reads/writes the directed-acyclic graph of declared promotion edges
// between Instances under a Tenant. Phase-9 promote APIs validate against
// this graph: no enabled edge = 400 "no path declared".
// ─────────────────────────────────────────────────────────────────────

/* apiFetch is positional: (path, method='GET', body=null). The helpers
 * below MUST use that shape — passing {method, body} as the 2nd arg makes
 * `method` an object and getHeaders(method).toUpperCase() throws
 * "e.toUpperCase is not a function" (see G556). The wrapper also
 * stringifies the body itself, so we hand it the raw object. */

/** GET — every declared edge for a tenant, with endpoint Instance metadata
 *  (name, tier, status) already joined so the UI can render in one trip. */
export const getPromotionPaths = (tenantId) =>
  apiFetch(`/api/v1/tenants/${encodeURIComponent(tenantId)}/promotion-paths`);

/** POST — declare a new edge. Body: { fromInstanceId, toInstanceId, enabled?, notes? }.
 *  Server validates: self-loop (400), cross-tenant endpoint (400), duplicate (409),
 *  cycle close (409). */
export const createPromotionPath = (tenantId, body) =>
  apiFetch(
    `/api/v1/tenants/${encodeURIComponent(tenantId)}/promotion-paths`,
    'POST',
    body
  );

/** PUT — patch the enabled flag and/or notes on an existing edge. Endpoints
 *  are immutable; to change them, delete + recreate. */
export const updatePromotionPath = (tenantId, pathId, body) =>
  apiFetch(
    `/api/v1/tenants/${encodeURIComponent(tenantId)}/promotion-paths/${encodeURIComponent(pathId)}`,
    'PUT',
    body
  );

/** DELETE — hard-remove an edge. The corresponding promotion path is gone;
 *  the underlying instances are untouched. */
export const deletePromotionPath = (tenantId, pathId) =>
  apiFetch(
    `/api/v1/tenants/${encodeURIComponent(tenantId)}/promotion-paths/${encodeURIComponent(pathId)}`,
    'DELETE'
  );

// ─────────────────────────────────────────────────────────────────────
// Phase 9 — Promotion execution (Promote / Approve / Reject / Cancel / Rollback)
// Wraps /api/v1/promotions. Backed by PromotionService + per-artifact
// PromotionCloner strategies. Validation chain (path exists+enabled,
// target ACTIVE, no dup pending, RBAC) runs server-side; helpers below
// are positional-form to match apiFetch (path, method, body).
// ─────────────────────────────────────────────────────────────────────

/** POST — initiate a promotion. Body:
 *  { resourceType, sourceResourceId, targetInstanceId, conflictResolution, notes }.
 *  Returns the created PromotionEventDTO. If target.requires_promotion_approval=true
 *  the status is PENDING_APPROVAL; otherwise PROMOTED with target_resource_id set. */
export const initiatePromotion = (body) =>
  apiFetch('/api/v1/promotions', 'POST', body);

/** POST — approver decision. Body: { notes }. Caller must hold
 *  promotion.approve at the target instance. Triggers the clone in the
 *  same transaction; row flips to PROMOTED on success or FAILED on
 *  cloner exception. */
export const approvePromotion = (id, body) =>
  apiFetch(`/api/v1/promotions/${encodeURIComponent(id)}/approve`, 'POST', body || {});

/** POST — approver rejection. Body: { notes }. Caller must hold
 *  promotion.reject at the target instance. */
export const rejectPromotion = (id, body) =>
  apiFetch(`/api/v1/promotions/${encodeURIComponent(id)}/reject`, 'POST', body || {});

/** POST — initiator-only cancellation while PENDING_APPROVAL. Body: { notes }. */
export const cancelPromotion = (id, body) =>
  apiFetch(`/api/v1/promotions/${encodeURIComponent(id)}/cancel`, 'POST', body || {});

/** POST — undo a PROMOTED move. Body: { reason } (required). Caller must
 *  hold promotion.rollback at the target instance. Target artifact is
 *  removed (hard delete or soft delete depending on artifact type) and
 *  the promotion_events row flips to ROLLED_BACK. */
export const rollbackPromotion = (id, body) =>
  apiFetch(`/api/v1/promotions/${encodeURIComponent(id)}/rollback`, 'POST', body);

/** GET — single promotion event with source/target instance + resource names joined. */
export const getPromotion = (id) =>
  apiFetch(`/api/v1/promotions/${encodeURIComponent(id)}`);

/** GET — tenant-scoped listing, newest-first, capped at 200. Optional
 *  status filter (PENDING_APPROVAL | APPROVED | PROMOTED | REJECTED |
 *  FAILED | ROLLED_BACK). */
export const listPromotions = (tenantId, status) => {
  const qs = status
    ? `?tenantId=${encodeURIComponent(tenantId)}&status=${encodeURIComponent(status)}`
    : `?tenantId=${encodeURIComponent(tenantId)}`;
  return apiFetch(`/api/v1/promotions${qs}`);
};

// ─────────────────────────────────────────────────────────────────────
// V191 / Phase-5b — Instance writers (create / update / archive)
// ─────────────────────────────────────────────────────────────────────
//
// NOTE: A read-only Instance CRUD stub at line 846 (`createInstance`,
// `updateInstance`, `deleteInstance`) used to call the bare /api/v1/instances
// endpoints. Phase-5b implements them server-side via MultiTenancyController.
// The wire shape is the same — the only behavioural change is that the
// backend now actually persists + validates + RBAC-gates.

// (No new helpers needed: legacy createInstance / updateInstance /
// deleteInstance already point at the right endpoints. Phase-5b is
// strictly an additive server-side implementation.)

/**
 * Phase-4 — atomic switch-instance flow. Re-issues the JWT with a new
 * `iid` claim and blacklists the previous token.
 * @param {{instanceId: string}} body
 */
export const switchInstance = (body) =>
  apiFetch('/api/v1/auth/switch-instance', 'POST', body);


// ── Agent Ownership · Accountability Registry (Phase 5) ──────────────────────
// Read-only projections over the ownership substrate. Owner/creator/agent names
// and human labels are resolved server-side (OwnershipController /registry + /rollup).
// Scope: these four aggregate over everything the CALLER is accountable for — every tenant in a
// Super Admin's customer, in an Org Admin's org, or the one tenant of a Tenant Admin. customerId is
// required ONLY for a Platform Admin, whose token carries no customer or tenant claim (§8.3); the
// server answers 400 customer_required if it is missing, which the page turns into a picker.
// Passing another role's customerId is refused server-side rather than ignored.
const scoped = (path, customerId) =>
  apiFetch(customerId ? `${path}${path.includes('?') ? '&' : '?'}customerId=${encodeURIComponent(customerId)}` : path);

export const getOwnershipRollup = (customerId) => scoped('/api/v1/ownership/rollup', customerId);

export const listAccountabilityRegistry = (params = {}) => {
  const qs = new URLSearchParams();
  const add = (k, v) => { if (v !== undefined && v !== null && v !== '') qs.append(k, v); };
  add('state', params.state);
  add('tier', params.tier);
  add('attestationStatus', params.attestationStatus);
  add('q', params.q);
  // Scope travels with the request. A Platform Admin who has chosen a customer must get the SAME
  // population in this table as in the KPI strip above it; without this the two disagreed silently.
  add('customerId', params.customerId);
  add('page', params.page);
  add('size', params.size);
  add('sort', params.sort);
  add('dir', params.dir);
  const str = qs.toString();
  return apiFetch(`/api/v1/ownership/registry${str ? `?${str}` : ''}`);
};

// Platform-Admin backfill of the registry projection for a tenant (idempotent).
export const backfillAccountabilityRegistry = (tenantId) =>
  apiFetch(`/api/v1/ownership/registry/backfill?tenantId=${encodeURIComponent(tenantId)}`, 'POST');

// ── Accountability Registry · Outcomes & Actions (Phase 5) ───────────────────
// Open ownership findings for the caller's tenant + the one-click "Represent estate"
// remediation (reconcile un-represented agents + backfill the registry).
export const getOwnershipFindings = (customerId) => scoped('/api/v1/ownership/findings', customerId);

export const representOwnershipEstate = () => apiFetch('/api/v1/ownership/represent', 'POST');

// ── Accountability governance config (A-5, V343) ───────────────────────────
// Per-customer leaver-reconciliation mode + cadence. Backend:
// AccountabilityConfigController @ /api/v1/accountability/config.
//
// Authorization is enforced server-side via SecurityContext.canBypassScopeForCustomer:
// Platform Admin for any customer, Super Admin for their own customer only. The UI hides
// the surface for other roles, but the hide is cosmetic — the server is the boundary.
//
// Omitting customerId resolves the caller's own customer from the JWT cid claim, which is
// what a Super Admin wants; a Platform Admin token has no cid and must pass one explicitly.
export const getAccountabilityConfig = (customerId) =>
  apiFetch(`/api/v1/accountability/config${customerId ? `/${encodeURIComponent(customerId)}` : ''}`);

// Partial update — only the keys present are changed, so callers can set the mode without
// restating the cadence. Returns the saved config INCLUDING effectiveLeaverMode, which may be
// stronger than what was sent when a platform floor is in force.
export const setAccountabilityConfig = (customerId, patch) =>
  apiFetch(`/api/v1/accountability/config/${encodeURIComponent(customerId)}`, 'PUT', patch);

// Escalation policies this customer may nominate for ownership reassignment (V345).
//
// Kept out of the config payload deliberately: the config is read on every visit to the
// Accountability surface, while this list is only needed when the picker is opened, and it
// grows with the customer's estate whereas the config is a single row.
//
// Each row carries builtForReassignment — whether the policy was explicitly marked for this
// purpose — so the picker can group those first. It is a hint about intent, not a restriction:
// any active policy in the customer may be nominated.
export const listAccountabilityEscalationPolicies = (customerId) =>
  apiFetch(`/api/v1/accountability/config/${encodeURIComponent(customerId)}/escalation-policies`);

// Owner accepts accountability for an agent (consent → ownership record ACTIVE).
// Only the nominated owner may attest (backend-enforced). NOMINATED → ACTIVE.
export const attestOwnership = (agentId) =>
  apiFetch(`/api/v1/ownership/agents/${encodeURIComponent(agentId)}/attest`, 'POST');

// CLAIM ceremony — name the accountable owner for a DISCOVERED_UNOWNED agent (-> NOMINATED).
export const claimOwnership = (agentId, ownerPrincipalId) =>
  apiFetch(`/api/v1/ownership/agents/${encodeURIComponent(agentId)}/claim`, 'POST', { ownerPrincipalId });

// Materiality preview — pure rubric tier from charter facts, no agent/persistence.
export const previewMateriality = (inputs) =>
  apiFetch('/api/v1/ownership/materiality/preview', 'POST', inputs);

// Materiality assess — persist the rubric tier for an agent from supplied charter facts.
// Write-scoped to the agent's tenant (risk/compliance). Tier is mapped by the rubric, never hand-set.
export const getMaterialityInputs = (agentId) => apiFetch(`/api/v1/ownership/agents/${encodeURIComponent(agentId)}/materiality`);
export const assessMateriality = (agentId, inputs) =>
  apiFetch(`/api/v1/ownership/agents/${encodeURIComponent(agentId)}/materiality`, 'POST', inputs);

// Materiality rubric metadata — current version (for the badge) + purpose classes (for the form).
export const getMaterialityRubric = () =>
  apiFetch('/api/v1/ownership/materiality/rubric');

// Invite a NEW person as an agent's accountable owner (reuses the platform invite pipeline +
// the Ownership Assignment Invitation email). With agentId → invites + claims (NOMINATED);
// without agentId (creation) → invites and returns { ownerPrincipalId } for the create payload.
export const inviteOwnershipOwner = (payload) =>
  apiFetch('/api/v1/ownership/invite-owner', 'POST', payload);

// ── Accountability Certificates (Phase 5 · A6) ───────────────────────────────
// Exportable per-agent proof of continuous ownership + a re-verified evidence chain.
export const getOwnershipCertificate = (agentId) =>
  apiFetch(`/api/v1/ownership/agents/${encodeURIComponent(agentId)}/certificate`);

// ── Examiner-ready ownership report (Phase 5 · A7) ───────────────────────────
// Live inventory, materiality method, segregation-of-duties, attestation posture,
// and open exceptions with remediation — tenant-scoped, generated from live state.
export const getExaminerOwnershipReport = (customerId) => scoped('/api/v1/ownership/examiner-report', customerId);
export const getOwnershipPosture = (customerId) => scoped('/api/v1/ownership/posture', customerId);
// Outcomes & Actions dashboard — KPI strip (posture aggregates) + gap MTTR. Feed lands in a later slice.
export const getOutcomesActions = (windowDays = 90, customerId) => scoped(`/api/v1/ownership/outcomes?windowDays=${windowDays}`, customerId);

// ── Ask the Registry (Phase 5) — deterministic NL answer over the registry ───
export const askOwnershipRegistry = (q) =>
  apiFetch(`/api/v1/ownership/ask?q=${encodeURIComponent(q || '')}`);
